// src/components/ClaimsTable.tsx
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  fetchClaims,
  sendLOC,
  requestLOA,
  sendLetter,
  generateLetter,
  generateRiskAssessment,
  generateDsarDocument,
  getRiskAssessmentVersions,
  RiskAssessmentVersion,
  cancelClaim,
  reinstateClaim,
  fetchReinstatementPaymentImpact,
  toggleReinstatement,
  updateClientInfo,
  updateAgreementFields,
  updateCreditAgreement,
  updateAgreementNumber,
  getCreditCheckReportUrl,
  getCreditCheckSummary,
  getClaimAmlSummary,
  testCreditCheckUrl,
  ClaimPage,
  Claim,
  Audit,
  ClaimAmlSummary,
  getClaimAudits,
  checkAssociatedClaims,
  AssociatedClaimsResponse,
  getClaim,
  addCorrespondence,
  Correspondence,
  lookupGeoIp,
  GeoIpResult,
  CancelClaimResponse,
  CancelClaimRequest,
  heldCancellationAlert,
  heldReviewTaskId,
  PHONE_REVIEW_QUESTION,
  getClaimKeyDates,
  updateClaimKeyDates,
  ClaimKeyDatesView,
  KeyDatesUpdatePayload,
  LenderBulkSubmissionDetails,
  LenderBulkSubmissionUpdatePayload,
  ReinstatementPaymentImpact,
  ReinstatementPaymentResult,
  RemainingPaymentAction,
  getLenderBulkSubmissionDetails,
  updateLenderBulkSubmissionDetails,
  listLocDocuments,
  listFoaDocuments,
  LocBlobDocument,
  LocResendResult,
  getDuplicationAudit,
  saveDuplicationAudit,
  DuplicationAudit,
  setEnforcementFlag,
  generateDocumentSasUrl,
  sendLenderWithdrawalEmail,
  sendMultipleRepEmail,
  client,
} from '../api/claims';
import { assignEmailToClaim, EmailMessage } from '../api/emails';
import { photoIdApi } from '../api/photoId';
import { UnifiedTask, unifiedTasksApi } from '../api/unifiedTasks';
import ClaimFiles from './ClaimFiles';
import { AuditForm } from './AuditForm';
import { HierarchicalClaimsTable } from './HierarchicalClaimsTable';
import EmailComposer from './EmailComposer';
import SmsComposer from './SmsComposer';
import EmailDrawer from './EmailDrawer';
import Modal from './ModalTrue';
import { HoldReviewModal } from './HoldReviewModal';
import SendLetterModal, { LetterData } from './SendLetterModal';
import ClaimKeyDatesTimeline from './ClaimKeyDatesTimeline';
import LenderBulkSubmissionModal from './LenderBulkSubmissionModal';
import LitigationFriendModal from './LitigationFriendModal';
import AuditTrailModal from './AuditTrailModal';
import SplitClaimsModal from './SplitClaimsModal';
import ReinstatementScopeModal from './ReinstatementScopeModal';
import ReinstatementPaymentModal from './ReinstatementPaymentModal';
import { reinstatementPaymentMessage } from './reinstatementPaymentMessage';
import DuplicationAuditModal from './DuplicationAuditModal';
import PDFViewerDrawer from './PDFViewerDrawer';
import { amlOutcome, hasFetchableAmlReport, AML_TONE_COLOURS } from './amlOutcome';
import DocViewerDrawer from './DocViewerDrawer';
import LocResendDialog from './LocResendDialog';
import { getLocActionAvailability } from './locActionAvailability';
import { dsarErrorMessage, saveBlobAsFile } from './dsarDocument';
import RedressTab from '../pages/redress/RedressTab';
import RedressErrorBoundary from './RedressErrorBoundary';
import { ChargeReviewModal, ClaimBreakdownEntry } from './ChargeReviewModal';
import ResetIdVerificationButton from './ResetIdVerificationButton';
import NonCooperationReasonsFieldset from './NonCooperationReasonsFieldset';
import { useAuth } from '../context/AuthContext';
import { CRA_CLASSIFICATION_OPTIONS } from '../constants/craClassification';
import { NonCooperationReasonCode } from '../constants/nonCooperationClosure';
import LenderResponseCard from './LenderResponseCard';
import FinalResponseModal from './FinalResponseModal';
import LOAIdentityReviewPanel from './LOAIdentityReviewPanel';
import { getProclaimClaimReference } from '../utils/proclaimReference';
import { cancelledByLabel } from '../utils/cancelledBy';
import { amountStillOwed } from '../utils/paymentBalance';
import { claimSaysClientDeceased } from '../utils/idRequestHistory';
import { IdRequestHistoryRow, IdRequestStatus } from './IdRequestHistory';
import './ClaimsTable.css';
import ClaimNotesPanel from './ClaimNotesPanel';
import ClientConfirmationBadge from './ClientConfirmationBadge';
import CreditReportStatus from './CreditReportStatus';
import ClientRequestSubReasonSelect from './ClientRequestSubReasonSelect';
import {
  REASON_CLIENT_REQUEST,
  buildClientRequestReason,
  isClientRequestSubReasonMissing,
  splitClientRequestReason,
} from './clientRequestSubReasons';

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

// Cancellation reasons for dropdown
const CANCELLATION_REASONS = [
  'Client request',
  'Cooling off period',
  'Non-cooperative client',
  'Lease agreement',
  'Duplicate claim',
  'Other Solicitor/CMC instructed',
  'Invalid lender',
  'General Closure (No Charge)',
  'General Closure (Charge)',
  'Other'
] as const;

// Special cancellation reasons that affect the flow
const REASON_LEASE_AGREEMENT = 'Lease agreement';
const REASON_NON_COOPERATIVE = 'Non-cooperative client';
const REASON_OTHER_SOLICITOR = 'Other Solicitor/CMC instructed';
const REASON_INVALID_LENDER = 'Invalid lender';
const REASON_GENERAL_CLOSURE = 'General Closure (No Charge)';
const REASON_GENERAL_CLOSURE_CHARGE = 'General Closure (Charge)';

const FINAL_RESPONSE_STATE_LABELS = {
  pending: 'Pending submission',
  sending: 'Submission in progress',
  accepted: 'Accepted by provider',
  failed: 'Provider rejected submission',
  unknown: 'Submission status uncertain',
} as const;

// Helper function to check if a claim is within the 14-day cooling off period
const isWithinCoolingPeriod = (claim: Claim | null): boolean => {
  if (!claim) return false;
  const agreementDate = claim.agreement_dateAgreed || claim.craCreditStartDate;
  if (!agreementDate) return false;

  let parsed: Date;
  // Handle DD/MM/YYYY or DD/MM/YYYY HH:mm format
  if (agreementDate.includes('/')) {
    const parts = agreementDate.split(/[\s/]/);
    parsed = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
  } else {
    // ISO format
    parsed = new Date(agreementDate);
  }

  if (isNaN(parsed.getTime())) return false;

  const now = new Date();
  const diffDays = (now.getTime() - parsed.getTime()) / (1000 * 60 * 60 * 24);
  return diffDays <= 14;
};

// Helper function to determine display status - update to handle the imported Claim type
const getDisplayStatus = (claim: Claim): string => {
  if (claim.status === 'CANCELLED') {
    return 'Cancelled';
  }
  // Check for bulk submission first (takes precedence over LOC sent)
  if (claim.submissionSent) {
    // Once the lender has replied to the bulk submission (an imported
    // response sets submissionOutcome), say so rather than 'Bulk Submitted'.
    return claim.submissionOutcome || claim.responseDate ? 'Response received' : 'Bulk Submitted';
  }
  if (!claim.locSentDate) {
    return 'LOC needs sending';
  }
  if (claim.locSentDate && !claim.responseDate) {
    return 'LOC Sent';
  }
  if (claim.responseDate) {
    return 'Response received';
  }
  return claim.status || 'Unknown'; // Handle undefined status
};

// InfoRow component for displaying label-value pairs
const InfoRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => {
  const hasValue = value !== undefined && value !== null && value !== '';
  return (
    <div style={{ marginBottom: '8px' }}>
      <strong>{label}:</strong>{' '}
      {hasValue ? value : 'N/A'}
    </div>
  );
};

// Staff manual ID upload form (#377). Replaces the old document-less "Mark ID
// as Verified" flip: a client refused the self-serve ID portal link, so staff
// now attach whatever document they received (postal/email/in person) and
// answer "Is this ID verified?" - Yes marks it verified by the authenticated
// user, No sends it into the normal pending/AI-review flow.
const ManualIdUploadForm: React.FC<{ clientId: string; onUploaded: () => void }> = ({ clientId, onUploaded }) => {
  const [file, setFile] = useState<File | null>(null);
  const [source, setSource] = useState<'postal' | 'email' | 'in_person'>('postal');
  const [verified, setVerified] = useState<'yes' | 'no' | ''>('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The <input type="file"> is uncontrolled -- clearing `file` state alone
  // does not clear the browser's displayed filename, so a re-upload after a
  // successful submit still shows the previous file. Reset via the ref.
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleSubmit = async () => {
    if (!file) {
      setError('Please choose a file to upload');
      return;
    }
    if (!verified) {
      setError('Please answer: Is this ID verified?');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await photoIdApi.uploadManualId(clientId, file, source, verified === 'yes', notes || undefined);
      alert(
        result.verified
          ? `ID marked verified by ${result.uploadedBy}`
          : 'ID document uploaded and sent for review'
      );
      setFile(null);
      setVerified('');
      setNotes('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      await onUploaded();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to upload ID document');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ marginTop: '14px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
      <div style={{ fontWeight: 600, marginBottom: '8px', fontSize: '14px' }}>Upload ID Document</div>
      <div style={{ marginBottom: '8px' }}>
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf,image/jpeg,image/jpg,image/png,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
        />
      </div>
      <div style={{ marginBottom: '8px', fontSize: '13px' }}>
        <label>
          Source:{' '}
          <select value={source} onChange={(e) => setSource(e.target.value as 'postal' | 'email' | 'in_person')}>
            <option value="postal">Postal</option>
            <option value="email">Email</option>
            <option value="in_person">In person</option>
          </select>
        </label>
      </div>
      <div style={{ marginBottom: '8px', fontSize: '13px' }}>
        Is this ID verified?{' '}
        <label style={{ marginLeft: '8px' }}>
          <input
            type="radio"
            name={`id-verified-${clientId}`}
            checked={verified === 'yes'}
            onChange={() => setVerified('yes')}
          />{' '}
          Yes
        </label>
        <label style={{ marginLeft: '10px' }}>
          <input
            type="radio"
            name={`id-verified-${clientId}`}
            checked={verified === 'no'}
            onChange={() => setVerified('no')}
          />{' '}
          No
        </label>
      </div>
      <div style={{ marginBottom: '8px' }}>
        <textarea
          placeholder="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          style={{ width: '100%', fontSize: '13px' }}
        />
      </div>
      {error && <div style={{ color: '#d32f2f', fontSize: '13px', marginBottom: '8px' }}>{error}</div>}
      <button
        onClick={handleSubmit}
        disabled={submitting}
        style={{
          padding: '8px 16px',
          backgroundColor: '#4CAF50',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: submitting ? 'not-allowed' : 'pointer',
          fontSize: '14px'
        }}
      >
        {submitting ? 'Uploading...' : 'Upload ID Document'}
      </button>
    </div>
  );
};

const formatAmlIndividualName = (individual: any) => {
  if (!individual) return 'N/A';
  const parts = [individual.Title, individual.Firstname, individual.Surname]
    .map((part?: string) => (part || '').trim())
    .filter(Boolean);
  return parts.length ? parts.join(' ') : 'N/A';
};

/** #591: "Payment Plan Agreed – by j.lowry@… on 07/10/2026, 14:02" from whatever the claim holds. */
export const describeFeeVoid = (claim: {
  cancellationPaymentVoidReason?: string;
  cancellationPaymentVoidedBy?: string;
  cancellationPaymentVoidedAt?: string;
}): string | null => {
  const parts: string[] = [];
  if (claim.cancellationPaymentVoidReason) parts.push(claim.cancellationPaymentVoidReason);
  if (claim.cancellationPaymentVoidedBy) parts.push(`by ${claim.cancellationPaymentVoidedBy}`);
  const when = claim.cancellationPaymentVoidedAt ? new Date(claim.cancellationPaymentVoidedAt) : null;
  if (when && !Number.isNaN(when.getTime())) {
    parts.push(`on ${when.toLocaleDateString('en-GB')}, ${when.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`);
  }
  if (parts.length === 0) return null;
  const [first, ...rest] = parts;
  return claim.cancellationPaymentVoidReason ? [first, rest.join(' ')].filter(Boolean).join(' – ') : parts.join(' ');
};

const formatCancellationPaymentStatus = (status: string): string => {
  const normalized = status?.toLowerCase?.() ?? '';
  switch (normalized) {
    case 'requested':
      return 'Payment requested';
    case 'paid':
      return 'Paid';
    case 'not_required':
      return 'Not required';
    default:
      return status || 'Unknown';
  }
};

type GeoLookupEntry = {
  loading: boolean;
  data?: GeoIpResult;
  error?: string;
};

// Helper function to count available documents
const getDocumentCount = (claim: Claim): number => {
  let count = 0;
  if (claim.formOfAuthorityUrl) count++;
  if (claim.finalBillUrl) count++;
  if (claim.damageBasedAgreementUrl) count++;
  if (claim.locDocUrl) count++;
  if (claim.valid8responseUrl) count++;
  if (claim.fileCount && claim.fileCount > 0) count += claim.fileCount;
  return count;
};

const modalCardStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: '480px',
  margin: '0 auto',
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  padding: '24px',
  boxShadow: '0 24px 48px rgba(15, 23, 42, 0.18)',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px'
};

const modalHeadingStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '20px',
  fontWeight: 600,
  color: '#0f172a'
};

const modalDescriptionStyle: React.CSSProperties = {
  margin: 0,
  color: '#475569',
  lineHeight: 1.5
};

const modalActionsStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
  flexWrap: 'wrap'
};

const modalButtonBaseStyle: React.CSSProperties = {
  padding: '10px 16px',
  borderRadius: '8px',
  fontWeight: 600,
  fontSize: '14px',
  cursor: 'pointer',
  transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
  border: 'none'
};

// Documents Modal Component
const DocsModal: React.FC<{
  claim: Claim | null;
  onClose: () => void;
  onViewCreditCheck?: (claim: Claim) => void;
  onViewFullReport?: (claimId: string) => void;
  onViewFiles?: (claim: Claim) => void;
  onViewAmlSummary?: (claim: Claim) => void;
  onOpenPdf?: (url: string, fileName: string) => void;
  onOpenDoc?: (url: string, fileName: string) => void;
}> = ({ claim, onClose, onViewCreditCheck, onViewFullReport, onViewFiles, onViewAmlSummary, onOpenPdf, onOpenDoc }) => {
  const [locDocs, setLocDocs] = useState<LocBlobDocument[]>([]);
  const [locLoading, setLocLoading] = useState(false);
  const [locDropdownOpen, setLocDropdownOpen] = useState(false);
  const [foaDocs, setFoaDocs] = useState<LocBlobDocument[]>([]);
  const [foaLoading, setFoaLoading] = useState(false);
  const [foaDropdownOpen, setFoaDropdownOpen] = useState(false);

  // Fetch LOC documents from blob storage when claim changes
  useEffect(() => {
    if (!claim) return;
    setLocLoading(true);
    setLocDocs([]);
    listLocDocuments(claim.id)
      .then(res => setLocDocs(res.documents))
      .catch(err => console.error('Failed to fetch LOC documents:', err))
      .finally(() => setLocLoading(false));
  }, [claim?.id]);

  // Fetch FOA documents when claim changes
  useEffect(() => {
    if (!claim) return;
    setFoaLoading(true);
    setFoaDocs([]);
    listFoaDocuments(claim.id)
      .then(res => setFoaDocs(res.documents))
      .catch(err => console.error('Failed to fetch FOA documents:', err))
      .finally(() => setFoaLoading(false));
  }, [claim?.id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!locDropdownOpen && !foaDropdownOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.loc-dropdown-container')) {
        setLocDropdownOpen(false);
      }
      if (!target.closest('.foa-dropdown-container')) {
        setFoaDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [locDropdownOpen, foaDropdownOpen]);

  if (!claim) return null;

  const handleOpenDocument = async (url: string, fileName: string) => {
    try {
      const response = await generateDocumentSasUrl({ blobUrl: url });
      const freshUrl = response.sasUrl;
      const isPdf = url.toLowerCase().includes('.pdf');
      if (isPdf && onOpenPdf) {
        onClose();
        onOpenPdf(freshUrl, fileName);
      } else {
        window.open(freshUrl, '_blank');
      }
    } catch (error) {
      console.error('Failed to get fresh URL, using original:', error);
      window.open(url, '_blank');
    }
  };

  return (
    <div
      className="credit-check-modal-overlay"
      onClick={onClose}
    >
      <div
        className="credit-check-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>Documents - Claim {claim.id}</h2>
          <button
            className="close-button"
            onClick={onClose}
          >
            &times;
          </button>
        </div>

        <div className="modal-content">
          <div className="docs-list">
            {/* Form of Authority - shows all historical FOAs from blob storage */}
            {foaLoading ? (
              <div className="doc-item">
                <span className="doc-icon">📄</span>
                <div className="doc-details">
                  <strong>Form of Authority</strong>
                  <p>Loading documents...</p>
                </div>
              </div>
            ) : foaDocs.length === 1 ? (
              <div className="doc-item">
                <span className="doc-icon">📄</span>
                <div className="doc-details">
                  <strong>Form of Authority</strong>
                  <p>{foaDocs[0].lastModified
                    ? new Date(foaDocs[0].lastModified).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                    : 'Legal authority document'}</p>
                </div>
                <button
                  onClick={() => handleOpenDocument(foaDocs[0].url, foaDocs[0].blobName || 'Form of Authority')}
                  className="doc-action-btn"
                >
                  Open
                </button>
              </div>
            ) : foaDocs.length > 1 ? (
              <div className="doc-item">
                <span className="doc-icon">📄</span>
                <div className="doc-details">
                  <strong>Form of Authority</strong>
                  <p>{foaDocs.length} documents available</p>
                </div>
                <div className="doc-actions foa-dropdown-container" style={{ position: 'relative' }}>
                  <button
                    onClick={() => setFoaDropdownOpen(!foaDropdownOpen)}
                    className="doc-action-btn"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                  >
                    Open
                    <span style={{ fontSize: '0.7rem' }}>{foaDropdownOpen ? '\u25B2' : '\u25BC'}</span>
                  </button>
                  {foaDropdownOpen && (
                    <div className="foa-dropdown-menu" style={{
                      position: 'absolute',
                      top: 'calc(100% + 6px)',
                      right: 0,
                      minWidth: '280px',
                      background: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
                      padding: '6px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '2px',
                      zIndex: 1000,
                    }}>
                      {foaDocs.map((doc, index) => (
                        <button
                          key={doc.blobName || index}
                          onClick={() => {
                            setFoaDropdownOpen(false);
                            handleOpenDocument(doc.url, doc.blobName || 'Form of Authority');
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '10px 12px',
                            background: 'transparent',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            color: '#1e293b',
                            textAlign: 'left',
                            width: '100%',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f1f5f9')}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                        >
                          <span style={{ flex: 1 }}>
                            {doc.lastModified
                              ? new Date(doc.lastModified).toLocaleDateString('en-GB', {
                                  day: 'numeric', month: 'short', year: 'numeric',
                                  hour: '2-digit', minute: '2-digit'
                              })
                              : 'Date unknown'}
                          </span>
                          {index === 0 && (
                            <span style={{
                              background: '#dcfce7',
                              color: '#166534',
                              fontSize: '11px',
                              fontWeight: 600,
                              padding: '2px 8px',
                              borderRadius: '10px',
                            }}>Latest</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : claim.formOfAuthorityUrl ? (
              <div className="doc-item">
                <span className="doc-icon">📄</span>
                <div className="doc-details">
                  <strong>Form of Authority</strong>
                  <p>Legal authority document</p>
                </div>
                <button
                  onClick={() => handleOpenDocument(claim.formOfAuthorityUrl!, 'Form of Authority')}
                  className="doc-action-btn"
                >
                  Open
                </button>
              </div>
            ) : null}

            {claim.finalBillUrl && (
              <div className="doc-item">
                <span className="doc-icon">📄</span>
                <div className="doc-details">
                  <strong>Final Statute Bill</strong>
                  <p>Final statute bill document</p>
                </div>
                <button
                  onClick={() => handleOpenDocument(claim.finalBillUrl!, 'Final Statute Bill')}
                  className="doc-action-btn"
                >
                  Open
                </button>
              </div>
            )}

            {claim.damageBasedAgreementUrl && (
              <div className="doc-item">
                <span className="doc-icon">📋</span>
                <div className="doc-details">
                  <strong>Damage Based Agreement</strong>
                  <p>Fee agreement document</p>
                </div>
                <button
                  onClick={() => handleOpenDocument(claim.damageBasedAgreementUrl!, 'Damage Based Agreement')}
                  className="doc-action-btn"
                >
                  Open
                </button>
              </div>
            )}

            {/* Letter of Complaint - shows all historical LOCs from blob storage */}
            {locLoading ? (
              <div className="doc-item">
                <span className="doc-icon">📧</span>
                <div className="doc-details">
                  <strong>Letter of Complaint</strong>
                  <p>Loading documents...</p>
                </div>
              </div>
            ) : locDocs.length === 1 ? (
              <div className="doc-item">
                <span className="doc-icon">📧</span>
                <div className="doc-details">
                  <strong>Letter of Complaint</strong>
                  <p>{locDocs[0].lastModified
                    ? new Date(locDocs[0].lastModified).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                    : 'Date unknown'}</p>
                </div>
                <button
                  onClick={() => handleOpenDocument(locDocs[0].url, locDocs[0].blobName)}
                  className="doc-action-btn"
                >
                  Open
                </button>
              </div>
            ) : locDocs.length > 1 ? (
              <div className="doc-item">
                <span className="doc-icon">📧</span>
                <div className="doc-details">
                  <strong>Letter of Complaint</strong>
                  <p>{locDocs.length} documents available</p>
                </div>
                <div className="doc-actions loc-dropdown-container">
                  <button
                    onClick={() => setLocDropdownOpen(!locDropdownOpen)}
                    className="doc-action-btn"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                  >
                    Open
                    <span style={{ fontSize: '0.7rem' }}>{locDropdownOpen ? '\u25B2' : '\u25BC'}</span>
                  </button>
                  {locDropdownOpen && (
                    <div className="loc-dropdown-menu" style={{
                      position: 'absolute',
                      top: 'calc(100% + 6px)',
                      right: 0,
                      minWidth: '280px',
                      background: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
                      padding: '6px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '2px',
                      zIndex: 1000,
                    }}>
                      {locDocs.map((doc, index) => (
                        <button
                          key={doc.blobName}
                          onClick={() => {
                            setLocDropdownOpen(false);
                            handleOpenDocument(doc.url, doc.blobName);
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '10px 12px',
                            background: 'transparent',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            color: '#1e293b',
                            textAlign: 'left',
                            width: '100%',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f1f5f9')}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                        >
                          <span style={{ flex: 1 }}>
                            {doc.lastModified
                              ? new Date(doc.lastModified).toLocaleDateString('en-GB', {
                                  day: 'numeric', month: 'short', year: 'numeric',
                                  hour: '2-digit', minute: '2-digit'
                                })
                              : 'Date unknown'}
                          </span>
                          {index === 0 && (
                            <span style={{
                              background: '#dcfce7',
                              color: '#166534',
                              fontSize: '11px',
                              fontWeight: 600,
                              padding: '2px 8px',
                              borderRadius: '10px',
                            }}>Latest</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : null}

            {claim.valid8responseUrl && (
              <div className="doc-item">
                <span className="doc-icon">📊</span>
                <div className="doc-details">
                  <strong>Credit Check Report</strong>
                  <p>Valid8/Experian credit verification</p>
                </div>
                <div className="doc-actions">
                  <button
                    onClick={() => {
                      onClose();
                      if (onViewCreditCheck) onViewCreditCheck(claim);
                    }}
                    className="doc-action-btn secondary"
                  >
                    Summary
                  </button>
                  <button
                    onClick={() => {
                      console.log('Full Report button clicked in Docs Modal', {
                        claimId: claim.id,
                        onViewFullReport: !!onViewFullReport
                      });
                      if (onViewFullReport) {
                        onViewFullReport(claim.id);
                      }
                    }}
                    className="doc-action-btn"
                  >
                    Full Report
                  </button>
                </div>
              </div>
            )}

            {(claim.amlCheckUrl || claim.amlcheckUrl) && (
              <div className="doc-item">
                <span className="doc-icon">🛡️</span>
                <div className="doc-details">
                  <strong>AML Check</strong>
                  <p>Anti-money laundering verification</p>
                </div>
                <div className="doc-actions">
                  <button
                    onClick={() => {
                      onClose();
                      if (onViewAmlSummary) onViewAmlSummary(claim);
                    }}
                    className="doc-action-btn secondary"
                  >
                    Summary
                  </button>
                </div>
              </div>
            )}

            {/* Client Re-signed Documents Section */}
            {(claim.foaResendSignedUrl || claim.dbaResendSignedUrl) && (
              <>
                {claim.foaResendSignedUrl && (
                  <div className="doc-item" style={{ borderLeft: '4px solid #22c55e', background: 'linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%)' }}>
                    <span className="doc-icon">✅</span>
                    <div className="doc-details">
                      <strong style={{ color: '#15803d' }}>FOA - Re-signed by Client</strong>
                      <p style={{ color: '#166534' }}>
                        {claim.foaResendSignedAt
                          ? `Signed on ${new Date(claim.foaResendSignedAt).toLocaleDateString()}`
                          : 'Client re-signed FOA document'}
                      </p>
                    </div>
                    <button
                      onClick={() => handleOpenDocument(claim.foaResendSignedUrl!, 'FOA Re-signed')}
                      className="doc-action-btn"
                      style={{ backgroundColor: '#22c55e', borderColor: '#22c55e' }}
                    >
                      View PDF
                    </button>
                  </div>
                )}

                {claim.dbaResendSignedUrl && (
                  <div className="doc-item" style={{ borderLeft: '4px solid #22c55e', background: 'linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%)' }}>
                    <span className="doc-icon">✅</span>
                    <div className="doc-details">
                      <strong style={{ color: '#15803d' }}>DBA - Re-signed by Client</strong>
                      <p style={{ color: '#166534' }}>
                        {claim.dbaResendSignedAt
                          ? `Signed on ${new Date(claim.dbaResendSignedAt).toLocaleDateString()}`
                          : 'Client re-signed DBA document'}
                      </p>
                    </div>
                    <button
                      onClick={() => handleOpenDocument(claim.dbaResendSignedUrl!, 'DBA Re-signed')}
                      className="doc-action-btn"
                      style={{ backgroundColor: '#22c55e', borderColor: '#22c55e' }}
                    >
                      View PDF
                    </button>
                  </div>
                )}
              </>
            )}

            {claim.fileCount && claim.fileCount > 0 && (
              <div className="doc-item">
                <span className="doc-icon">📎</span>
                <div className="doc-details">
                  <strong>Correspondence Files</strong>
                  <p>{claim.fileCount} uploaded file{claim.fileCount !== 1 ? 's' : ''}</p>
                </div>
                <button
                  onClick={() => {
                    onClose();
                    if (onViewFiles) onViewFiles(claim);
                  }}
                  className="doc-action-btn"
                >
                  View Files
                </button>
              </div>
            )}

            {getDocumentCount(claim) === 0 && !locLoading && locDocs.length === 0 && (
              <div className="no-docs">
                <p>No documents available for this claim yet.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

type PaymentPreview = {
  chargeableClaimIds: string[];
  chargeableClaimCount: number;
  estimatedAmount?: number;
  currency?: string;
  breakdown?: ClaimBreakdownEntry[];
};

type PendingCancellationState = {
  claimId: string;
  claimEmail: string;
  cancelAllAssociated: boolean;
  associatedData: AssociatedClaimsResponse;
  reason: string;
  nonCooperationReasonCodes?: NonCooperationReasonCode[];
  requiresPaymentDecision: boolean;
  paymentPreview?: PaymentPreview;
  /** Task 6: the date the client actually asked to cancel, when staff know
   * it. Empty string = not supplied = today's behaviour. */
  requestReceivedDate?: string;
} | null;

type EditFormData = {
  title: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  addressLine1: string;
  addressLine2: string;
  town: string;
  county: string;
  postcode: string;
  dateOfBirth: string;
};

type NewTaskFormState = {
  subject: string;
  description: string;
  dueDate: string;
  priority: 'high' | 'medium' | 'low';
};

type ClientVerificationState = Awaited<ReturnType<typeof photoIdApi.getClientVerification>>;

interface CreditCheckAddressEntry {
  type?: string;
  source?: string;
  address?: string;
}

interface CreditCheckHouseholdMember {
  name?: string;
  registered_since?: string;
}

interface CreditCheckAccountEntry {
  account_number?: string;
  lender?: string;
  account_type?: string;
  start_date?: string;
  status?: string;
  balance?: string;
  payment?: string;
  payment_frequency?: string;
  worst_status?: string;
  settlement_date?: string | null;
}

interface CreditCheckSummaryState {
  claimId?: string;
  provider?: string;
  formatType?: string;
  summary?: {
    overall_decision?: string;
    identity_verified?: boolean;
    credit_accounts_found?: number;
    total_balance?: string;
    worst_status?: string;
    pep_sanctions_clear?: boolean;
    electoral_roll_confirmed?: boolean;
    decision_timestamp?: string | null;
  };
  applicant?: {
    name?: string;
    date_of_birth?: string;
    age?: string;
    address?: string;
    all_addresses?: CreditCheckAddressEntry[];
  };
  creditAccounts?: CreditCheckAccountEntry[];
  electoralRoll?: {
    confirmed?: boolean;
    years_at_address?: string;
    household_members?: CreditCheckHouseholdMember[];
  };
  reportAvailable?: boolean;
  /** Set when this claim has no report and the summary is its .001 claim's. */
  reportFromClaimId?: string | null;
  /** The line to show for that case, worded by the server. */
  reportNotice?: string | null;
  message?: string;
}

const DEFAULT_PAGE_SIZE = 50;

export default function ClaimsTable() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin, hasRole } = useAuth();
  const canEditCreditAgreement = isAdmin || hasRole('credit-agreement-editor');

  const pageSize = DEFAULT_PAGE_SIZE;
  const [claims, setClaims] = useState<Claim[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  // Read initial search from URL params (e.g., after split claims redirect)
  const initialSearch = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('search') || '';
  }, []);
  const [search, setSearch] = useState<string>(initialSearch);
  const [searchInput, setSearchInput] = useState<string>(initialSearch);
  const [pageInput, setPageInput] = useState<string>('');
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [sendingIds, setSendingIds] = useState<Set<string>>(new Set());
  const [sendingAll, setSendingAll] = useState<boolean>(false);
  const [locResendClaim, setLocResendClaim] = useState<Claim | null>(null);
  const [selectedClaim, setSelectedClaim] = useState<Claim | null>(null);
  const [riskAssessmentVersions, setRiskAssessmentVersions] = useState<RiskAssessmentVersion[] | null>(null);
  const [showDocsModal, setShowDocsModal] = useState<boolean>(false);
  const [selectedClaimForDocs, setSelectedClaimForDocs] = useState<Claim | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [taskCompleteError, setTaskCompleteError] = useState<{ taskId: string; message: string } | null>(null);
  const [actionLoading, setActionLoading] = useState<Set<string>>(new Set());
  const [pendingCancellation, setPendingCancellation] = useState<PendingCancellationState>(null);
  const [phoneReviewTask, setPhoneReviewTask] = useState<{
    taskId: string;
    requestedReason: string | null;
    clientEmail: string | null;
  } | null>(null);
  const [showReasonModal, setShowReasonModal] = useState<boolean>(false);
  const [showBypassModal, setShowBypassModal] = useState<boolean>(false);
  const [showReviewModal, setShowReviewModal] = useState<boolean>(false);
  const [reviewBreakdown, setReviewBreakdown] = useState<{
    breakdown: ClaimBreakdownEntry[];
    chargeableCount: number;
    estimatedAmount: number;
    currency: string;
    chargeableClaimIds: string[];
  } | null>(null);
  // Monotonic request id for /cancel/preview — used to drop stale responses
  // when the user clicks Continue → Back → Continue in quick succession.
  const previewRequestId = useRef(0);
  const [showCancelScopeModal, setShowCancelScopeModal] = useState<boolean>(false);
  const [cancelScopeData, setCancelScopeData] = useState<{ claimId: string; claimEmail: string; associatedData: AssociatedClaimsResponse } | null>(null);
  const [reasonDraft, setReasonDraft] = useState<string>('');
  // Task 6: optional "Request received date". Pre-filled from the
  // ?requestReceivedDate deep link (a cancellation manual-review task links
  // here with the date it holds); empty otherwise, which is byte-identical
  // to the pre-Task-6 behaviour.
  const [requestReceivedDateDraft, setRequestReceivedDateDraft] = useState<string>('');
  const prefilledRequestReceivedDate = useMemo(() => {
    const raw = new URLSearchParams(location.search).get('requestReceivedDate');
    if (!raw) return '';
    // <input type="date"> only accepts YYYY-MM-DD; the link may carry a
    // full ISO timestamp.
    const match = raw.match(/^\d{4}-\d{2}-\d{2}/);
    return match ? match[0] : '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [noChargeWaiverReason, setNoChargeWaiverReason] = useState<string>('');
  const [selectedCancellationReason, setSelectedCancellationReason] = useState<string>('Client request');
  const [clientRequestSubReason, setClientRequestSubReason] = useState<string>('');
  const [selectedNonCooperationReasons, setSelectedNonCooperationReasons] = useState<NonCooperationReasonCode[]>([]);
  const [updateMessage, setUpdateMessage] = useState<string>('');
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [editFormData, setEditFormData] = useState<EditFormData>({
    title: '',
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    addressLine1: '',
    addressLine2: '',
    town: '',
    county: '',
    postcode: '',
    dateOfBirth: '',
  });
  // Vehicle reg is per-claim (not per-client, #405) but NOT a credit-agreement
  // fact, so it stays editable by any authenticated staff member via the
  // dedicated (unrestricted) agreement-fields endpoint, separate from both the
  // client-info form and the role-gated Credit Agreement panel below. Never
  // propagated to sibling claims.
  const [editVehicleRegistration, setEditVehicleRegistration] = useState<string>('');
  // Role-gated Credit Agreement edit (admins + credit-agreement-editor). Its own
  // mode so it never entangles the client-info edit; every change is audited server-side.
  const [caEditMode, setCaEditMode] = useState<boolean>(false);
  const [caEdits, setCaEdits] = useState<Record<string, string>>({});
  const [caSaving, setCaSaving] = useState<boolean>(false);
  // Inline agreement-number edit - any staff member, separate from the
  // role-gated panel edit above (hidden while that is open).
  const [anEditing, setAnEditing] = useState<boolean>(false);
  const [anValue, setAnValue] = useState<string>('');
  const [anSaving, setAnSaving] = useState<boolean>(false);
  const [anError, setAnError] = useState<string | null>(null);
  const [anWarning, setAnWarning] = useState<string | null>(null);
  // The claim the drawer shows now, so a slow save can tell if staff moved on.
  const drawerClaimIdRef = useRef<string | null>(null);
  const [emailComposerClaim, setEmailComposerClaim] = useState<Claim | null>(null);
  const [showEmailComposer, setShowEmailComposer] = useState<boolean>(false);
  const [smsComposerClaim, setSmsComposerClaim] = useState<Claim | null>(null);
  const [showSmsComposer, setShowSmsComposer] = useState<boolean>(false);
  const [showEmailDrawer, setShowEmailDrawer] = useState<boolean>(false);
  const [selectedClaimAudits, setSelectedClaimAudits] = useState<Audit[]>([]);
  const [showAuditForm, setShowAuditForm] = useState<boolean>(false);
  const [clientVerification, setClientVerification] = useState<ClientVerificationState | null>(null);
  const [verificationLoading, setVerificationLoading] = useState<boolean>(false);
  const [showIdModal, setShowIdModal] = useState<boolean>(false);
  const [claimTasks, setClaimTasks] = useState<UnifiedTask[]>([]);
  const [tasksLoading, setTasksLoading] = useState<boolean>(false);
  const [auditLoading, setAuditLoading] = useState<boolean>(false);
  const [geoLookups, setGeoLookups] = useState<Record<string, GeoLookupEntry>>({});
  const [showNewTaskModal, setShowNewTaskModal] = useState<boolean>(false);
  const [newTaskForm, setNewTaskForm] = useState<NewTaskFormState>({
    subject: '',
    description: '',
    dueDate: '',
    priority: 'medium',
  });
  const [showCreditCheckModal, setShowCreditCheckModal] = useState<boolean>(false);
  const [creditCheckSummary, setCreditCheckSummary] = useState<CreditCheckSummaryState | null>(null);
  const [creditCheckClaim, setCreditCheckClaim] = useState<Claim | null>(null);
  const [creditCheckLoading, setCreditCheckLoading] = useState<boolean>(false);
  const [creditCheckError, setCreditCheckError] = useState<string>('');
  const [showAmlModal, setShowAmlModal] = useState<boolean>(false);
  const [amlSummary, setAmlSummary] = useState<ClaimAmlSummary | null>(null);
  const [amlClaim, setAmlClaim] = useState<Claim | null>(null);
  const [amlLoading, setAmlLoading] = useState<boolean>(false);
  const [amlError, setAmlError] = useState<string>('');
  const [keyDates, setKeyDates] = useState<ClaimKeyDatesView | null>(null);
  const [keyDatesLoading, setKeyDatesLoading] = useState<boolean>(false);
  const [keyDatesSaving, setKeyDatesSaving] = useState<boolean>(false);
  const [keyDatesError, setKeyDatesError] = useState<string | null>(null);
  const [lenderBulkSubmission, setLenderBulkSubmission] = useState<LenderBulkSubmissionDetails | null>(null);
  const [lbsLoading, setLbsLoading] = useState<boolean>(false);
  const [lbsSaving, setLbsSaving] = useState<boolean>(false);
  const [lbsError, setLbsError] = useState<string | null>(null);
  const [showLbsModal, setShowLbsModal] = useState<boolean>(false);
  const [actionsMenuOpen, setActionsMenuOpen] = useState<boolean>(false);
  const [sendEmailMenuOpen, setSendEmailMenuOpen] = useState<boolean>(false);
  const [showLenderWithdrawalConfirm, setShowLenderWithdrawalConfirm] = useState<boolean>(false);
  const lenderWithdrawalInFlight = useRef(false);
  const [lenderWithdrawalSending, setLenderWithdrawalSending] = useState(false);
  // Retain accepted sends through failed/stale refreshes for this table session.
  const [lenderWithdrawalsSent, setLenderWithdrawalsSent] = useState<Set<string>>(new Set());
  const [lenderWithdrawalResult, setLenderWithdrawalResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [showMultipleRepConfirm, setShowMultipleRepConfirm] = useState<boolean>(false);
  const [multipleRepResult, setMultipleRepResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [drawerHiddenForModal, setDrawerHiddenForModal] = useState<boolean>(false);
  const [showRedressModal, setShowRedressModal] = useState<boolean>(false);
  const [showUnitHistory, setShowUnitHistory] = useState<boolean>(false);
  const [showLetterModal, setShowLetterModal] = useState<boolean>(false);
  const [selectedClaimForLetter, setSelectedClaimForLetter] = useState<Claim | null>(null);
  const [showSplitClaimsModal, setShowSplitClaimsModal] = useState<boolean>(false);
  const [finalResponseClaim, setFinalResponseClaim] = useState<Claim | null>(null);
  const [reinstatementScopeClaim, setReinstatementScopeClaim] = useState<Claim | null>(null);
  const [reinstatementPaymentPrompt, setReinstatementPaymentPrompt] = useState<{
    impact: ReinstatementPaymentImpact;
    force: boolean;
  } | null>(null);
  const [reinstatementPaymentSubmitting, setReinstatementPaymentSubmitting] = useState(false);
  // Non-viable (disabled) lender block when reinstating. Holds the claim + lender awaiting an admin override decision.
  const [lenderBlockModal, setLenderBlockModal] = useState<{ claimId: string; lenderName: string } | null>(null);

  // Duplication Audit state
  const [showDupAuditModal, setShowDupAuditModal] = useState<boolean>(false);
  const [dupAudit, setDupAudit] = useState<DuplicationAudit | null>(null);
  const [dupAuditLoading, setDupAuditLoading] = useState<boolean>(false);
  const [dupAuditSaving, setDupAuditSaving] = useState<boolean>(false);
  const [dupAuditError, setDupAuditError] = useState<string | null>(null);

  // Claim notes state

  // Litigation Friend modal state
  const [showLitigationFriendModal, setShowLitigationFriendModal] = useState<boolean>(false);
  const [showAuditTrailModal, setShowAuditTrailModal] = useState<boolean>(false);

  // Enforcement flag state
  const [showEnforcementModal, setShowEnforcementModal] = useState<boolean>(false);
  const [enforcementForm, setEnforcementForm] = useState<{ doNotEnforce: boolean; doNotEnforceReason: string; doNotEnforceNotes: string }>({ doNotEnforce: false, doNotEnforceReason: '', doNotEnforceNotes: '' });
  const [enforcementSaving, setEnforcementSaving] = useState<boolean>(false);

  // Client document resend state
  const [resendingFoa, setResendingFoa] = useState<boolean>(false);
  const [resendingDba, setResendingDba] = useState<boolean>(false);
  const [syncingClientResend, setSyncingClientResend] = useState<boolean>(false);
  const [sendingLoaQuestionnaire, setSendingLoaQuestionnaire] = useState<boolean>(false);
  const [generatingDsar, setGeneratingDsar] = useState<boolean>(false);
  const [resendConfirmModal, setResendConfirmModal] = useState<{
    open: boolean;
    documentType: 'foa' | 'dba' | null;
    originalSignedDate: string | null;
    originalUrl: string | null;
  }>({ open: false, documentType: null, originalSignedDate: null, originalUrl: null });

  // PDF Viewer state
  const [pdfViewerOpen, setPdfViewerOpen] = useState<boolean>(false);
  const [selectedPdf, setSelectedPdf] = useState<{ url: string; fileName: string } | null>(null);

  // Doc Viewer state (for Word docs and other non-PDF formats)
  const [docViewerOpen, setDocViewerOpen] = useState<boolean>(false);
  const [selectedDoc, setSelectedDoc] = useState<{ url: string; fileName: string } | null>(null);
  const [pdfViewerOpenedFromAudit, setPdfViewerOpenedFromAudit] = useState<boolean>(false);
  const [auditFormHiddenForPdf, setAuditFormHiddenForPdf] = useState<boolean>(false);

  const filterWithoutLOC = false;
  const shouldHideClaimDrawer = drawerHiddenForModal || showAuditForm || showDupAuditModal || showRedressModal;

  const openPdfViewer = useCallback(async (url: string, fileName: string, fromAudit = false) => {
    try {
      // Get fresh SAS URL (or S3 URL returned directly for S3 documents)
      const response = await generateDocumentSasUrl({ blobUrl: url });
      setSelectedPdf({ url: response.sasUrl, fileName });
    } catch (error) {
      console.error('Failed to get fresh document URL, using original:', error);
      setSelectedPdf({ url, fileName });
    }
    setPdfViewerOpen(true);
    setPdfViewerOpenedFromAudit(fromAudit);
  }, []);

  const handlePdfViewerClose = useCallback(() => {
    setPdfViewerOpen(false);
    setSelectedPdf(null);
    if (pdfViewerOpenedFromAudit) {
      setAuditFormHiddenForPdf(false);
      setPdfViewerOpenedFromAudit(false);
    }
  }, [pdfViewerOpenedFromAudit]);

  const handleAuditDocumentView = useCallback((url: string, fileName: string) => {
    setAuditFormHiddenForPdf(true);
    openPdfViewer(url, fileName, true);
  }, [openPdfViewer]);

  const closeAuditForm = useCallback(() => {
    setShowAuditForm(false);
    setAuditFormHiddenForPdf(false);
    if (pdfViewerOpenedFromAudit) {
      handlePdfViewerClose();
    }
  }, [handlePdfViewerClose, pdfViewerOpenedFromAudit]);

  const cancellationPaymentSummary = useMemo(() => {
    if (!selectedClaim || !selectedClaim.cancellationPaymentStatus) return null;
    const status = selectedClaim.cancellationPaymentStatus.toLowerCase();
    const source = selectedClaim.cancellationSource?.toLowerCase();
    const amount = typeof selectedClaim.cancellationPaymentAmountTotal === 'number'
      ? selectedClaim.cancellationPaymentAmountTotal
      : null;

    if (status === 'not_required') {
      const bypassed = source === 'admin-ui' || selectedClaim.cancellationReason?.toLowerCase().includes('bypass');
      return {
        summary: bypassed ? 'No fee (admin bypass)' : 'No fee required',
        amount: null,
        requestedAt: null,
        paidAt: null,
        isVoided: false,
      };
    }

    // Handle voided status
    if (status === 'voided') {
      return {
        summary: 'Payment voided',
        amount,
        requestedAt: selectedClaim.cancellationPaymentRequestedAt || null,
        paidAt: null,
        isVoided: true,
        // #591: why, by whom and when -- e.g. "Payment Plan Agreed".
        voidDetail: describeFeeVoid(selectedClaim),
      };
    }

    if (status === 'requested') {
      return {
        summary: 'Awaiting payment',
        amount,
        requestedAt: selectedClaim.cancellationPaymentRequestedAt || null,
        paidAt: null,
        isVoided: false,
      };
    }

    if (status === 'paid') {
      const manualPay = selectedClaim.cancellationManualPayment;
      // Final fix-wave (Important 1): every settlement channel (bank
      // transfer, Worldpay staff-confirm, and Stripe's own webhook) writes
      // a cancellationManualPayment block -- it's the shared amount/ledger
      // bookkeeping shape settle_payment uses for ALL of them, not a
      // signal that a human recorded the payment by hand.
      //
      // Fix round 1 (Minor 2): the discriminator is read from
      // cancellationManualPayment.recordedBy ('stripe_webhook' is the
      // literal only settle_payment's Stripe branch writes there), NOT
      // claim-level cancellationSource -- cancellationSource is
      // overwritten by other cancellation flows that have nothing to do
      // with payment method (claim_cancellation_service.py's generic
      // re-cancel, admin.py's 'loc-auto-cancel' and
      // 'automated-associated-cancellation' auto-cancels), so a claim
      // re-cancelled by one of those after a genuine Stripe settlement
      // would otherwise drift back to reading "Paid manually".
      // cancellationManualPayment itself is never touched by those flows.
      const isStripePayment = manualPay?.recordedBy === 'stripe_webhook';
      return {
        summary: manualPay && !isStripePayment ? 'Paid manually' : 'Payment received',
        amount: manualPay ? manualPay.amountPaid : amount,
        requestedAt: selectedClaim.cancellationPaymentRequestedAt || null,
        paidAt: selectedClaim.cancellationPaymentPaidAt || null,
        isVoided: false,
      };
    }

    if (status === 'partial') {
      const manualPay = selectedClaim.cancellationManualPayment;
      const paidAmount = manualPay?.amountPaid || 0;
      const expectedAmount = amount || manualPay?.expectedAmount || 0;
      return {
        summary: `Partial payment (£${paidAmount.toFixed(2)} of £${expectedAmount.toFixed(2)})`,
        amount: expectedAmount,
        requestedAt: selectedClaim.cancellationPaymentRequestedAt || null,
        paidAt: selectedClaim.cancellationPaymentPaidAt || null,
        isVoided: false,
      };
    }

    return {
      summary: formatCancellationPaymentStatus(selectedClaim.cancellationPaymentStatus),
      amount,
      requestedAt: selectedClaim.cancellationPaymentRequestedAt || null,
      paidAt: selectedClaim.cancellationPaymentPaidAt || null,
      isVoided: false,
    };
  }, [selectedClaim]);

  const amlIndividual = amlSummary?.summary?.input?.Individual as any | undefined;
  const amlAddress = amlSummary?.summary?.input?.Address as any | undefined;

  const unitBadgeTooltip = useMemo(() => {
    if (!selectedClaim || typeof selectedClaim.unitCount !== 'number') {
      return undefined;
    }

    const history = Array.isArray(selectedClaim.unitHistory)
      ? [...selectedClaim.unitHistory]
      : [];

    if (!history.length) {
      return 'Units reflect billable actions. Baseline is 2.';
    }

    history.sort((a, b) => {
      const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return bTime - aTime;
    });

    const recent = history.slice(0, 5);
    const lines = recent.map((entry) => {
      const timestamp = entry.timestamp ? new Date(entry.timestamp).toLocaleString() : 'Unknown time';
      const delta = typeof entry.delta === 'number' ? (entry.delta >= 0 ? `+${entry.delta}` : `${entry.delta}`) : '';
      const total = typeof entry.total === 'number' ? ` → ${entry.total}` : '';
      const reason = entry.reason ? ` – ${entry.reason}` : '';
      const actor = entry.actor ? ` by ${entry.actor}` : '';
      return `${timestamp}: ${delta}${total}${reason}${actor}`.trim();
    });

    return ['Units reflect billable actions. Recent changes:', ...lines].join('\n');
  }, [selectedClaim]);

  const dupAuditAutoSummary = useMemo(() => {
    if (!selectedClaim) return '';
    const lender = selectedClaim.craFinanceLender || 'unknown lender';
    const dateStr = selectedClaim.craCreditStartDate || selectedClaim.agreement_dateAgreed || '';
    let formatted = '';
    if (dateStr) {
      const parts = dateStr.split('/');
      if (parts.length >= 3) {
        const d = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
        if (!isNaN(d.getTime())) {
          formatted = d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
        }
      } else {
        const d = new Date(dateStr);
        if (!isNaN(d.getTime())) {
          formatted = d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
        }
      }
    }
    return `Car finance with ${lender}${formatted ? ` taken out in ${formatted}` : ''}`;
  }, [selectedClaim]);

  const unitHistoryRows = useMemo(() => {
    if (!showUnitHistory || !selectedClaim) {
      return [];
    }

    const history = Array.isArray(selectedClaim.unitHistory)
      ? [...selectedClaim.unitHistory]
      : [];

    if (!history.length) {
      const baseline = {
        timestamp: selectedClaim.createdInApiAt || null,
        delta: 0,
        total: selectedClaim.unitCount ?? 2,
        reason: 'Baseline initialised',
        actor: 'Claimsgate',
        source: 'claim-created'
      };
      return [baseline];
    }

    history.sort((a, b) => {
      const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return bTime - aTime;
    });

    return history;
  }, [selectedClaim, showUnitHistory]);

  const formattedUnitHistory = useMemo(() => {
    return unitHistoryRows.map((entry) => {
      const timestamp = entry.timestamp ? new Date(entry.timestamp).toLocaleString() : 'Unknown time';
      const delta = typeof entry.delta === 'number' ? (entry.delta >= 0 ? `+${entry.delta}` : `${entry.delta}`) : '';
      const total = typeof entry.total === 'number' ? entry.total : undefined;
      const reason = entry.reason || 'No reason provided';
      const rawActor = entry.actor || 'System';
      const actor = rawActor === 'system' ? 'System' : rawActor;
      const source = entry.source ? entry.source.replace(/[-_]/g, ' ') : '';

      return {
        timestamp,
        delta,
        total,
        reason,
        actor,
        source,
      };
    });
  }, [unitHistoryRows]);

  useEffect(() => {
    setShowUnitHistory(false);
  }, [selectedClaim?.id]);

  // A half-finished agreement-number edit never carries over to another claim.
  useEffect(() => {
    drawerClaimIdRef.current = selectedClaim?.id ?? null;
    setAnEditing(false);
    setAnValue('');
    setAnSaving(false);
    setAnError(null);
    setAnWarning(null);
  }, [selectedClaim?.id]);

  // Which risk assessments this claim shows: matters opened before the v2
  // cutover keep the original alongside the update (#571). On failure the
  // drawer falls back to one button that opens the newest version.
  useEffect(() => {
    setRiskAssessmentVersions(null);
    const claimId = selectedClaim?.id;
    if (!claimId) return;
    let cancelled = false;
    Promise.resolve()
      .then(() => getRiskAssessmentVersions(claimId))
      .then(res => {
        if (!cancelled) setRiskAssessmentVersions(Array.isArray(res?.data?.versions) ? res.data.versions : null);
      })
      .catch(err => console.error('Error loading risk assessment versions:', err));
    return () => { cancelled = true; };
  }, [selectedClaim?.id]);

// Fetch helper
  const loadClaims = useCallback(
    (options: { silent?: boolean } = {}) => {
      const { silent = false } = options;
      if (!silent) setLoading(true);
      setError(null);

      return fetchClaims(page, pageSize, search)
        .then((data: ClaimPage & { totalCount?: number }) => {
          setClaims(data.items);
          setHasMore(data.hasMore);
          setTotalCount(data.totalCount ?? totalCount ?? 0);
        })
        .catch(err => {
          console.error('Error fetching claims:', err);
          setError(err.message || 'Error fetching claims');
          setClaims([]);
          setTotalCount(0);
          setHasMore(false);
        })
        .finally(() => {
          if (!silent) setLoading(false);
        });
    },
    [page, pageSize, search, totalCount]
  );

  const fetchKeyDates = useCallback(async (claimId: string) => {
    setKeyDatesLoading(true);
    setKeyDatesError(null);
    try {
      const response = await getClaimKeyDates(claimId);
      setKeyDates(response.keyDates);
    } catch (err: any) {
      console.error('Failed to fetch key dates for claim:', claimId, err);
      const message = err?.response?.data?.error || err?.message || 'Failed to load key dates';
      setKeyDatesError(message);
      setKeyDates(null);
    } finally {
      setKeyDatesLoading(false);
    }
  }, []);

  const handleRefreshKeyDates = useCallback(() => {
    if (selectedClaim) {
      fetchKeyDates(selectedClaim.id);
    }
  }, [selectedClaim, fetchKeyDates]);

  const handleSaveKeyDates = useCallback(
    async (updates: KeyDatesUpdatePayload) => {
      if (!selectedClaim) {
        const error = new Error('No claim selected');
        setKeyDatesError(error.message);
        throw error;
      }

      setKeyDatesSaving(true);
      setKeyDatesError(null);

      try {
        const response = await updateClaimKeyDates(selectedClaim.id, updates);
        setKeyDates(response.keyDates);
      } catch (err: any) {
        console.error('Failed to update key dates for claim:', selectedClaim.id, err);
        const message = err?.response?.data?.error || err?.message || 'Failed to update key dates';
        throw new Error(message);
      } finally {
        setKeyDatesSaving(false);
      }
    },
    [selectedClaim]
  );

  const fetchLenderBulkSubmission = useCallback(async (claimId: string) => {
    setLbsLoading(true);
    setLbsError(null);
    try {
      const response = await getLenderBulkSubmissionDetails(claimId);
      setLenderBulkSubmission(response.lenderBulkSubmission);
    } catch (err: any) {
      console.error('Failed to fetch lender bulk submission details for claim:', claimId, err);
      const message = err?.response?.data?.error || err?.message || 'Failed to load lender bulk submission details';
      setLbsError(message);
      setLenderBulkSubmission(null);
    } finally {
      setLbsLoading(false);
    }
  }, []);

  const handleRefreshLenderBulkSubmission = useCallback(() => {
    if (selectedClaim) {
      fetchLenderBulkSubmission(selectedClaim.id);
    }
  }, [selectedClaim, fetchLenderBulkSubmission]);

  const handleSaveLenderBulkSubmission = useCallback(
    async (updates: LenderBulkSubmissionUpdatePayload) => {
      if (!selectedClaim) {
        const error = new Error('No claim selected');
        setLbsError(error.message);
        throw error;
      }

      setLbsSaving(true);
    try {
      const response = await updateLenderBulkSubmissionDetails(selectedClaim.id, updates);
      setLenderBulkSubmission(response.lenderBulkSubmission);
      setLbsError(null);
    } catch (err: any) {
        console.error('Failed to update lender bulk submission details for claim:', selectedClaim.id, err);
        const message = err?.response?.data?.error || err?.message || 'Failed to update lender bulk submission details';
        setLbsError(message);
        throw new Error(message);
      } finally {
        setLbsSaving(false);
      }
    },
    [selectedClaim]
  );

  // Initial + on‐page/search change
  useEffect(() => { loadClaims(); }, [loadClaims]);

  // Handle opening a claim from navigation state (e.g., after creating a new
  // claim -- CreateClaim.tsx) OR from an `?openClaimId=` query param.
  //
  // Ticket #403: the task-detail pane's "Claim {id} ↗" chip used to be a
  // same-tab `<Link state={{ openClaimId }}>`, which lost the staff
  // member's place in the task list every time. It's now a real
  // `target="_blank"` anchor to `/?openClaimId=<id>` -- a fresh tab has no
  // navigation state to read, so this effect also accepts the id via the
  // query string. State still wins if somehow both are present (kept for
  // CreateClaim.tsx's existing same-tab redirect, unchanged).
  useEffect(() => {
    const state = location.state as { openClaimId?: string } | null;
    const queryClaimId = new URLSearchParams(location.search).get('openClaimId');
    const openClaimId = state?.openClaimId || queryClaimId || null;
    if (openClaimId) {
      // Fetch and open the claim
      const openNewClaim = async () => {
        try {
          const claim = await getClaim(openClaimId);
          setSelectedClaim(claim);
        } catch (error) {
          console.error('Failed to open claim:', error);
        }
      };
      openNewClaim();
      // Clear the state/query to prevent re-opening on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.search, location.pathname, navigate]);

  // Debug state changes for docs modal
  useEffect(() => {
    console.log('Docs modal state changed:', { showDocsModal, selectedClaimForDocs });
  }, [showDocsModal, selectedClaimForDocs]);


  // Handlers
  const handleSearch = () => {
    setPage(1);
    setSearch(searchInput.trim());
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      handleClearSearch();
    }
  };

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleSearch();
  };

  const handleRefresh = () => loadClaims();

  // Page jumping functions
  const handlePageJump = (targetPage: number) => {
    if (targetPage >= 1 && targetPage <= totalPages && targetPage !== page) {
      setPage(targetPage);
      setPageInput('');
    }
  };

  const handlePageInputSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const targetPage = parseInt(pageInput);
    if (!isNaN(targetPage)) {
      handlePageJump(targetPage);
    }
  };

  const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numeric input
    if (value === '' || /^\d+$/.test(value)) {
      setPageInput(value);
    }
  };

  // Generate page number buttons for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    // Adjust start page if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  const handleSendLOC = async (id: string) => {
    setSendingIds(s => new Set(s).add(id));
    try {
      await sendLOC(id);
      const sentAt = new Date().toISOString();
      setClaims(cs =>
        cs.map(c => c.id === id ? { ...c, locSentDate: sentAt } : c)
      );
      // The send endpoint creates the document URL server-side. Refresh an
      // open claim so an FC "Send new LOC" immediately becomes "Resend LOC"
      // and points at the document that was actually stored.
      if (selectedClaim?.id === id) {
        try {
          const freshClaim = await getClaim(id);
          setSelectedClaim(freshClaim);
        } catch (refreshError) {
          console.warn('LOC sent but refreshed claim details could not be loaded:', refreshError);
          setSelectedClaim(current => current?.id === id
            ? { ...current, locSentDate: sentAt }
            : current);
        }
      }
    } catch (err: any) {
      alert(`Error sending LOC for ${id}: ${err?.response?.data?.error || err.message}`);
      // Optionally log to console as well
      console.error('LOC send error:', err);
    } finally {
      setSendingIds(s => {
        const next = new Set(s);
        next.delete(id);
        return next;
      });
    }
  };

  const handleLocResent = (result: LocResendResult) => {
    const updates = {
      locSentDate: result.sentAt,
      locDocUrl: result.documentUrl,
    };
    setClaims(current => current.map(item => (
      item.id === result.claimId ? { ...item, ...updates } : item
    )));
    setSelectedClaim(current => (
      current?.id === result.claimId ? { ...current, ...updates } : current
    ));
    setUpdateMessage(`LOC version ${result.version} resent to ${result.lenderEmail}`);
    setTimeout(() => setUpdateMessage(''), 5000);
  };

  const handleSendAll = async () => {
    setSendingAll(true);
    for (const c of claims) {
      if (!sendingIds.has(c.id)) {
        // await to prevent API‐flood; you could also Promise.all
        // but then you'd lose per‐row locking
        // so we await each
        // eslint-disable-next-line no-await-in-loop
        await handleSendLOC(c.id);
      }
    }
    setSendingAll(false);
  };

  const handleSendAllWithoutLOC = async () => {
    setSendingAll(true);
    for (const c of claims.filter(c => !c.locSentDate)) {
      // eslint-disable-next-line no-await-in-loop
      await handleSendLOC(c.id);
    }
    setSendingAll(false);
  };

  // Add cancellation functionality
  const executeCancellation = async ({
    claimId,
    claimEmail,
    cancelAllAssociated,
    associatedData,
    reason,
    nonCooperationReasonCodes,
    decision,
    requestReceivedDate,
  }: {
    claimId: string;
    claimEmail: string;
    cancelAllAssociated: boolean;
    associatedData: AssociatedClaimsResponse;
    reason: string;
    nonCooperationReasonCodes?: NonCooperationReasonCode[];
    decision: 'bypass' | 'request' | null;
    requestReceivedDate?: string;
  }) => {
    try {
      const requestPayload: CancelClaimRequest = {
        reason,
        cancelAllAssociated,
      };
      if (nonCooperationReasonCodes?.length) {
        requestPayload.nonCooperationReasonCodes = nonCooperationReasonCodes;
      }
      if (requestReceivedDate) {
        requestPayload.requestReceivedDate = requestReceivedDate;
      }

      if (decision === 'bypass') {
        requestPayload.paymentDecision = 'bypass';
        requestPayload.bypassPayment = true;
        requestPayload.paymentDecisionConfirmed = true;
      } else if (decision === 'request') {
        requestPayload.paymentDecision = 'request';
        requestPayload.bypassPayment = false;
        requestPayload.paymentDecisionConfirmed = true;
      }

      const response = await cancelClaim(claimId, requestPayload);

      const updateSelectedClaim = (updates: Partial<Claim>) => {
        setSelectedClaim(prev => {
          if (!prev || prev.id !== claimId) return prev;
          return { ...prev, ...updates } as Claim;
        });
      };

      // The redress-scheme block is worked out by the backend on read, so
      // fetch it for the drawer once the claim is cancelled -- otherwise an
      // out-of-scheme claim shows a live Reinstate until the drawer reopens.
      // Best-effort: the server refuses the reinstatement either way.
      const refreshReinstatementBlock = async () => {
        try {
          const fresh = await getClaim(claimId);
          setSelectedClaim(prev => {
            if (!prev || prev.id !== claimId) return prev;
            return { ...prev, reinstatementBlock: fresh?.reinstatementBlock ?? null } as Claim;
          });
        } catch (refreshErr) {
          console.warn('Could not refresh the reinstatement block after cancelling:', refreshErr);
        }
      };

      // Card 11: CL claim - manual task was created instead of cancellation
      if (response.data.manualTaskCreated) {
        alert(
          response.data.message ||
          'A manual review task has been created. An agent will contact the client to determine which claim(s) to cancel.'
        );
        setPendingCancellation(null);
        setShowBypassModal(false);
        setShowReasonModal(false);
        await loadClaims({ silent: true });
        return;
      }

      // 70+ pre-cancellation hold: NOT a cancellation. No claim status
      // change to apply locally -- reload, then ask whether the client is on
      // the phone right now. Amendment 3 (19 Aug 2026): "Yes" opens the same
      // review script inline instead of leaving the task to wait in the lane.
      if (response.data.held) {
        setPendingCancellation(null);
        setShowBypassModal(false);
        setShowReasonModal(false);
        await loadClaims({ silent: true });

        const holdTaskId = heldReviewTaskId(response.data);
        if (holdTaskId && window.confirm(PHONE_REVIEW_QUESTION)) {
          setPhoneReviewTask({ taskId: holdTaskId, requestedReason: reason, clientEmail: claimEmail || null });
        } else {
          alert(heldCancellationAlert(response.data));
        }
        return;
      }

      if (response.data.requiresPayment) {
        const amount = response.data.amount ?? 0;
        const claimTotal = response.data.claimCount ?? (cancelAllAssociated && associatedData.hasAssociatedClaims
          ? associatedData.associatedClaimsCount + 1
          : 1);
        const expiresDisplay = response.data.paymentExpiresAt
          ? new Date(response.data.paymentExpiresAt).toLocaleString('en-GB', {
              year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit'
            })
          : '7 days';
        const recipient = claimEmail || 'the client';
        alert(
          `Payment request emailed to ${recipient}.` +
          `\nAmount due: £${amount.toFixed(2)} for ${claimTotal} claim${claimTotal === 1 ? '' : 's'}.` +
          `\nPayment link expires ${expiresDisplay}.`
        );

        const nowIso = new Date().toISOString();
        updateSelectedClaim({
          status: 'CANCELLED',
          cancellationDate: nowIso,
          cancellationPaymentStatus: 'requested',
          cancellationPaymentAmountTotal: amount,
          cancellationPaymentRequestedAt: nowIso,
          cancellationPaymentId: response.data.paymentId,
          cancellationPaymentLinkUrl: response.data.paymentUrl,
          cancellationPaymentExpiresAt: response.data.paymentExpiresAt,
          cancellationReinstateToken: response.data.reinstateToken || undefined,
          cancellationSource: 'admin-ui',
          cancellationReason: reason,
        });
        await refreshReinstatementBlock();
        await loadClaims({ silent: true });
      } else {
        const successMessage = cancelAllAssociated && associatedData.hasAssociatedClaims
          ? `Successfully cancelled ${associatedData.associatedClaimsCount + 1} claims for this client. Confirmation emails will be sent.`
          : `Claim ${claimId} cancelled successfully. A confirmation email will be sent to ${claimEmail}.`;

        alert(successMessage);
        const nowIso = new Date().toISOString();
        updateSelectedClaim({
          status: 'CANCELLED',
          cancellationDate: nowIso,
          cancellationPaymentStatus: 'not_required',
          cancellationPaymentAmountTotal: undefined,
          cancellationPaymentRequestedAt: undefined,
          cancellationPaymentId: undefined,
          cancellationPaymentLinkUrl: undefined,
          cancellationPaymentExpiresAt: undefined,
          cancellationSource: 'admin-ui',
          cancellationReason: reason,
        });
        await refreshReinstatementBlock();
        await loadClaims({ silent: true });
      }

      setPendingCancellation(null);
      setShowBypassModal(false);
    } catch (err: any) {
      const status = err?.response?.status;
      const data = err?.response?.data;

      if (status === 409 && data?.paymentDecisionRequired) {
        // For General Closure (Charge), skip the bypass/require modal and always require payment
        if (reason === REASON_GENERAL_CLOSURE_CHARGE) {
          await executeCancellation({
            claimId,
            claimEmail,
            cancelAllAssociated,
            associatedData,
            reason,
            nonCooperationReasonCodes,
            decision: 'request',
            requestReceivedDate,
          });
          return;
        }

        const preview: PaymentPreview = {
          chargeableClaimIds: data.chargeableClaimIds ?? [],
          chargeableClaimCount: data.chargeableClaimCount ?? (data.chargeableClaimIds?.length ?? 0),
          estimatedAmount: typeof data.estimatedAmount === 'number' ? data.estimatedAmount : undefined,
          currency: data.currency || 'GBP',
        };

        setPendingCancellation({
          claimId,
          claimEmail,
          cancelAllAssociated,
          associatedData,
          reason,
          nonCooperationReasonCodes,
          requiresPaymentDecision: true,
          paymentPreview: preview,
          requestReceivedDate,
        });
        setShowReasonModal(false);
        setShowBypassModal(true);
        return;
      }

      const errorMessage = data?.message || data?.error || err?.message || 'An unknown error occurred while cancelling.';
      setActionError(errorMessage);
      alert(`Error cancelling claim: ${errorMessage}`);
      console.error('Error cancelling claim:', err);
    } finally {
      setActionLoading(prev => {
        const next = new Set(prev);
        next.delete(claimId);
        return next;
      });
    }
  };

  const handleBypassDecision = async (bypassPayment: boolean) => {
    if (!pendingCancellation) return;
    const payload = pendingCancellation;
    setShowBypassModal(false);
    setReasonDraft('');
    const { requiresPaymentDecision: _ignored, ...cancellationPayload } = payload;
    await executeCancellation({
      ...cancellationPayload,
      decision: bypassPayment ? 'bypass' : 'request',
    });
    setPendingCancellation(null);
  };

  const handleBypassModalClose = () => {
    if (pendingCancellation) {
      setActionLoading(prev => {
        const next = new Set(prev);
        next.delete(pendingCancellation.claimId);
        return next;
      });
    }
    setPendingCancellation(null);
    setShowBypassModal(false);
  };

  const handleReasonCancel = () => {
    if (pendingCancellation) {
      setActionLoading(prev => {
        const next = new Set(prev);
        next.delete(pendingCancellation.claimId);
        return next;
      });
    }
    setPendingCancellation(null);
    setShowReasonModal(false);
    setReasonDraft('');
    setNoChargeWaiverReason('');
    setSelectedCancellationReason('Client request');
    setClientRequestSubReason('');
    setSelectedNonCooperationReasons([]);
    // Hygiene: ensure the review modal is closed and any in-flight preview
    // response is discarded if the admin abandons the cancel flow.
    setShowReviewModal(false);
    setReviewBreakdown(null);
    previewRequestId.current++;
  };

  const handleReviewBack = () => {
    setShowReviewModal(false);
    setReviewBreakdown(null);
    // If a preview request is in flight when the admin clicks Back, invalidate
    // it so its response can't reopen the review modal after we close it.
    previewRequestId.current++;
    // Re-seed the date field from what was actually submitted (handleReasonSubmit
    // clears the draft on submit, so the draft itself is empty by now) -- otherwise
    // a second Continue would send undefined and judge the client at today.
    setRequestReceivedDateDraft(pendingCancellation?.requestReceivedDate || '');
    // Same for the reason: submit reset it to the default, so without this a
    // second Continue would send "Client request" whatever was picked.
    const submittedReason = pendingCancellation?.reason || '';
    const submittedSubReason = splitClientRequestReason(submittedReason);
    if (submittedSubReason) {
      setSelectedCancellationReason(REASON_CLIENT_REQUEST);
      setClientRequestSubReason(submittedSubReason);
    } else if ((CANCELLATION_REASONS as readonly string[]).includes(submittedReason)) {
      setSelectedCancellationReason(submittedReason);
      setSelectedNonCooperationReasons(pendingCancellation?.nonCooperationReasonCodes || []);
    } else if (submittedReason) {
      setSelectedCancellationReason('Other');
      setReasonDraft(submittedReason);
    }
    setShowReasonModal(true);
  };

  const handleReviewContinue = () => {
    if (!reviewBreakdown) return;
    const breakdown = reviewBreakdown;
    setShowReviewModal(false);

    if (breakdown.chargeableCount > 0) {
      // Hand the preview to the existing Payment Handling modal by stuffing
      // it into pendingCancellation.paymentPreview (same shape the modal
      // already reads in the 409-fallback path).
      if (pendingCancellation) {
        setPendingCancellation({
          ...pendingCancellation,
          requiresPaymentDecision: true,
          paymentPreview: {
            chargeableClaimCount: breakdown.chargeableCount,
            chargeableClaimIds: breakdown.chargeableClaimIds,
            estimatedAmount: breakdown.estimatedAmount,
            currency: breakdown.currency,
            breakdown: breakdown.breakdown,
          },
        });
      }
      setReviewBreakdown(null);
      setShowBypassModal(true);
    } else {
      // Nothing chargeable — skip the Payment Handling modal and call the
      // same bypass-payment path the admin would have triggered manually.
      setReviewBreakdown(null);
      void handleBypassDecision(true);
    }
  };

  const fetchCancelPreview = async (
    claimId: string,
    cancelAllAssociated: boolean,
    requestReceivedDate?: string,
  ): Promise<{
    breakdown: ClaimBreakdownEntry[];
    chargeableCount: number;
    estimatedAmount: number;
    currency: string;
    chargeableClaimIds: string[];
  }> => {
    const res = await client.post<{
      claimBreakdown?: ClaimBreakdownEntry[];
      chargeableCount?: number;
      estimatedAmount?: number;
      currency?: string;
      chargeableClaimIds?: string[];
    }>(
      `/ui/claims/${encodeURIComponent(claimId)}/cancel/preview`,
      requestReceivedDate
        ? { cancelAllAssociated, requestReceivedDate }
        : { cancelAllAssociated },
    );
    const body = res.data ?? {};
    return {
      breakdown: body.claimBreakdown ?? [],
      chargeableCount: body.chargeableCount ?? 0,
      estimatedAmount: body.estimatedAmount ?? 0,
      currency: body.currency ?? 'GBP',
      chargeableClaimIds: body.chargeableClaimIds ?? [],
    };
  };

  const handleReasonSubmit = async () => {
    if (!pendingCancellation) return;

    // Determine final reason based on dropdown selection
    let finalReason: string;
    if (selectedCancellationReason === 'Other') {
      finalReason = reasonDraft.trim() ? reasonDraft.trim() : 'Admin cancelled via UI';
    } else if (selectedCancellationReason === REASON_CLIENT_REQUEST) {
      if (!clientRequestSubReason) {
        alert('Select a Client request sub-category.');
        return;
      }
      finalReason = buildClientRequestReason(clientRequestSubReason);
    } else {
      finalReason = selectedCancellationReason;
    }

    if (finalReason === REASON_NON_COOPERATIVE && selectedNonCooperationReasons.length === 0) {
      alert('Select at least one outstanding client requirement.');
      return;
    }

    const updated = {
      ...pendingCancellation,
      reason: finalReason,
      nonCooperationReasonCodes: finalReason === REASON_NON_COOPERATIVE
        ? selectedNonCooperationReasons
        : undefined,
      requestReceivedDate: requestReceivedDateDraft.trim() || undefined,
    };

    setShowReasonModal(false);
    const { requiresPaymentDecision: _ignored, ...cancellationPayload } = updated;

    // Keep pendingCancellation in case we get a 409 and need to show payment modal
    setPendingCancellation(updated);
    setReasonDraft('');
    setRequestReceivedDateDraft('');
    setNoChargeWaiverReason('');
    setSelectedCancellationReason('Client request');
    setClientRequestSubReason('');
    setSelectedNonCooperationReasons([]);

    // Special handling for reasons that always bypass payment — skip the
    // charge-review step entirely because no payment can be requested anyway.
    if (finalReason === REASON_LEASE_AGREEMENT || finalReason === REASON_GENERAL_CLOSURE || finalReason === REASON_INVALID_LENDER) {
      await executeCancellation({
        ...cancellationPayload,
        decision: 'bypass',  // Always bypass for lease agreements and general closure
      });
      return;
    }

    // Regular flow: fetch the side-effect-free preview and show the
    // Review Charges modal before issuing the cancel request. If the
    // preview call fails, fall back to the legacy path — the backend's
    // 409 paymentDecisionRequired still acts as a safety net.
    // Capture a monotonic request id so a Back→Continue race can't let an
    // older response stomp on a newer one (or reopen the modal after Back).
    const myRequestId = ++previewRequestId.current;
    try {
      const preview = await fetchCancelPreview(
        updated.claimId,
        updated.cancelAllAssociated,
        updated.requestReceivedDate,
      );
      if (myRequestId !== previewRequestId.current) {
        // A newer submit (or a Back/Cancel) has superseded this one — drop.
        return;
      }
      setReviewBreakdown(preview);
      setShowReviewModal(true);
    } catch (err) {
      if (myRequestId !== previewRequestId.current) {
        // The user abandoned this flow before the error arrived — stay quiet.
        return;
      }
      console.error('Failed to fetch cancel preview, falling back to legacy flow:', err);
      // Surface a non-blocking warning so the admin knows the charge-review
      // step was skipped and the backend's 409 safety-net path is in play.
      setActionError('Could not load charge breakdown — continuing with standard flow.');
      setTimeout(() => setActionError(''), 5000);
      await executeCancellation({
        ...cancellationPayload,
        decision: null,
      });
    }
  };

  const handleCancelClaim = async (claimId: string, claimEmail: string) => {
    setActionError(null);
    setActionLoading(prev => new Set(prev).add(claimId));

    let associatedData: AssociatedClaimsResponse;
    try {
      associatedData = await checkAssociatedClaims(claimId);
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'Failed to check associated claims.';
      setActionError(message);
      alert(message);
      setActionLoading(prev => {
        const next = new Set(prev);
        next.delete(claimId);
        return next;
      });
      return;
    }

    if (associatedData.hasAssociatedClaims) {
      // Show the scope choice modal instead of window.confirm
      setCancelScopeData({ claimId, claimEmail, associatedData });
      setShowCancelScopeModal(true);
    } else {
      // Single claim — go straight to reason modal
      setPendingCancellation({
        claimId,
        claimEmail,
        cancelAllAssociated: false,
        associatedData,
        reason: '',
        requiresPaymentDecision: false,
      });
      setReasonDraft('');
      setSelectedNonCooperationReasons([]);
      setRequestReceivedDateDraft(prefilledRequestReceivedDate);
      setShowReasonModal(true);
    }
  };

  const handleCancelScopeChoice = (cancelAll: boolean) => {
    if (!cancelScopeData) return;
    setShowCancelScopeModal(false);
    setPendingCancellation({
      claimId: cancelScopeData.claimId,
      claimEmail: cancelScopeData.claimEmail,
      cancelAllAssociated: cancelAll,
      associatedData: cancelScopeData.associatedData,
      reason: '',
      requiresPaymentDecision: false,
    });
    setCancelScopeData(null);
    setReasonDraft('');
    setSelectedNonCooperationReasons([]);
    setRequestReceivedDateDraft(prefilledRequestReceivedDate);
    // Default to No Charge when cancelling all claims
    setSelectedCancellationReason(cancelAll ? REASON_GENERAL_CLOSURE : 'Client request');
    setClientRequestSubReason('');
    setShowReasonModal(true);
  };

  const handleCancelScopeClose = () => {
    setShowCancelScopeModal(false);
    if (cancelScopeData) {
      setActionLoading(prev => {
        const next = new Set(prev);
        next.delete(cancelScopeData.claimId);
        return next;
      });
    }
    setCancelScopeData(null);
  };

  const handleReinstateClaim = async (
    claimId: string,
    force = false,
    remainingPaymentAction?: RemainingPaymentAction,
  ) => {
    setActionLoading(prev => new Set(prev).add(claimId));
    setActionError(null);
    if (remainingPaymentAction) setReinstatementPaymentSubmitting(true);

    try {
      if (!remainingPaymentAction) {
        const impact = await fetchReinstatementPaymentImpact(claimId);
        if (impact.blocked) {
          setActionError(impact.blockReason || 'Finance must review this payment before reinstatement.');
          return;
        }
        if (impact.requiresDecision) {
          setReinstatementPaymentPrompt({ impact, force });
          return;
        }
        if (!force && !window.confirm(
          `Are you sure you want to reinstate claim ${claimId}? This will clear the LOC sent date so it can be automatically resent.`
        )) {
          return;
        }
      }

      setReinstatementPaymentPrompt(null);
      const updatedClaimData = await reinstateClaim(
        claimId,
        force,
        remainingPaymentAction,
      );

      // Update the local state
      setClaims(prevClaims =>
        prevClaims.map(claim =>
          claim.id === claimId ? { ...claim, ...updatedClaimData } : claim
        )
      );

      const paymentResult = (updatedClaimData as Claim & {
        reinstatementPaymentResult?: ReinstatementPaymentResult;
      }).reinstatementPaymentResult;
      const paymentMessage = reinstatementPaymentMessage(paymentResult);
      if (paymentResult?.replacementIsManual && paymentResult.replacementPaymentUrl) {
        navigator.clipboard?.writeText(paymentResult.replacementPaymentUrl).catch(() => undefined);
      }
      setUpdateMessage(
        `Claim ${claimId} has been successfully reinstated and LOC will be resent automatically.${paymentMessage}`
      );
      setTimeout(() => setUpdateMessage(''), 5000);

    } catch (error: any) {
      // The lender for this claim is non-viable (closed). Both admins and normal
      // users get the in-app modal; admins can override ("Reinstate anyway"),
      // non-admins see a read-only "ask an admin" message with only a Close
      // button (no override action). The backend enforces admin-only override
      // regardless, so hiding the button is UX, not the security boundary.
      if (error.response?.status === 409 && error.response?.data?.lenderDisabled && !force) {
        const lenderName = error.response.data.lenderName || 'this lender';
        setLenderBlockModal({ claimId, lenderName });
        return;
      }
      console.error(`Error reinstating claim ${claimId}:`, error);
      setActionError(`Failed to reinstate claim ${claimId}: ${error.response?.data?.error || error.message}`);
    } finally {
      setReinstatementPaymentSubmitting(false);
      setActionLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(claimId);
        return newSet;
      });
    }
  };

  // Opens the scope picker. Claims with no clientId have no other siblings to act on,
  // so the modal is unnecessary — fall back to a direct single-claim toggle.
  const handleToggleReinstatement = async (claim: Claim) => {
    if (!claim.clientId) {
      const currentlyDisabled = claim.reinstatementDisabled;
      const action = currentlyDisabled ? 'enable' : 'disable';
      if (!window.confirm(`Are you sure you want to ${action} reinstatement for this claim?`)) return;
      setActionLoading(prev => new Set(prev).add(claim.id));
      try {
        const result = await toggleReinstatement(claim.id, !currentlyDisabled, { scope: 'single' });
        applyReinstatementResult(result.updatedClaimIds, !currentlyDisabled);
        setUpdateMessage(result.message || `Reinstatement ${action}d successfully`);
        setTimeout(() => setUpdateMessage(''), 5000);
      } catch (error: any) {
        setActionError(error.response?.data?.error || `Failed to ${action} reinstatement`);
      } finally {
        setActionLoading(prev => { const n = new Set(prev); n.delete(claim.id); return n; });
      }
      return;
    }
    setReinstatementScopeClaim(claim);
    setDrawerHiddenForModal(true);
  };

  // Applies a toggle-reinstatement response to local state.
  const applyReinstatementResult = (updatedIds: string[], disabled: boolean) => {
    const idSet = new Set(updatedIds);
    setClaims(prevClaims =>
      prevClaims.map(c => idSet.has(c.id) ? { ...c, reinstatementDisabled: disabled } : c)
    );
    setSelectedClaim(prev => prev && idSet.has(prev.id) ? { ...prev, reinstatementDisabled: disabled } : prev);
  };

  // Filter client‐side
  const displayed = filterWithoutLOC
    ? claims.filter(c => !c.locSentDate)
    : claims;

  // Calculate total pages
  const totalPages = totalCount ? Math.ceil(totalCount / pageSize) : (hasMore ? page + 1 : page);

  // Add a custom styles object for the table with proper TypeScript types
  const tableStyles = {
    container: {
      margin: '20px 0',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      overflow: 'hidden'
    },
    actionsBar: {
      display: 'flex',
      flexWrap: 'wrap' as const, // Type assertion
      gap: '10px',
      padding: '16px',
      backgroundColor: '#f7f9fa',
      borderBottom: '1px solid #eaeef2',
      alignItems: 'center'
    },
    searchContainer: {
      display: 'flex',
      gap: '8px',
      alignItems: 'center',
      width: '100%'
    },
    searchInput: {
      padding: '8px 12px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '14px',
      flex: '1 1 auto'
    },
    button: {
      padding: '8px 12px',
      backgroundColor: '#01B8C0',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 500,
      transition: 'background-color 0.2s',
      flexShrink: 0,
      flex: '0 0 120px',
      textAlign: 'center' as const
    },
    secondaryButton: {
      padding: '8px 12px',
      backgroundColor: '#f0f0f0',
      color: '#333',
      border: '1px solid #ddd',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      transition: 'all 0.2s ease'
    },
    clearButton: {
      padding: '8px 12px',
      backgroundColor: '#ff6b6b',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      transition: 'all 0.2s ease',
      fontWeight: 500
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse' as const // Type assertion
    },
    th: {
      textAlign: 'left' as const, // Type assertion
      padding: '12px 16px',
      borderBottom: '2px solid #eaeef2',
      color: '#004754',
      fontWeight: 600,
      fontSize: '14px'
    },
    td: {
      padding: '12px 16px',
      borderBottom: '1px solid #eaeef2',
      color: '#333',
      fontSize: '14px'
    },
    link: {
      color: '#01B8C0',
      textDecoration: 'none'
    },
    detailsBtn: {
      padding: '6px 12px',
      backgroundColor: '#f0f0f0',
      border: '1px solid #ddd',
      borderRadius: '4px',
      cursor: 'pointer',
      marginRight: '8px'
    },
    sendLocBtn: {
      padding: '6px 12px',
      backgroundColor: '#01B8C0',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer'
    },
    pagination: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '8px',
      padding: '16px',
      borderTop: '1px solid #eaeef2',
      flexWrap: 'wrap' as const
    },
    pageButton: {
      padding: '8px 12px',
      backgroundColor: '#f0f0f0',
      color: '#333',
      border: '1px solid #ddd',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      minWidth: '40px',
      textAlign: 'center' as const,
      transition: 'all 0.2s ease'
    },
    pageButtonActive: {
      backgroundColor: '#01B8C0',
      color: 'white',
      borderColor: '#01B8C0'
    },
    pageButtonDisabled: {
      backgroundColor: '#e9ecef',
      color: '#6c757d',
      cursor: 'not-allowed',
      borderColor: '#e9ecef'
    },
    pageInput: {
      padding: '6px 8px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '14px',
      width: '60px',
      textAlign: 'center' as const
    },
    pageJumpContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      margin: '0 16px'
    },
    tr: {
      borderBottom: '1px solid #eee',
      // Add any other row styles you want
    }
  };

  const handleCloseDetails = () => {
    handleSelectClaim(null);
  };

  // Payment modal handlers

  const handleEditClick = (claimToEdit: Claim) => {
    setSelectedClaim(claimToEdit);
    setIsEditMode(true);
    setEditFormData({
      title: claimToEdit.title || '',
      firstName: claimToEdit.firstName || '',
      lastName: claimToEdit.lastName || '',
      email: claimToEdit.email || '',
      phoneNumber: claimToEdit.phoneNumber || '',
      addressLine1: claimToEdit.addressLine1 || '',
      addressLine2: claimToEdit.addressLine2 || '',
      town: claimToEdit.town || '',
      county: claimToEdit.county || '',
      postcode: claimToEdit.postcode || '',
      dateOfBirth: claimToEdit.dateOfBirth || '',
    });
    setEditVehicleRegistration(claimToEdit.vehicleRegistration || '');
    setUpdateMessage('');
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setUpdateMessage('');
  };

  const handleSaveEdits = async () => {
    if (!selectedClaim || !isEditMode) return;

    // Vehicle reg (#405) is per-claim and NOT a credit-agreement fact, so it's
    // still saved via the shared (unrestricted) agreement-fields endpoint here.
    // craAgreementNumberOriginal/craCreditStartDate moved to the role-gated
    // Credit Agreement panel's own save path (handleSaveCreditAgreement) and
    // are no longer sent from this form.
    //
    // Validate BEFORE any network call fires. If this validation ran after
    // updateClientInfo() below, an invalid change would abort only the
    // agreement-fields save while the personal/address edits had already
    // been committed - leaving the user with silently-persisted changes and
    // edit mode still open. Validating up front means no requests fire at
    // all on invalid input, and the user sees the error immediately.
    const newVehicleReg = editVehicleRegistration.trim();
    const agreementFieldsPayload: {
      vehicleRegistration?: string;
    } = {};
    if (newVehicleReg !== (selectedClaim.vehicleRegistration || '')) {
      agreementFieldsPayload.vehicleRegistration = newVehicleReg;
    }
    const agreementChanged = Object.keys(agreementFieldsPayload).length > 0;

    setActionLoading(prev => new Set(prev).add(selectedClaim.id));
    setUpdateMessage('Saving...');
    try {
      // First save to just this claim
      const response = await updateClientInfo(selectedClaim.id, editFormData);

      // Check if client has other claims and prompt for bulk update
      let totalUpdated = response.totalUpdated || 1;
      const otherClaimCount = (response.clientClaimCount || 1) - 1;

      if (otherClaimCount > 0) {
        const applyToAll = window.confirm(
          `This client has ${otherClaimCount} other claim${otherClaimCount > 1 ? 's' : ''}. ` +
          `Apply these changes to all linked claims?`
        );

        if (applyToAll) {
          const bulkResponse = await updateClientInfo(selectedClaim.id, {
            ...editFormData,
            applyToAllClaims: true,
          });
          totalUpdated = bulkResponse.totalUpdated || 1;

          // Refresh claims list to reflect changes across all claims
          setClaims(prevClaims =>
            prevClaims.map(c => {
              if (c.clientId === selectedClaim.clientId) {
                const updates: Record<string, any> = {};
                const personalFields = ['title', 'firstName', 'lastName', 'email', 'phoneNumber', 'addressLine1', 'addressLine2', 'town', 'county', 'postcode', 'dateOfBirth'];
                for (const field of personalFields) {
                  if (editFormData[field as keyof typeof editFormData] !== undefined) {
                    updates[field] = editFormData[field as keyof typeof editFormData];
                  }
                }
                return { ...c, ...updates };
              }
              return c;
            })
          );
        }
      }

      let finalClaim: Partial<Claim> | undefined = response.claim;
      if (agreementChanged) {
        const agrResponse = await updateAgreementFields(selectedClaim.id, agreementFieldsPayload);
        finalClaim = agrResponse.claim;
      }

      // Update the selected claim in local state
      setClaims(prevClaims =>
        prevClaims.map(c => c.id === selectedClaim.id ? { ...c, ...finalClaim } : c)
      );
      setSelectedClaim(prev => prev ? { ...prev, ...finalClaim } : null);

      const msg = totalUpdated > 1
        ? `Client info updated across ${totalUpdated} claims`
        : (agreementChanged ? 'Claim details updated successfully' : (response.message || 'Client info updated successfully'));
      setUpdateMessage(msg);
      setIsEditMode(false);
    } catch (error: any) {
      console.error("Error updating client info:", error);
      setUpdateMessage(error.response?.data?.error || 'Error updating client info');
    } finally {
      setActionLoading(prev => {
        const next = new Set(prev);
        if (selectedClaim) next.delete(selectedClaim.id);
        return next;
      });
    }
  };
  
  // ---- Credit Agreement editing (role-gated) ----
  // Base commissionType choices. The extraction schema (caf_extraction_schema.py)
  // also writes 'Unknown' and the craClassificationStatus vocabulary
  // ('Investigating Fixed Commission' / 'Investigating DCA Commission' /
  // 'No Investigation as No Commission' / 'Fixed Commission') straight into
  // this field -- rather than keep this list in lockstep with that enum, the
  // select below prepends whatever value is already on the claim.
  const COMMISSION_TYPE_OPTIONS = ['N/A', 'None', 'DCA', 'Non-DCA'];

  // Fields the credit-agreement-editor role may change, in display order.
  const CA_EDIT_FIELDS: Array<{ key: keyof Claim; label: string; type?: 'text' | 'number' | 'commissionType' | 'craClassification' | 'date' | 'datetime' }> = [
    { key: 'craFinanceLender', label: 'Finance Lender' },
    { key: 'craAgreementNumberOriginal', label: 'Agreement Number' },
    { key: 'agreement_dateAgreed', label: 'Agreement Date Signed', type: 'datetime' },
    { key: 'defendantReference', label: 'Lender/Defendant Reference' },
    { key: 'commissionAmount', label: 'Commission Amount', type: 'number' },
    { key: 'commissionType', label: 'Commission Type', type: 'commissionType' },
    { key: 'craCompanyType', label: 'Company Type' },
    { key: 'craCaseType', label: 'Case Type' },
    { key: 'craClassificationStatus', label: 'Classification Status', type: 'craClassification' },
    { key: 'craClassificationReason', label: 'Classification Reason' },
    { key: 'craCreditStartDate', label: 'Agreement Start Date', type: 'date' },
    { key: 'craCreditEndDate', label: 'Agreement End Date', type: 'date' },
  ];

  // Our claim dates are stored UK-style (DD/MM/YYYY, agreement_dateAgreed may add HH:mm).
  // HTML date/datetime-local inputs use ISO, so convert both ways.
  const ukToIso = (s: string): string => {
    const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2}))?/.exec((s || '').trim());
    if (!m) return '';
    const [, d, mo, y, h, mi] = m;
    const base = `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
    return h !== undefined ? `${base}T${h.padStart(2, '0')}:${mi}` : base;
  };
  const isoToUk = (s: string, withTime: boolean): string => {
    if (!s) return '';
    const [datePart, timePart] = s.split('T');
    const [y, mo, d] = datePart.split('-');
    if (!y || !mo || !d) return s;
    const dmy = `${d}/${mo}/${y}`;
    return (withTime && timePart) ? `${dmy} ${timePart}` : dmy;
  };

  const startCaEdit = () => {
    if (!selectedClaim) return;
    const seed: Record<string, string> = {};
    CA_EDIT_FIELDS.forEach(f => {
      const v = (selectedClaim as any)[f.key];
      const str = v === undefined || v === null ? '' : String(v);
      // date/datetime pickers need ISO; store the picker-format value in state
      seed[f.key as string] = (f.type === 'date' || f.type === 'datetime') ? ukToIso(str) : str;
    });
    setCaEdits(seed);
    setCaEditMode(true);
    // The panel edit takes over the Agreement Number row; drop any inline
    // draft so it can't reappear, stale, once the panel edit is cancelled.
    setAnEditing(false);
    setAnValue('');
    setAnError(null);
    setAnWarning(null);
  };

  const cancelCaEdit = () => {
    setCaEditMode(false);
    setCaEdits({});
  };

  // ---- Agreement number inline edit (any staff member) ----
  const startAgreementNumberEdit = () => {
    if (!selectedClaim) return;
    setAnValue(selectedClaim.craAgreementNumberOriginal || '');
    setAnError(null);
    setAnWarning(null);
    setAnEditing(true);
  };

  const cancelAgreementNumberEdit = () => {
    setAnEditing(false);
    setAnError(null);
  };

  const handleSaveAgreementNumber = async () => {
    if (!selectedClaim) return;
    const claimId = selectedClaim.id;
    setAnSaving(true);
    setAnError(null);
    // If the drawer has moved to another claim by the time the save
    // finishes, leave that claim's edit box, messages and banner alone.
    const stillOnThisClaim = () => drawerClaimIdRef.current === claimId;
    try {
      const res = await updateAgreementNumber(claimId, anValue);
      const finalClaim = res.claim;
      setClaims(prev => prev.map(c => c.id === claimId ? { ...c, ...finalClaim } : c));
      setSelectedClaim(prev => prev && prev.id === claimId ? { ...prev, ...finalClaim } : prev);
      if (!stillOnThisClaim()) return;
      setAnWarning(res.warning || null);
      setAnEditing(false);
      setUpdateMessage(res.changed ? 'Agreement number updated' : 'No change made');
    } catch (error: any) {
      console.error('Error updating agreement number:', error);
      if (!stillOnThisClaim()) return;
      setAnError(error.response?.data?.error || "The agreement number couldn't be saved. Please try again.");
    } finally {
      // Moving claims already reset the saving flag for the new claim.
      if (stillOnThisClaim()) setAnSaving(false);
    }
  };

  const handleSaveCreditAgreement = async () => {
    if (!selectedClaim) return;
    // Only send fields that actually changed vs the claim.
    const changed: Record<string, string> = {};
    CA_EDIT_FIELDS.forEach(f => {
      const orig = (selectedClaim as any)[f.key];
      const origStr = (orig === undefined || orig === null ? '' : String(orig)).trim();
      // date/datetime state is ISO; convert back to our stored UK format before diffing/sending
      let next = (caEdits[f.key as string] ?? '').trim();
      if (f.type === 'date') next = isoToUk(next, false);
      else if (f.type === 'datetime') next = isoToUk(next, true);
      if (next !== origStr) changed[f.key as string] = next;
    });
    if (Object.keys(changed).length === 0) {
      setUpdateMessage('No credit-agreement changes to save');
      setCaEditMode(false);
      return;
    }
    // Clearing the agreement date needs explicit confirmation - it's read
    // elsewhere in this system to decide whether a claim can be charged, so it
    // must never be blanked as a side effect of a routine correction.
    const payload: Record<string, string | boolean> = { ...changed };
    if (Object.prototype.hasOwnProperty.call(changed, 'agreement_dateAgreed') && changed.agreement_dateAgreed === '') {
      const confirmed = window.confirm(
        'Clearing the agreement date can affect whether this claim is charged. Are you sure you want to continue?'
      );
      if (!confirmed) {
        setUpdateMessage('Save cancelled');
        return;
      }
      payload.confirmBlankDate = true;
    }
    setCaSaving(true);
    try {
      const res = await updateCreditAgreement(selectedClaim.id, payload);
      const finalClaim = res.claim;
      setClaims(prev => prev.map(c => c.id === selectedClaim.id ? { ...c, ...finalClaim } : c));
      setSelectedClaim(prev => prev ? { ...prev, ...finalClaim } : null);
      setUpdateMessage(`Credit agreement updated (${res.changes?.length ?? Object.keys(changed).length} field(s))`);
      setCaEditMode(false);
      setCaEdits({});
    } catch (error: any) {
      console.error('Error updating credit agreement:', error);
      setUpdateMessage(error.response?.data?.error || 'Error updating credit agreement');
    } finally {
      setCaSaving(false);
    }
  };

  const handleComposeEmail = (claim: Claim) => {
    setEmailComposerClaim(claim);
    setShowEmailComposer(true);
  };

  const handleEmailSent = (result: any) => {
    setShowEmailComposer(false);
    setUpdateMessage('Email sent successfully');
    setTimeout(() => setUpdateMessage(''), 5000);
    // Optionally refresh the claim to show new correspondence
    if (emailComposerClaim) {
      loadClaims();
    }
  };

  const handleComposeSms = (claim: Claim) => {
    setSmsComposerClaim(claim);
    setShowSmsComposer(true);
  };

  const handleSmsSent = (result: any) => {
    setShowSmsComposer(false);
    setUpdateMessage('SMS sent successfully');
    setTimeout(() => setUpdateMessage(''), 5000);
    // Refresh to show new correspondence
    if (smsComposerClaim) {
      loadClaims();
    }
  };

  const handleOpenLetterModal = (claim: Claim) => {
    setSelectedClaimForLetter(claim);
    setShowLetterModal(true);
    setActionsMenuOpen(false);
    setDrawerHiddenForModal(true);
  };

  const handleSendLetter = async (letterData: LetterData, generateOnly: boolean) => {
    if (!selectedClaimForLetter) return;

    try {
      let result: any;
      if (generateOnly) {
        result = await generateLetter(selectedClaimForLetter.id, letterData);
      } else {
        result = await sendLetter(selectedClaimForLetter.id, letterData);
      }

      // Show success message with document link if available
      if (generateOnly && result.data?.documentUrl) {
        // Open document in new tab automatically
        window.open(result.data.documentUrl, '_blank');
        setUpdateMessage('Letter generated successfully - document opened in new tab');
      } else {
        setUpdateMessage(generateOnly ? 'Letter generated successfully' : 'Letter sent successfully');
      }
      setTimeout(() => setUpdateMessage(''), 8000);

      // Update the selected claim if it's open in the drawer
      if (selectedClaim && selectedClaim.id === selectedClaimForLetter.id) {
        // Add the new correspondence to the selected claim
        if (result.data?.correspondence) {
          setSelectedClaim(prev => {
            if (!prev) return prev;
            const updatedCorrespondence = [...(prev.correspondence || []), result.data.correspondence];
            return {
              ...prev,
              correspondence: updatedCorrespondence,
              unitCount: result.data.unitCount !== undefined ? result.data.unitCount : prev.unitCount
            };
          });
        }
      }

      // Reload claims to update the main table
      await loadClaims();
      setShowLetterModal(false);
      setSelectedClaimForLetter(null);
      setDrawerHiddenForModal(false);
    } catch (err: any) {
      console.error(`Error ${generateOnly ? 'generating' : 'sending'} letter:`, err);
      throw new Error(err.response?.data?.error || `Failed to ${generateOnly ? 'generate' : 'send'} letter`);
    }
  };

  const handleInputChange = (field: keyof typeof editFormData, value: string) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveEnforcementFlag = async () => {
    if (!selectedClaim) return;
    setEnforcementSaving(true);
    try {
      const result = await setEnforcementFlag(selectedClaim.id, enforcementForm);
      setSelectedClaim(result.claim);
      setShowEnforcementModal(false);
    } catch (err) {
      console.error('Failed to set enforcement flag', err);
      alert('Failed to update enforcement flag');
    } finally {
      setEnforcementSaving(false);
    }
  };

  // Check if a resend button should be disabled (sent within last 24 hours)
  // TEMPORARILY DISABLED FOR TESTING - TODO: Re-enable after testing
  const isResendDisabled = (documentType: 'foa' | 'dba'): { disabled: boolean; reason: string } => {
    // eslint-disable-next-line no-console, @typescript-eslint/no-unused-vars
    console.log('isResendDisabled called - TESTING MODE, returning false', documentType);
    return { disabled: false, reason: '' }; // TESTING: Cooldown disabled
  };

  // Handle clicking the resend button
  const handleResendDocumentClick = (documentType: 'foa' | 'dba') => {
    if (!selectedClaim) return;

    // Check if client has email
    if (!selectedClaim.email) {
      setUpdateMessage('Error: No email can be sent to the client unless an email is present on the claim');
      setTimeout(() => setUpdateMessage(''), 5000);
      return;
    }

    // Check if original document was signed
    const originalUrlField = documentType === 'foa' ? 'formOfAuthorityUrl' : 'damageBasedAgreementUrl';
    const originalUrl = (selectedClaim as any)[originalUrlField];

    if (originalUrl) {
      // Original is signed - show confirmation modal
      // Try to find when it was signed from various sources
      let signedDate = null;
      if (selectedClaim.createdInApiAt) {
        signedDate = selectedClaim.createdInApiAt;
      }

      setResendConfirmModal({
        open: true,
        documentType,
        originalSignedDate: signedDate,
        originalUrl
      });
    } else {
      // No original signed - proceed directly
      handleResendDocument(documentType);
    }

    setActionsMenuOpen(false);
  };

  // Actually send the resend request
  const handleResendDocument = async (documentType: 'foa' | 'dba') => {
    if (!selectedClaim) return;

    const setLoading = documentType === 'foa' ? setResendingFoa : setResendingDba;
    setLoading(true);
    setResendConfirmModal({ open: false, documentType: null, originalSignedDate: null, originalUrl: null });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/ui/claims/${selectedClaim.id}/resend-document-to-client`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ document_type: documentType })
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.already_sent) {
          setUpdateMessage(`Error: ${data.error}`);
        } else {
          throw new Error(data.error || 'Failed to resend document');
        }
      } else {
        const docName = documentType === 'foa' ? 'FOA (Form of Authority)' : 'DBA (Finance Retention)';
        setUpdateMessage(`${docName} sent successfully to ${selectedClaim.email}`);

        // Reload claims to update the sent timestamp
        await loadClaims();
      }

      setTimeout(() => setUpdateMessage(''), 8000);
    } catch (err: any) {
      setUpdateMessage(`Error: ${err.message || 'Failed to resend document'}`);
      setTimeout(() => setUpdateMessage(''), 8000);
    } finally {
      setLoading(false);
    }
  };

  // Sync client resend signed documents from DocuSeal (for local testing)
  const handleSyncClientResend = async () => {
    if (!selectedClaim) return;

    setSyncingClientResend(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/ui/claims/${selectedClaim.id}/sync-client-resend-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to sync');
      }

      if (data.synced_documents && data.synced_documents.length > 0) {
        setUpdateMessage(`Synced ${data.synced_documents.length} signed document(s) from DocuSeal`);
        // Reload claims to show updated data
        await loadClaims();
        // Also refresh the selected claim
        if (selectedClaim) {
          const updatedClaim = await getClaim(selectedClaim.id);
          setSelectedClaim(updatedClaim);
        }
      } else {
        setUpdateMessage('No new signed documents found to sync');
      }

      if (data.errors && data.errors.length > 0) {
        console.warn('Sync errors:', data.errors);
      }

      setTimeout(() => setUpdateMessage(''), 5000);
    } catch (err: any) {
      setUpdateMessage(`Error: ${err.message || 'Failed to sync'}`);
      setTimeout(() => setUpdateMessage(''), 5000);
    } finally {
      setSyncingClientResend(false);
    }
  };

  const handleAddCorrespondence = async (correspondence: Correspondence) => {
    if (!selectedClaim) return;
    
    try {
      const result = await addCorrespondence(selectedClaim.id, correspondence);
      
      // Update the selected claim's correspondence array
      setSelectedClaim(prev => {
        if (!prev) return prev;
        const updatedCorrespondence = [...(prev.correspondence || []), result.correspondence];
        return {
          ...prev,
          correspondence: updatedCorrespondence,
          lastCorrespondenceDate: result.correspondence.timestamp
        };
      });
      
      // Show success message
      setUpdateMessage('Call record saved successfully');
      setTimeout(() => setUpdateMessage(''), 3000);
      
      // Refresh claims list to update correspondence count
      loadClaims();
    } catch (error: any) {
      console.error('Failed to add correspondence:', error);
      setActionError(error.response?.data?.error || 'Failed to save call record');
      setTimeout(() => setActionError(''), 5000);
    }
  };

  const handleGeoLookup = async (ip: string) => {
    if (!ip) return;

    setGeoLookups(prev => ({
      ...prev,
      [ip]: {
        ...prev[ip],
        loading: true,
        error: undefined
      }
    }));

    try {
      const data = await lookupGeoIp(ip);
      setGeoLookups(prev => ({
        ...prev,
        [ip]: {
          loading: false,
          data,
          error: undefined
        }
      }));
    } catch (error: any) {
      const errorMessage = error?.response?.data?.error || error?.message || 'Lookup failed';
      setGeoLookups(prev => ({
        ...prev,
        [ip]: {
          loading: false,
          data: prev[ip]?.data,
          error: errorMessage
        }
      }));
    }
  };

  const renderSignupIpValue = (claim: Claim): React.ReactNode => {
    const ipAddress = claim.clientIpAddress;

    if (!ipAddress) {
      return <span className="ip-missing">Not captured</span>;
    }

    const lookup = geoLookups[ipAddress];
    const locationParts = [lookup?.data?.city, lookup?.data?.state, lookup?.data?.country].filter(Boolean);
    const latitude = lookup?.data?.latitude;
    const longitude = lookup?.data?.longitude;
    const formatCoordinate = (value?: number | null) =>
      typeof value === 'number' ? value.toFixed(4) : undefined;
    const formattedLatitude = formatCoordinate(latitude);
    const formattedLongitude = formatCoordinate(longitude);
    const hasCoordinates = formattedLatitude !== undefined && formattedLongitude !== undefined;

    return (
      <span className="ip-info">
        <code className="ip-address">{ipAddress}</code>
        <button
          type="button"
          className="ip-lookup-button"
          onClick={() => handleGeoLookup(ipAddress)}
          disabled={lookup?.loading}
        >
          {lookup?.loading ? 'Looking up…' : 'Geo lookup'}
        </button>
        {lookup?.data && (
          <span className="ip-geo-result">
            {locationParts.length > 0 ? locationParts.join(', ') : 'No location data'}
            {hasCoordinates && (
              <span className="ip-geo-coords">
                ({formattedLatitude}, {formattedLongitude})
              </span>
            )}
          </span>
        )}
        {lookup?.error && (
          <span className="ip-geo-error">{lookup.error}</span>
        )}
      </span>
    );
  };

  // Task handler functions
  const handleCompleteTask = async (taskId: string) => {
    setTaskCompleteError(null);
    try {
      await unifiedTasksApi.completeTask(taskId);
      // Refresh tasks
      if (selectedClaim) {
        const tasks = await unifiedTasksApi.getTasksForClaim(selectedClaim.id);
        setClaimTasks(tasks || []);
      }
      setUpdateMessage('Task marked as complete');
      setTimeout(() => setUpdateMessage(''), 3000);
    } catch (error: any) {
      console.error('Error completing task:', error);
      setTaskCompleteError({
        taskId,
        message: error.response?.data?.error || 'Failed to complete task',
      });
    }
  };

  const handleCreateTask = async () => {
    if (!selectedClaim) {
      console.error('No claim selected');
      return;
    }
    
    console.log('Creating task for claim:', selectedClaim.id);
    console.log('Task form data:', newTaskForm);
    
    try {
      const taskData = {
        type: 'manual',
        claimId: selectedClaim.id,
        subject: newTaskForm.subject,
        description: newTaskForm.description,
        dueDate: newTaskForm.dueDate || undefined,
        priority: newTaskForm.priority as 'high' | 'medium' | 'low'
      };
      
      console.log('Sending task data:', taskData);
      const createdTask = await unifiedTasksApi.createTask(taskData);
      console.log('Task created successfully:', createdTask);
      
      // Refresh tasks
      const tasks = await unifiedTasksApi.getTasksForClaim(selectedClaim.id);
      setClaimTasks(tasks || []);
      
      // Reset form and close modal
      setNewTaskForm({
        subject: '',
        description: '',
        dueDate: '',
        priority: 'medium'
      });
      setShowNewTaskModal(false);
      setUpdateMessage('Task created successfully');
      setTimeout(() => setUpdateMessage(''), 3000);
    } catch (error: any) {
      console.error('Error creating task:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to create task';
      setActionError(errorMessage);
      setTimeout(() => setActionError(''), 5000);
    }
  };

  const getTaskTypeDisplay = (task: UnifiedTask): string => {
    switch (task.type) {
      case 'id_verification':
        return 'ID Verification';
      case 'email_lender_response':
        return 'Lender Response';
      case 'email_lender_query':
        return 'Lender Query';
      case 'email_client':
        return 'Client Email';
      case 'email_other':
        return 'Other Email';
      case 'manual':
        return 'Manual Task';
      default:
        return task.type || 'Task';
    }
  };

  const getTaskStatusDisplay = (status: string): string => {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'in_progress':
        return 'In Progress';
      case 'completed':
        return 'Completed';
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Rejected';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status || 'Unknown';
    }
  };

  const formatTaskDate = (dateString: string | undefined): string => {
    if (!dateString) return 'No due date';
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return `Overdue by ${Math.abs(diffDays)} days`;
    } else if (diffDays === 0) {
      return 'Due today';
    } else if (diffDays === 1) {
      return 'Due tomorrow';
    } else {
      return `Due in ${diffDays} days`;
    }
  };

  const handleViewCreditCheck = async (claim: Claim) => {
    setCreditCheckError('');
    setCreditCheckSummary(null);
    setCreditCheckClaim(claim); // Store the claim for the modal
    setShowCreditCheckModal(true);
    setCreditCheckLoading(true);
    setDrawerHiddenForModal(true);

    try {
      const response = await getCreditCheckSummary(claim.id);
      console.log('Credit check response:', response); // Debug log
      setCreditCheckSummary(response);
    } catch (error: any) {
      console.error('Error fetching credit check summary:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to fetch credit check data';
      setCreditCheckError(errorMessage);
    } finally {
      setCreditCheckLoading(false);
    }
  };

  const handleCloseCreditCheckModal = () => {
    setShowCreditCheckModal(false);
    setDrawerHiddenForModal(false);
  };

  const handleViewAmlSummary = async (claim: Claim) => {
    setAmlError('');
    setAmlSummary(null);
    setAmlClaim(claim);
    setShowAmlModal(true);
    setAmlLoading(true);
    setDrawerHiddenForModal(true);

    try {
      const summary = await getClaimAmlSummary(claim.id);
      setAmlSummary(summary);
    } catch (error: any) {
      console.error('Error fetching AML summary:', error);
      const errorMessage = error?.response?.data?.error || error?.message || 'Failed to fetch AML data';
      setAmlError(errorMessage);
    } finally {
      setAmlLoading(false);
    }
  };

  const handleCloseAmlModal = () => {
    setShowAmlModal(false);
    setAmlSummary(null);
    setAmlError('');
    setAmlLoading(false);
    setAmlClaim(null);
    setDrawerHiddenForModal(false);
  };

  const handleViewRiskAssessment = async (claim: Claim, version?: string, label?: string) => {
    try {
      const response = await generateRiskAssessment(claim.id, version);
      // Open in the in-app document reader like other Word docs. A new tab
      // only downloads the .docx (blank tab) or hands it to Edge's Office viewer.
      setSelectedDoc({ url: response.data.url, fileName: `${label || 'Risk assessment'} - ${claim.id}.docx` });
      setDocViewerOpen(true);
    } catch (error: any) {
      console.error('Error generating risk assessment:', error);
      alert(error?.response?.data?.error || 'Failed to generate risk assessment document');
    }
  };

  const handleViewFullReport = (claimId: string) => {
    // getCreditCheckReportUrl already includes the token in the URL
    const reportUrl = getCreditCheckReportUrl(claimId);
    
    console.log('Opening credit check report:', {
      claimId,
      reportUrl
    });
    
    // Open in new tab - the URL already includes the token from getCreditCheckReportUrl
    window.open(reportUrl, '_blank');
  };


  const handleShowDocs = (claim: Claim) => {
    console.log('handleShowDocs called with claim:', claim);
    console.log('Document URLs:', {
      formOfAuthorityUrl: claim.formOfAuthorityUrl,
      finalBillUrl: claim.finalBillUrl,
      damageBasedAgreementUrl: claim.damageBasedAgreementUrl,
      locDocUrl: claim.locDocUrl,
      valid8responseUrl: claim.valid8responseUrl,
      fileCount: claim.fileCount
    });
    setSelectedClaimForDocs(claim);
    setShowDocsModal(true);
    console.log('State after setting:', { showDocsModal: true, selectedClaimForDocs: claim });
  };

  const handleCloseDocs = () => {
    setSelectedClaimForDocs(null);
    setShowDocsModal(false);
  };

  const handleStartAudit = () => {
    if (selectedClaim) {
      setShowAuditForm(true);
    }
  };

  const handleCloseAuditForm = () => {
    closeAuditForm();
  };

  const handleAuditSuccess = async () => {
    closeAuditForm();
    setUpdateMessage('Audit completed successfully');
    if (selectedClaim) {
      // Re-fetch audits to show the new one
      try {
        setLoading(true);
        const auditData = await getClaimAudits(selectedClaim.id);
        const sortedAudits = auditData.audits.sort((a, b) => new Date(b.auditedAt).getTime() - new Date(a.auditedAt).getTime());
        setSelectedClaimAudits(sortedAudits);
      } catch (err) {
        console.error("Failed to re-fetch audits after success:", selectedClaim.id, err);
      } finally {
        setLoading(false);
      }
    }
    setTimeout(() => setUpdateMessage(''), 3000);
  };

  const handleViewFullCreditReport = (claim: Claim) => {
    const reportUrl = getCreditCheckReportUrl(claim.id);
    window.open(reportUrl, '_blank');
  };

  const handleShowAuditForm = () => {
    setShowAuditForm(true);
  };

  // Drag and Drop handlers for email assignment
  const handleDragOver = (e: React.DragEvent, claimId: string) => {
    e.preventDefault();
    e.currentTarget.classList.add('drag-over');
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.currentTarget.classList.remove('drag-over');
  };

  const handleDrop = async (e: React.DragEvent, claimId: string) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');

    try {
      const emailData = e.dataTransfer.getData('email');
      if (!emailData) return;

      const email: EmailMessage = JSON.parse(emailData);
      
      // Show confirmation dialog
      const confirmed = window.confirm(
        `Assign email "${email.subject || '(No subject)'}" to this claim?`
      );

      if (confirmed) {
        // Show loading state
        setActionLoading(prev => new Set(prev).add(claimId));
        
        await assignEmailToClaim(email.id, claimId);
        
        // Show success message
        setUpdateMessage(`Email assigned to claim ${claimId} successfully`);
        setTimeout(() => setUpdateMessage(''), 3000);
        
        // Refresh claim files if this is the selected claim
        if (selectedClaim && selectedClaim.id === claimId) {
          // The ClaimFiles component will auto-refresh
        }
      }
    } catch (error) {
      console.error('Failed to assign email to claim:', error);
      setUpdateMessage('Failed to assign email to claim');
      setTimeout(() => setUpdateMessage(''), 3000);
    } finally {
      setActionLoading(prev => {
        const next = new Set(prev);
        next.delete(claimId);
        return next;
      });
    }
  };

  const handleAuditComplete = (auditId: string) => {
    closeAuditForm();
    // Reload audits for the selected claim
    if (selectedClaim) {
      getClaimAudits(selectedClaim.id).then(auditData => {
        const sortedAudits = auditData.audits.sort((a, b) => new Date(b.auditedAt).getTime() - new Date(a.auditedAt).getTime());
        setSelectedClaimAudits(sortedAudits);
      });
    }
  };

  const handleSendMultipleRepEmail = async (claimId: string) => {
    if (!selectedClaim) return;
    setShowMultipleRepConfirm(true);
  };

  const handleConfirmMultipleRepEmail = async () => {
    if (!selectedClaim) return;
    const claimId = selectedClaim.id;
    setShowMultipleRepConfirm(false);

    try {
      actionLoading.add(claimId);
      setActionLoading(new Set(actionLoading));
      await sendMultipleRepEmail(claimId);
      setMultipleRepResult({
        type: 'success',
        message: `Email sent successfully to ${selectedClaim.email}. A 7-day follow-up task has been created.`
      });
      await loadClaims({ silent: true });
      const freshClaim = await getClaim(claimId);
      setSelectedClaim(freshClaim);
    } catch (error: any) {
      setMultipleRepResult({
        type: 'error',
        message: `Failed to send email: ${error.response?.data?.error || error.message}`
      });
    } finally {
      actionLoading.delete(claimId);
      setActionLoading(new Set(actionLoading));
    }
  };

  const handleConfirmLenderWithdrawal = async () => {
    if (!selectedClaim || lenderWithdrawalInFlight.current) return;
    const claimId = selectedClaim.id;
    lenderWithdrawalInFlight.current = true;
    setLenderWithdrawalSending(true);
    setShowLenderWithdrawalConfirm(false);

    try {
      actionLoading.add(claimId);
      setActionLoading(new Set(actionLoading));
      const { data } = await sendLenderWithdrawalEmail(claimId);
      setLenderWithdrawalsSent(previous => new Set(previous).add(claimId));
      const warnings = [
        data.claimStamped === false ? 'Could not save the send marker on the claim.' : '',
        data.correspondenceLogged === false ? 'Could not log correspondence.' : '',
      ].filter(Boolean).join(' ');
      const successMessage = `Withdrawal of responsibility sent to ${data.lenderName} (${data.lenderEmail}). ${data.correspondenceLogged === true ? 'Logged in correspondence.' : ''} ${warnings}`.trim();
      setLenderWithdrawalResult({
        type: 'success',
        message: successMessage,
      });
      try {
        await loadClaims({ silent: true });
        const freshClaim = await getClaim(claimId);
        setSelectedClaim(current => current?.id === claimId ? freshClaim : current);
      } catch {
        setLenderWithdrawalResult({
          type: 'success',
          message: `${successMessage} Could not refresh claim details. The withdrawal was sent; check correspondence before sending again.`,
        });
      }
    } catch (error: any) {
      setLenderWithdrawalResult({
        type: 'error',
        message: `Failed to send withdrawal: ${error.response?.data?.error || error.message}`,
      });
    } finally {
      lenderWithdrawalInFlight.current = false;
      setLenderWithdrawalSending(false);
      actionLoading.delete(claimId);
      setActionLoading(new Set(actionLoading));
    }
  };

  const handleManualIdUploaded = async () => {
    // Refresh the client verification status after a staff manual upload.
    if (selectedClaim && selectedClaim.clientId) {
      try {
        setVerificationLoading(true);
        const verification = await photoIdApi.getClientVerification(selectedClaim.clientId);
        setClientVerification(verification);
      } catch (err) {
        console.error("Failed to refresh ID verification:", err);
      } finally {
        setVerificationLoading(false);
      }
    }
  };

  const handleSelectClaim = async (claim: Claim | null) => {
    // If a claim is selected, fetch fresh data from API to get clientConflictInfo
    if (claim && claim.id) {
      try {
        const freshClaim = await getClaim(claim.id);
        // Debug: Log correspondence data from API
        const firstCorr = freshClaim.correspondence?.[0];
        console.log('🔍 handleSelectClaim - freshClaim from API:', {
          claimId: freshClaim.id,
          hasCorrespondence: !!freshClaim.correspondence,
          correspondenceLength: freshClaim.correspondence?.length,
          firstItem: firstCorr && 'subject' in firstCorr ? firstCorr.subject : firstCorr && 'title' in firstCorr ? firstCorr.title : undefined
        });
        setSelectedClaim(freshClaim);
      } catch (error) {
        console.error('Failed to fetch claim details:', error);
        // Fall back to using the claim from the list if fetch fails
        console.log('🔍 handleSelectClaim - FALLBACK to list claim:', {
          claimId: claim.id,
          hasCorrespondence: !!claim.correspondence,
          correspondenceLength: claim.correspondence?.length
        });
        setSelectedClaim(claim);
      }
    } else {
      setSelectedClaim(claim);
    }
    setShowLbsModal(false);
    setDrawerHiddenForModal(false);
    setActionsMenuOpen(false);
    setIsEditMode(false);
    closeAuditForm(); // Reset audit form state (and PDF viewer if needed)
    setEditFormData({
      title: '',
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      addressLine1: '',
      addressLine2: '',
      town: '',
      county: '',
      postcode: '',
      dateOfBirth: '',
    });
    setSelectedClaimAudits([]);
    setClientVerification(null);
    setClaimTasks([]);
    setKeyDates(null);
    setKeyDatesError(null);
    setLenderBulkSubmission(null);
    setLbsError(null);
    setShowLbsModal(false);
    setDupAudit(null);
    setDupAuditError(null);
    setShowDupAuditModal(false);

    if (claim) {
      void fetchKeyDates(claim.id);
      // Fetch tasks for the claim
      setTasksLoading(true);
      try {
        const tasks = await unifiedTasksApi.getTasksForClaim(claim.id);
        setClaimTasks(tasks || []);
      } catch (error) {
        console.error('Error fetching tasks:', error);
        setClaimTasks([]);
      } finally {
        setTasksLoading(false);
      }
      
      // Fetch audits
      try {
        setAuditLoading(true);
        const auditData = await getClaimAudits(claim.id);
        const sortedAudits = auditData.audits.sort((a, b) => new Date(b.auditedAt).getTime() - new Date(a.auditedAt).getTime());
        setSelectedClaimAudits(sortedAudits);
      } catch (err) {
        console.error("Failed to fetch audits for claim:", claim.id, err);
        setError("Failed to fetch audit details.");
      } finally {
        setAuditLoading(false);
      }
      
      // Fetch ID verification if we have a clientId
      if (claim.clientId) {
        try {
          setVerificationLoading(true);
          const verification = await photoIdApi.getClientVerification(claim.clientId);
          setClientVerification(verification);
        } catch (err) {
          console.error("Failed to fetch ID verification for client:", claim.clientId, err);
        } finally {
          setVerificationLoading(false);
        }
      }
    } else {
      setKeyDatesLoading(false);
      setKeyDatesSaving(false);
      setLbsLoading(false);
      setLbsSaving(false);
    }
  };

  const handleOpenLenderBulkSubmission = () => {
    if (!selectedClaim) return;
    setActionsMenuOpen(false);
    setDrawerHiddenForModal(true);
    setShowLbsModal(true);
    if (!lenderBulkSubmission && !lbsLoading) {
      void fetchLenderBulkSubmission(selectedClaim.id);
    }
  };

  const fetchDuplicationAudit = useCallback(async (claimId: string) => {
    setDupAuditLoading(true);
    setDupAuditError(null);
    try {
      const response = await getDuplicationAudit(claimId);
      setDupAudit(response.duplicationAudit);
    } catch (err: any) {
      console.error('Failed to fetch duplication audit:', err);
      setDupAuditError(err?.response?.data?.error || err?.message || 'Failed to load duplication audit');
      setDupAudit(null);
    } finally {
      setDupAuditLoading(false);
    }
  }, []);

  const handleOpenDupAudit = () => {
    if (!selectedClaim) return;
    setActionsMenuOpen(false);
    setShowDupAuditModal(true);
    fetchDuplicationAudit(selectedClaim.id);
  };

  const handleSaveDupAudit = useCallback(async (data: Omit<DuplicationAudit, 'completedBy' | 'completedAt' | 'lastUpdatedBy' | 'lastUpdatedAt'>) => {
    if (!selectedClaim) return;
    setDupAuditSaving(true);
    setDupAuditError(null);
    try {
      const response = await saveDuplicationAudit(selectedClaim.id, data);
      setDupAudit(response.duplicationAudit);
      setShowDupAuditModal(false);
      // Refresh claim to pick up new duplicationAudit field
      const updatedClaim = await getClaim(selectedClaim.id);
      setSelectedClaim(updatedClaim);
    } catch (err: any) {
      console.error('Failed to save duplication audit:', err);
      setDupAuditError(err?.response?.data?.error || err?.message || 'Failed to save duplication audit');
    } finally {
      setDupAuditSaving(false);
    }
  }, [selectedClaim]);

  useEffect(() => {
    if (!actionsMenuOpen) return;
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.actions-dropdown')) {
        setActionsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [actionsMenuOpen]);

  useEffect(() => {
    if (!sendEmailMenuOpen) return;
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.send-email-dropdown')) {
        setSendEmailMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [sendEmailMenuOpen]);

  useEffect(() => {
    if (!showLbsModal) {
      setDrawerHiddenForModal(false);
    }
  }, [showLbsModal]);

  useEffect(() => {
    if (!showLetterModal) {
      setDrawerHiddenForModal(false);
    }
  }, [showLetterModal]);

  // Render the main component structure
  return (
    <div className="claims-table-container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 style={{ 
            color: '#004754', 
            margin: 0,
            fontSize: '1.8rem',
            fontWeight: 600 
          }}>Claims</h2>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button 
              onClick={() => setShowEmailDrawer(true)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1d4ed8';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#2563eb';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
              }}
            >
              <span style={{ fontSize: '16px' }}>📧</span> Email Inbox
            </button>
          </div>
        </div>
        
        <HierarchicalClaimsTable 
          onSelectClaim={(claim) => handleSelectClaim(claim as any)}
          onShowDocs={(claim) => handleShowDocs(claim as any)}
          onCancelClaim={(claimId, claimEmail) => handleCancelClaim(claimId, claimEmail ?? '')}
        />
        
        {/* Keep the existing modals and sidebars */}
        {selectedClaim && !shouldHideClaimDrawer && (
          <div className={`claim-details-drawer ${selectedClaim ? 'visible' : ''} ${shouldHideClaimDrawer ? 'hidden-for-modal' : ''}`}>
            <div className="details-drawer-header">
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                Claim Details: {selectedClaim.proclaimReference || selectedClaim.id}
                {selectedClaim.isLegacyClaim && (
                  <span style={{
                    fontSize: '14px',
                    padding: '4px 8px',
                    backgroundColor: '#e5e7eb',
                    color: '#6b7280',
                    borderRadius: '4px',
                    fontWeight: 500
                  }}>
                    Legacy
                  </span>
                )}
                {selectedClaim.finalResponseClosure ? (
                  <span style={{
                    backgroundColor: '#7f1d1d', color: 'white', padding: '2px 6px',
                    borderRadius: '4px', fontSize: '11px', marginLeft: '4px',
                  }}>
                    Permanently Closed
                  </span>
                ) : selectedClaim.reinstatementDisabled && (
                  <span style={{
                    backgroundColor: '#dc2626',
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    marginLeft: '4px',
                  }}>
                    No Reinstate
                  </span>
                )}
                <ClientConfirmationBadge
                  clientConfirmations={selectedClaim.clientConfirmations}
                  correspondence={selectedClaim.correspondence}
                />
                {typeof selectedClaim.unitCount === 'number' && (
                  <button
                    type="button"
                    className="unit-badge unit-badge-button"
                    title={unitBadgeTooltip || 'Units reflect billable actions. Baseline is 2.'}
                    onClick={() => setShowUnitHistory(prev => !prev)}
                  >
                    Units: {selectedClaim.unitCount}
                  </button>
                )}
              </h3>
              <div className="header-actions">
                {!isEditMode && (
                  <button
                    type="button"
                    onClick={() => setShowRedressModal(true)}
                    style={{
                      padding: '6px 14px',
                      marginRight: 8,
                      border: '1px solid #2563eb',
                      background: '#2563eb',
                      color: '#fff',
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontWeight: 500,
                    }}
                  >
                    Redress
                  </button>
                )}
                {!isEditMode && (
                  <div className="actions-dropdown">
                    <button
                      type="button"
                      className="actions-dropdown__toggle"
                      onClick={() => { setActionsMenuOpen(prev => !prev); setSendEmailMenuOpen(false); }}
                    >
                      Actions
                      <span className="actions-dropdown__chevron">▾</span>
                    </button>
                    {actionsMenuOpen && (
                      <div className="actions-dropdown__menu">
                        <button type="button" onClick={() => { handleEditClick(selectedClaim); setActionsMenuOpen(false); }}>
                          Edit client info
                        </button>
                        <button type="button" onClick={() => { handleComposeEmail(selectedClaim); setActionsMenuOpen(false); }}>
                          Email
                        </button>
                        <button type="button" onClick={() => { handleComposeSms(selectedClaim); setActionsMenuOpen(false); }}>
                          SMS
                        </button>
                        <button type="button" onClick={() => handleOpenLetterModal(selectedClaim)}>
                          Send letter
                        </button>
                        <button type="button" className="actions-dropdown__item--audit-trail" onClick={() => { setShowAuditTrailModal(true); setActionsMenuOpen(false); setDrawerHiddenForModal(true); }}>
                          Get audit trail
                        </button>
                        <button type="button" onClick={() => {
                          setFinalResponseClaim(selectedClaim);
                          setActionsMenuOpen(false);
                        }}>
                          {selectedClaim.finalResponseClosure ? 'View final response' : 'Send final response'}
                        </button>
                        <button type="button" disabled={lenderWithdrawalSending} onClick={() => {
                          setShowLenderWithdrawalConfirm(true);
                          setActionsMenuOpen(false);
                        }}>
                          {(lenderWithdrawalsSent.has(selectedClaim.id) || selectedClaim.lenderWithdrawalSentAt || selectedClaim.locWithdrawalSentToLender)
                            ? 'Resend withdrawal to lender'
                            : 'Send withdrawal to lender'}
                        </button>
                        {(() => {
                          const locAction = getLocActionAvailability(
                            selectedClaim,
                            sendingIds.has(selectedClaim.id) || actionLoading.has(selectedClaim.id),
                          );
                          // Bulk submitted: the lender already has it, so no LOC button at all.
                          if (locAction.hidden) return null;
                          const label = sendingIds.has(selectedClaim.id) ? 'Sending…' : locAction.label;
                          return (
                            <span className="actions-dropdown__tooltip" title={locAction.tooltip}>
                              <button
                                type="button"
                                disabled={locAction.disabled}
                                aria-describedby={`loc-action-help-${selectedClaim.id}`}
                                onClick={() => {
                                  if (locAction.action === 'resend') {
                                    setLocResendClaim(selectedClaim);
                                  } else {
                                    handleSendLOC(selectedClaim.id);
                                  }
                                  setActionsMenuOpen(false);
                                }}
                              >
                                {label}
                              </button>
                              <span id={`loc-action-help-${selectedClaim.id}`} className="sr-only">
                                {locAction.tooltip}
                              </span>
                            </span>
                          );
                        })()}
                        {selectedClaim.status !== 'CANCELLED' && (
                        <button
                          type="button"
                          onClick={() => {
                            handleCancelClaim(selectedClaim.id, selectedClaim.email);
                            setActionsMenuOpen(false);
                          }}
                          disabled={actionLoading.has(selectedClaim.id)}
                          style={{ backgroundColor: '#dc2626', color: '#ffffff', borderRadius: '6px' }}
                        >
                          {actionLoading.has(selectedClaim.id) ? 'Processing…' : 'Cancel claim'}
                        </button>
                        )}
                        {selectedClaim.status === 'CANCELLED' && selectedClaim.reinstatementBlock && (
                          <>
                            {/* Tooltip on a wrapper: some browsers show none on a disabled button. */}
                            <span title={selectedClaim.reinstatementBlock.message} style={{ display: 'block', cursor: 'not-allowed' }}>
                              <button
                                type="button"
                                disabled
                                style={{ backgroundColor: '#e5e7eb', color: '#9ca3af', pointerEvents: 'none', width: '100%' }}
                              >
                                Reinstate claim
                              </button>
                            </span>
                            <span style={{ display: 'block', fontSize: '12px', color: '#b91c1c', padding: '2px 4px' }}>
                              {selectedClaim.reinstatementBlock.message}
                            </span>
                          </>
                        )}
                        {selectedClaim.status === 'CANCELLED' && !selectedClaim.reinstatementBlock && !selectedClaim.reinstatementDisabled && (
                          <button
                            type="button"
                            onClick={() => {
                              handleReinstateClaim(selectedClaim.id);
                              setActionsMenuOpen(false);
                            }}
                            disabled={actionLoading.has(selectedClaim.id)}
                          >
                            {actionLoading.has(selectedClaim.id) ? 'Processing…' : 'Reinstate claim'}
                          </button>
                        )}
                        {/* No Enable/Disable toggle when the scheme blocks it: the
                            block cannot be switched off, so the toggle would mislead. */}
                        {selectedClaim.status === 'CANCELLED' && !selectedClaim.finalResponseClosure && !selectedClaim.reinstatementBlock && (
                          <button
                            type="button"
                            onClick={() => {
                              handleToggleReinstatement(selectedClaim);
                              setActionsMenuOpen(false);
                            }}
                            disabled={actionLoading.has(selectedClaim.id)}
                            style={{
                              backgroundColor: selectedClaim.reinstatementDisabled ? '#28a745' : '#dc2626',
                              color: '#ffffff',
                              borderRadius: '6px',
                              padding: '6px 12px',
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: '13px',
                            }}
                          >
                            {selectedClaim.reinstatementDisabled ? 'Enable Reinstatement' : 'Disable Reinstatement'}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            handleOpenLenderBulkSubmission();
                            setActionsMenuOpen(false);
                          }}
                          disabled={lbsLoading && !lenderBulkSubmission}
                        >
                          {lbsLoading && !lenderBulkSubmission ? 'Loading…' : 'View lender bulk submission'}
                        </button>
                        <button
                          type="button"
                          onClick={() => { setShowLitigationFriendModal(true); setActionsMenuOpen(false); setDrawerHiddenForModal(true); }}
                          style={{ backgroundColor: '#6b7280', color: '#ffffff', borderRadius: '6px' }}
                        >
                          {selectedClaim.litigationFriendDetails ? 'View/Edit Executor / Lit Friend' : 'Add Litigation Friend / Executor'}
                        </button>
                        {/* Client document resend buttons */}
                        <button
                          type="button"
                          disabled={resendingFoa || isResendDisabled('foa').disabled}
                          title={isResendDisabled('foa').reason || 'Resend FOA document to client for signing'}
                          onClick={() => handleResendDocumentClick('foa')}
                        >
                          {resendingFoa ? 'Sending…' : 'Resend FOA'}
                        </button>
                        <button
                          type="button"
                          disabled={resendingDba || isResendDisabled('dba').disabled}
                          title={isResendDisabled('dba').reason || 'Resend DBA document to client for signing'}
                          onClick={() => handleResendDocumentClick('dba')}
                        >
                          {resendingDba ? 'Sending…' : 'Resend DBA'}
                        </button>
                        <button
                          type="button"
                          disabled={sendingLoaQuestionnaire}
                          title="Send LOA questionnaire link to client via email"
                          onClick={async () => {
                            if (!selectedClaim) return;
                            setSendingLoaQuestionnaire(true);
                            setActionsMenuOpen(false);
                            try {
                              const { data: loaResult } = await requestLOA(selectedClaim.id);
                              // Say WHICH of the two happened. A resend hands
                              // the client the identical link they already
                              // had; if they have already completed that
                              // questionnaire the portal will refuse it, and
                              // the old blanket "sent!" gave staff no way to
                              // tell a fresh questionnaire from a repeat.
                              alert(loaResult?.resent
                                ? 'Existing LOA link resent — the client already had this exact questionnaire. '
                                  + 'No new one was created.'
                                : 'New LOA questionnaire sent to client.');
                              // Refresh claim to show updated correspondence
                              const updatedClaim = await getClaim(selectedClaim.id);
                              setSelectedClaim(updatedClaim);
                            } catch (err: any) {
                              console.error('Error sending LOA questionnaire:', err);
                              alert(`Failed to send LOA questionnaire: ${err.response?.data?.error || err.message}`);
                            } finally {
                              setSendingLoaQuestionnaire(false);
                            }
                          }}
                        >
                          {sendingLoaQuestionnaire ? 'Sending…' : 'Send LOA Questionnaire'}
                        </button>
                        <button
                          type="button"
                          disabled={generatingDsar}
                          title="Download a DSAR response letter for this client (all their claims) to check and send"
                          onClick={async () => {
                            if (!selectedClaim) return;
                            const dsarClaimId = selectedClaim.id;
                            setGeneratingDsar(true);
                            setActionsMenuOpen(false);
                            try {
                              const { blob, filename } = await generateDsarDocument(dsarClaimId);
                              saveBlobAsFile(blob, filename);
                              alert("DSAR letter downloaded – it's also saved on the claim's timeline");
                              // Show the new timeline entry. The letter is already
                              // downloaded, so a failed refresh is only a warning.
                              try {
                                const fresh = await getClaim(dsarClaimId);
                                setSelectedClaim(current => (current?.id === dsarClaimId ? fresh : current));
                              } catch (refreshError) {
                                console.warn('DSAR letter generated but the claim could not be refreshed:', refreshError);
                              }
                            } catch (err) {
                              console.error('Error generating DSAR letter:', err);
                              alert(await dsarErrorMessage(err));
                            } finally {
                              setGeneratingDsar(false);
                            }
                          }}
                        >
                          {generatingDsar ? 'Generating DSAR…' : 'Generate DSAR document'}
                        </button>
                        <button
                          type="button"
                          disabled={syncingClientResend}
                          title="Sync signed documents from DocuSeal (for local testing)"
                          onClick={handleSyncClientResend}
                          style={{ backgroundColor: '#6366f1', color: 'white' }}
                        >
                          {syncingClientResend ? 'Syncing…' : 'Sync Signed Docs'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowSplitClaimsModal(true);
                            setActionsMenuOpen(false);
                            setDrawerHiddenForModal(true);
                          }}
                        >
                          Split claims
                        </button>
                        <button
                          type="button"
                          onClick={handleOpenDupAudit}
                        >
                          Duplication Audit
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {isEditMode && (
                  <>
                    <button onClick={handleSaveEdits} className="save-button" disabled={actionLoading.has(selectedClaim.id)}>
                      Save Changes
                    </button>
                    <button onClick={handleCancelEdit} className="cancel-button">
                      Cancel
                    </button>
                  </>
                )}
                <button onClick={handleCloseDetails} className="close-drawer-button">&times;</button>
              </div>
            </div>
            <div className="details-drawer-content" onScroll={() => setActionsMenuOpen(false)}>
              <LenderResponseCard
                responses={selectedClaim.lenderResponses}
                outcomeDate={selectedClaim.submissionOutcomeDate}
                commissionAmount={selectedClaim.commissionAmount}
                commissionInformedDate={selectedClaim.commissionInformedDate}
              />
              {showUnitHistory && (
                <div className="unit-history-popover">
                  <div className="unit-history-header">
                    <span className="unit-history-title">Unit history</span>
                    <button type="button" className="unit-history-close" onClick={() => setShowUnitHistory(false)}>
                      ×
                    </button>
                  </div>
                  {formattedUnitHistory.length === 0 ? (
                    <div className="unit-history-empty">No unit history recorded yet.</div>
                  ) : (
                    <table className="unit-history-table">
                      <thead>
                        <tr>
                          <th>When</th>
                          <th>Change</th>
                          <th>Total</th>
                          <th>Reason</th>
                          <th>Actor</th>
                          <th>Source</th>
                        </tr>
                      </thead>
                      <tbody>
                        {formattedUnitHistory.map((row, index) => (
                          <tr key={`${row.timestamp}-${index}`} className={row.delta.startsWith('+') ? 'unit-row-positive' : row.delta.startsWith('-') ? 'unit-row-negative' : 'unit-row-neutral'}>
                            <td>{row.timestamp}</td>
                            <td>{row.delta}</td>
                            <td>{row.total ?? '-'}</td>
                            <td>{row.reason}</td>
                            <td>{row.actor}</td>
                            <td>{row.source || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* View Selector - Only show if litigation friend exists */}
              {selectedClaim.litigationFriendDetails && (
                <div style={{
                  marginBottom: '20px',
                  marginTop: '20px',
                  display: 'flex',
                  gap: '10px',
                  alignItems: 'center'
                }}>
                  <label style={{ fontWeight: '600', marginRight: '5px' }}>View:</label>
                  <button
                    style={{
                      padding: '10px 20px',
                      borderRadius: '6px',
                      border: '2px solid #4CAF50',
                      backgroundColor: '#4CAF50',
                      color: '#fff',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'default',
                      boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)'
                    }}
                  >
                    Client Information
                  </button>
                  <button
                    onClick={() => { setShowLitigationFriendModal(true); setDrawerHiddenForModal(true); }}
                    style={{
                      padding: '10px 20px',
                      borderRadius: '6px',
                      border: '2px solid #2196F3',
                      backgroundColor: '#fff',
                      color: '#333',
                      fontSize: '14px',
                      fontWeight: '400',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {selectedClaim.litigationFriendDetails?.role === 'executor' || selectedClaim.litigationFriendDetails?.role === 'administrator' ? 'Executor/Administrator' : 'Litigation Friend'}
                  </button>
                </div>
              )}

              {/* Client Info Section */}
              {(
                <div className="info-section">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h4 style={{ marginTop: 0, marginBottom: 0 }}>Client Information</h4>

                  {/* Conflict Warning Badge */}
                  {selectedClaim.clientConflictInfo?.conflictFound && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 12px',
                        backgroundColor: '#fff3cd',
                        border: '1px solid #ffc107',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#856404'
                      }}
                    >
                      <span style={{ fontSize: '18px' }}>⚠️</span>
                      <div>
                        <div>Client Conflict Detected</div>
                        {selectedClaim.clientConflictInfo.conflictMarkedAt && (
                          <div style={{ fontSize: '12px', fontWeight: 400, marginTop: '2px' }}>
                            Marked: {new Date(selectedClaim.clientConflictInfo.conflictMarkedAt).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Multiple Representation Email Banner */}
                  {selectedClaim.multipleRepEmailSentAt && (() => {
                    const sentDate = new Date(selectedClaim.multipleRepEmailSentAt!);
                    const dueDate = new Date(sentDate.getTime() + 7 * 24 * 60 * 60 * 1000);
                    const now = new Date();
                    const daysLeft = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                    const isOverdue = daysLeft < 0;
                    const isDueSoon = daysLeft >= 0 && daysLeft <= 2;

                    return (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '10px 14px',
                          backgroundColor: isOverdue ? '#fef2f2' : isDueSoon ? '#fffbeb' : '#eff6ff',
                          border: `1px solid ${isOverdue ? '#fca5a5' : isDueSoon ? '#fcd34d' : '#93c5fd'}`,
                          borderRadius: '6px',
                          fontSize: '13px',
                          color: isOverdue ? '#991b1b' : isDueSoon ? '#92400e' : '#1e40af',
                          marginTop: '8px'
                        }}
                      >
                        <span style={{ fontSize: '16px' }}>{isOverdue ? '🔴' : isDueSoon ? '🟡' : '🔵'}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600 }}>
                            Multiple Representation Email Sent
                          </div>
                          <div style={{ fontSize: '12px', marginTop: '2px' }}>
                            Sent by {selectedClaim.multipleRepEmailSentBy || 'system'} on {sentDate.toLocaleDateString('en-GB')} at {sentDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                          <div style={{ fontSize: '12px', fontWeight: 600, marginTop: '4px' }}>
                            {isOverdue
                              ? `Response overdue by ${Math.abs(daysLeft)} day${Math.abs(daysLeft) === 1 ? '' : 's'}`
                              : daysLeft === 0
                                ? 'Response due today'
                                : `Response due in ${daysLeft} day${daysLeft === 1 ? '' : 's'} (${dueDate.toLocaleDateString('en-GB')})`
                            }
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Finance claims (FC/FM): the claim's original Proclaim claim ID */}
                {(() => {
                  const proclaimClaimReference = getProclaimClaimReference(selectedClaim);
                  if (!proclaimClaimReference) return null;
                  return (
                    <div
                      data-testid="proclaim-claim-reference"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '12px',
                        marginLeft: '-13px',
                        padding: '6px 12px',
                        backgroundColor: '#f3e8ff',
                        border: '1px solid #c084fc',
                        borderRadius: '6px',
                        color: '#6b21a8',
                        fontSize: '14px'
                      }}
                    >
                      <strong style={{ color: '#000' }}>Proclaim Claim Reference:</strong>{' '}
                      <span style={{ fontFamily: 'monospace', fontSize: '15px', fontWeight: 700, letterSpacing: '0.5px' }}>
                        {proclaimClaimReference}
                      </span>
                    </div>
                  );
                })()}
                {isEditMode ? (
                  <div className="edit-form">
                    <label>
                      Title:
                      <select
                        value={editFormData.title}
                        onChange={(e) => setEditFormData({...editFormData, title: e.target.value})}
                      >
                        <option value="">(none)</option>
                        <option value="Mr">Mr</option>
                        <option value="Mrs">Mrs</option>
                        <option value="Miss">Miss</option>
                        <option value="Ms">Ms</option>
                        <option value="Mx">Mx</option>
                        <option value="Dr">Dr</option>
                        <option value="Prof">Prof</option>
                        {editFormData.title &&
                          !['Mr', 'Mrs', 'Miss', 'Ms', 'Mx', 'Dr', 'Prof'].includes(editFormData.title) && (
                            <option value={editFormData.title}>{editFormData.title}</option>
                          )}
                      </select>
                    </label>
                    <label>
                      First Name:
                      <input
                        type="text"
                        value={editFormData.firstName}
                        onChange={(e) => setEditFormData({...editFormData, firstName: e.target.value})}
                      />
                    </label>
                    <label>
                      Last Name:
                      <input
                        type="text"
                        value={editFormData.lastName}
                        onChange={(e) => setEditFormData({...editFormData, lastName: e.target.value})}
                      />
                    </label>
                    <label>
                      Email:
                      <input
                        type="email"
                        value={editFormData.email}
                        onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                      />
                    </label>
                    <label>
                      Phone:
                      <input
                        type="tel"
                        value={editFormData.phoneNumber}
                        onChange={(e) => setEditFormData({...editFormData, phoneNumber: e.target.value})}
                      />
                    </label>
                    <label>
                      Address Line 1:
                      <input
                        type="text"
                        value={editFormData.addressLine1}
                        onChange={(e) => setEditFormData({...editFormData, addressLine1: e.target.value})}
                      />
                    </label>
                    <label>
                      Address Line 2:
                      <input
                        type="text"
                        value={editFormData.addressLine2}
                        onChange={(e) => setEditFormData({...editFormData, addressLine2: e.target.value})}
                      />
                    </label>
                    <label>
                      Town:
                      <input
                        type="text"
                        value={editFormData.town}
                        onChange={(e) => setEditFormData({...editFormData, town: e.target.value})}
                      />
                    </label>
                    <label>
                      County:
                      <input
                        type="text"
                        value={editFormData.county}
                        onChange={(e) => setEditFormData({...editFormData, county: e.target.value})}
                      />
                    </label>
                    <label>
                      Postcode:
                      <input
                        type="text"
                        value={editFormData.postcode}
                        onChange={(e) => setEditFormData({...editFormData, postcode: e.target.value})}
                      />
                    </label>
                    <label>
                      Date of Birth:
                      <input
                        type="text"
                        placeholder="DD/MM/YYYY"
                        value={editFormData.dateOfBirth}
                        onChange={(e) => setEditFormData({...editFormData, dateOfBirth: e.target.value})}
                      />
                    </label>
                  </div>
                ) : (
                  <div className="info-grid">
                    <InfoRow label="Name" value={`${selectedClaim.title ? selectedClaim.title + ' ' : ''}${selectedClaim.firstName || ''} ${selectedClaim.lastName || ''}`} />
                    <InfoRow label="Email" value={selectedClaim.email} />
                    <InfoRow label="Signup IP" value={renderSignupIpValue(selectedClaim)} />
                    <InfoRow label="Phone" value={selectedClaim.phoneNumber} />
                    <InfoRow label="Date of Birth" value={selectedClaim.dateOfBirth} />
                    <InfoRow label="Address" value={`${selectedClaim.addressLine1 || ''} ${selectedClaim.addressLine2 || ''}`} />
                    <InfoRow label="Town" value={selectedClaim.town} />
                    <InfoRow label="County" value={selectedClaim.county} />
                    <InfoRow label="Postcode" value={selectedClaim.postcode} />

                    {/* Conflict Status Row */}
                    {selectedClaim.clientConflictInfo && (
                      <InfoRow
                        label="Conflict Status"
                        value={
                          selectedClaim.clientConflictInfo.conflictFound
                            ? <span style={{ color: '#dc3545', fontWeight: 600 }}>⚠️ Conflict Detected</span>
                            : <span style={{ color: '#28a745' }}>✓ No Conflict</span>
                        }
                      />
                    )}

                    {selectedClaim.clientConflictInfo?.lastUpdated && (
                      <InfoRow
                        label="Client Last Updated"
                        value={new Date(selectedClaim.clientConflictInfo.lastUpdated).toLocaleString()}
                      />
                    )}

                    {selectedClaim.previouslyKnownNames && selectedClaim.previouslyKnownNames.length > 0 && (
                      <div style={{ marginBottom: '8px' }}>
                        <strong style={{ fontSize: '13px', color: '#555' }}>Previously Known As:</strong>
                        <div style={{ paddingLeft: '8px', marginTop: '4px' }}>
                          {selectedClaim.previouslyKnownNames.map((name: string, idx: number) => (
                            <div key={idx} style={{ fontSize: '13px', marginBottom: '2px' }}>
                              {idx + 1}. {name}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedClaim.previousAddresses && selectedClaim.previousAddresses.length > 0 && (
                      <div style={{ marginBottom: '8px' }}>
                        <strong style={{ fontSize: '13px', color: '#555' }}>Previous Addresses:</strong>
                        <div style={{ paddingLeft: '8px', marginTop: '4px' }}>
                          {selectedClaim.previousAddresses.map((addr: string, idx: number) => (
                            <div key={idx} style={{ fontSize: '13px', marginBottom: '2px' }}>
                              {idx + 1}. {addr}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {updateMessage && (
                  <div className={`update-message ${updateMessage.includes('Error') ? 'error' : 'success'}`}>
                    {updateMessage}
                  </div>
                )}
              </div>
              )}

              {/* Litigation Friend Section - now in LitigationFriendModal - inline code removed */}


              <div className="info-section" style={{ marginTop: '20px' }}>
                <ClaimKeyDatesTimeline
                  claimId={selectedClaim.id}
                  data={keyDates}
                  loading={keyDatesLoading}
                  error={keyDatesError}
                  saving={keyDatesSaving}
                  onSave={handleSaveKeyDates}
                  onRefresh={handleRefreshKeyDates}
                />
              </div>

              <LOAIdentityReviewPanel claimId={selectedClaim.id} />

              {/* LOA Portal Submissions */}
              {selectedClaim.loaSubmissions && selectedClaim.loaSubmissions.length > 0 && (
                <div className="info-section" style={{ marginTop: '20px' }}>
                  <div style={{
                    backgroundColor: '#e8f5e9',
                    padding: '12px',
                    borderRadius: '6px',
                    marginBottom: '15px',
                    border: '1px solid #c8e6c9'
                  }}>
                    <h4 style={{ margin: '0 0 15px 0', color: '#2e7d32', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>📝</span> LOA Portal Submissions ({selectedClaim.loaSubmissions.length})
                    </h4>

                    {selectedClaim.loaSubmissions.map((submission, idx) => (
                      <div key={submission.loaRequestId || idx} style={{
                        backgroundColor: '#fff',
                        border: '1px solid #e0e0e0',
                        borderRadius: '6px',
                        padding: '12px',
                        marginBottom: idx < selectedClaim.loaSubmissions!.length - 1 ? '12px' : '0'
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '10px',
                          paddingBottom: '8px',
                          borderBottom: '1px solid #e0e0e0'
                        }}>
                          <span style={{ fontWeight: '600', color: '#333' }}>
                            Submission #{selectedClaim.loaSubmissions!.length - idx}
                          </span>
                          <span style={{ fontSize: '12px', color: '#666' }}>
                            {submission.submittedAt ? new Date(submission.submittedAt).toLocaleString() : 'Unknown date'}
                          </span>
                        </div>

                        {/* Audit Information */}
                        {submission.audit && (
                          <div style={{ marginBottom: '10px' }}>
                            <div style={{ fontWeight: '500', fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                              Audit Details
                            </div>
                            <div style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr',
                              gap: '8px',
                              fontSize: '13px'
                            }}>
                              <div>
                                <span style={{ color: '#888' }}>IP Address: </span>
                                <span style={{ fontFamily: 'monospace', fontWeight: '500' }}>
                                  {submission.audit.ipAddress || 'N/A'}
                                </span>
                              </div>
                              <div style={{ gridColumn: '1 / -1' }}>
                                <span style={{ color: '#888' }}>User Agent: </span>
                                <span style={{ fontSize: '11px', wordBreak: 'break-all' }}>
                                  {submission.audit.userAgent || 'N/A'}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Signature Information */}
                        {submission.signature && (
                          <div style={{ marginBottom: '10px' }}>
                            <div style={{ fontWeight: '500', fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                              Signature Details
                            </div>
                            <div style={{ fontSize: '13px' }}>
                              <div>
                                <span style={{ color: '#888' }}>Signed at: </span>
                                <span>{submission.signature.timestamp ? new Date(submission.signature.timestamp).toLocaleString() : 'N/A'}</span>
                              </div>
                              <div>
                                <span style={{ color: '#888' }}>Method: </span>
                                <span>{submission.signature.method || 'N/A'}</span>
                              </div>
                              {submission.signature.pngUrl && (
                                <div>
                                  <a
                                    href={submission.signature.pngUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: '#1976d2', fontSize: '12px' }}
                                  >
                                    View Signature Image →
                                  </a>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Values captured before the portal overwrote claim identity. */}
                        {submission.previousValues && (
                          <div style={{ marginBottom: '10px', padding: '8px', background: '#fff8e1', borderRadius: '4px' }}>
                            <div style={{ fontWeight: '500', fontSize: '12px', color: '#795548', marginBottom: '4px' }}>
                              Values before this submission
                            </div>
                            <div style={{ fontSize: '13px' }}>
                              <div>
                                <span style={{ color: '#888' }}>Name: </span>
                                <span>{[submission.previousValues.firstName, submission.previousValues.lastName].filter(Boolean).join(' ') || 'N/A'}</span>
                              </div>
                              <div>
                                <span style={{ color: '#888' }}>DOB: </span>
                                <span>{submission.previousValues.dateOfBirth || 'N/A'}</span>
                              </div>
                              <div>
                                <span style={{ color: '#888' }}>Email: </span>
                                <span>{submission.previousValues.clientEmail || 'N/A'}</span>
                              </div>
                              {submission.previousValues.address && (
                                <div>
                                  <span style={{ color: '#888' }}>Address: </span>
                                  <span>{[
                                    submission.previousValues.address.line1,
                                    submission.previousValues.address.line2,
                                    submission.previousValues.address.city,
                                    submission.previousValues.address.county,
                                    submission.previousValues.address.postcode,
                                  ].filter(Boolean).join(', ') || 'N/A'}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Submitted Data Summary */}
                        {submission.submissionData && (
                          <div style={{ marginBottom: '10px' }}>
                            <div style={{ fontWeight: '500', fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                              Submitted Data
                            </div>
                            <div style={{ fontSize: '13px' }}>
                              {submission.submissionData.personalDetails && (
                                <>
                                  <div>
                                    <span style={{ color: '#888' }}>Full Legal Name: </span>
                                    <span>{submission.submissionData.personalDetails.fullLegalName || 'N/A'}</span>
                                  </div>
                                  <div>
                                    <span style={{ color: '#888' }}>DOB: </span>
                                    <span>{submission.submissionData.personalDetails.dateOfBirth || 'N/A'}</span>
                                  </div>
                                  {submission.submissionData.personalDetails.previousNames &&
                                   submission.submissionData.personalDetails.previousNames.length > 0 && (
                                    <div>
                                      <span style={{ color: '#888' }}>Previous Names: </span>
                                      <span>
                                        {submission.submissionData.personalDetails.previousNames
                                          .map(n => `${n.firstName} ${n.lastName}`.trim())
                                          .filter(n => n)
                                          .join(', ') || 'None'}
                                      </span>
                                    </div>
                                  )}
                                </>
                              )}
                              {submission.submissionData.addresses?.currentAddress && (
                                <div>
                                  <span style={{ color: '#888' }}>Address: </span>
                                  <span>
                                    {[
                                      submission.submissionData.addresses.currentAddress.line1,
                                      submission.submissionData.addresses.currentAddress.line2,
                                      submission.submissionData.addresses.currentAddress.city,
                                      submission.submissionData.addresses.currentAddress.postcode
                                    ].filter(Boolean).join(', ') || 'N/A'}
                                  </span>
                                </div>
                              )}
                              {submission.submissionData.addresses?.previousAddresses &&
                               submission.submissionData.addresses.previousAddresses.length > 0 && (
                                <div>
                                  <span style={{ color: '#888' }}>Previous Addresses: </span>
                                  <div style={{ paddingLeft: '8px', marginTop: '2px' }}>
                                    {submission.submissionData.addresses.previousAddresses.map((addr, addrIdx) => {
                                      const addrStr = [addr.line1, addr.city, addr.postcode].filter(Boolean).join(', ');
                                      return addrStr ? (
                                        <div key={addrIdx} style={{ fontSize: '12px', marginBottom: '2px' }}>
                                          {addrIdx + 1}. {addrStr}
                                        </div>
                                      ) : null;
                                    })}
                                  </div>
                                </div>
                              )}
                              {submission.submissionData.vehicles && submission.submissionData.vehicles.length > 0 && (
                                <div>
                                  <span style={{ color: '#888' }}>Vehicle: </span>
                                  <span>
                                    {(() => {
                                      const claimVehicle = submission.submissionData.vehicles.find(
                                        v => v.claimId === selectedClaim.id
                                      );
                                      if (claimVehicle) {
                                        return [claimVehicle.make, claimVehicle.model, claimVehicle.registration].filter(Boolean).join(' ');
                                      }
                                      const first = submission.submissionData.vehicles[0];
                                      return [first.make, first.model, first.registration].filter(Boolean).join(' ');
                                    })()}
                                  </span>
                                </div>
                              )}
                              {submission.submissionData.consent && (
                                <div>
                                  <span style={{ color: '#888' }}>Cheque Consent: </span>
                                  <span style={{
                                    color: submission.submissionData.consent.chequePaymentConsent ? '#2e7d32' : '#c62828',
                                    fontWeight: '500'
                                  }}>
                                    {submission.submissionData.consent.chequePaymentConsent ? 'Yes' : 'No'}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Document Link */}
                        {submission.documentUrl && (
                          <div>
                            <a
                              href={submission.documentUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '6px 12px',
                                backgroundColor: '#1976d2',
                                color: '#fff',
                                borderRadius: '4px',
                                textDecoration: 'none',
                                fontSize: '12px',
                                fontWeight: '500'
                              }}
                            >
                              📄 View LOA Document
                            </a>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pending LOA Token Info */}
              {selectedClaim.pendingLoaToken && (
                <div className="info-section" style={{ marginTop: '10px' }}>
                  <div style={{
                    backgroundColor: '#fff3e0',
                    padding: '12px',
                    borderRadius: '6px',
                    border: '1px solid #ffcc80'
                  }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#e65100', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>⏳</span> Pending LOA Questionnaire
                    </h4>
                    <div style={{ fontSize: '13px' }}>
                      <div>
                        <span style={{ color: '#888' }}>Link sent: </span>
                        <span>{selectedClaim.pendingLoaToken.sentAt ? new Date(selectedClaim.pendingLoaToken.sentAt).toLocaleString() : 'Unknown'}</span>
                      </div>
                      <div>
                        <span style={{ color: '#888' }}>Expires: </span>
                        <span>{selectedClaim.pendingLoaToken.expiresAt ? new Date(selectedClaim.pendingLoaToken.expiresAt).toLocaleString() : 'Unknown'}</span>
                      </div>
                      <div style={{ marginTop: '8px' }}>
                        <a
                          href={selectedClaim.pendingLoaToken.portalUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#e65100', fontSize: '12px' }}
                        >
                          View Portal Link →
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Credit Agreement Details */}
              <div className="info-section" style={{ marginTop: '20px' }}>
                <div style={{ 
                  backgroundColor: '#e3f2fd', 
                  padding: '12px', 
                  borderRadius: '6px', 
                  marginBottom: '15px',
                  border: '1px solid #bbdefb'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0 0 10px 0' }}>
                    <h4 style={{ margin: 0, color: '#333' }}>Credit Agreement Details</h4>
                    {canEditCreditAgreement && (
                      caEditMode ? (
                        <span style={{ display: 'inline-flex', gap: 6 }}>
                          <button type="button" onClick={handleSaveCreditAgreement} disabled={caSaving}
                            style={{ padding: '3px 10px', fontSize: 12, background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                            {caSaving ? 'Saving…' : 'Save'}
                          </button>
                          <button type="button" onClick={cancelCaEdit} disabled={caSaving}
                            style={{ padding: '3px 10px', fontSize: 12, background: '#eee', color: '#333', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}>
                            Cancel
                          </button>
                        </span>
                      ) : (
                        <button type="button" onClick={startCaEdit}
                          style={{ padding: '3px 10px', fontSize: 12, background: '#fff', color: '#1976d2', border: '1px solid #1976d2', borderRadius: 4, cursor: 'pointer' }}>
                          Edit
                        </button>
                      )
                    )}
                  </div>
                  <InfoRow
                    label="Date Claim Opened"
                    value={selectedClaim.createdInApiAt
                      ? new Date(selectedClaim.createdInApiAt).toLocaleString('en-GB', {
                          day: '2-digit', month: 'short', year: 'numeric',
                          hour: '2-digit', minute: '2-digit',
                        })
                      : undefined}
                  />
                  {CA_EDIT_FIELDS.map(f => {
                    const key = f.key as string;
                    let display: React.ReactNode;
                    if (f.key === 'commissionAmount') {
                      display = (selectedClaim.commissionAmount !== undefined && selectedClaim.commissionAmount !== null)
                        ? `£${Number(selectedClaim.commissionAmount).toFixed(2)}` : 'N/A';
                    } else if (f.key === 'commissionType') {
                      display = selectedClaim.commissionType || 'N/A';
                    } else if (f.key === 'craAgreementNumberOriginal') {
                      // Any staff member can correct the number here; the
                      // role-gated panel edit replaces this while it's open.
                      display = anEditing ? (
                        <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 4, verticalAlign: 'top' }}>
                          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                            <input
                              type="text"
                              aria-label="Agreement number"
                              value={anValue}
                              disabled={anSaving}
                              onChange={(e) => setAnValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); handleSaveAgreementNumber(); }
                                if (e.key === 'Escape') { e.preventDefault(); cancelAgreementNumberEdit(); }
                              }}
                              className="info-inline-input"
                            />
                            <button type="button" aria-label="Save agreement number" onClick={handleSaveAgreementNumber} disabled={anSaving}
                              style={{ padding: '3px 10px', fontSize: 12, background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                              {anSaving ? 'Saving…' : 'Save'}
                            </button>
                            <button type="button" aria-label="Cancel agreement number edit" onClick={cancelAgreementNumberEdit} disabled={anSaving}
                              style={{ padding: '3px 10px', fontSize: 12, background: '#eee', color: '#333', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}>
                              Cancel
                            </button>
                          </span>
                          <span style={{ fontSize: 12, color: '#666' }}>Changing this won't resend the letter of claim.</span>
                          {anError && <span role="alert" style={{ fontSize: 12, color: '#c62828' }}>{anError}</span>}
                        </span>
                      ) : (
                        <>
                          {selectedClaim.craAgreementNumberOriginal || 'N/A'}
                          <button type="button" aria-label="Edit agreement number" onClick={startAgreementNumberEdit}
                            style={{ marginLeft: 8, padding: '3px 10px', fontSize: 12, background: '#fff', color: '#1976d2', border: '1px solid #1976d2', borderRadius: 4, cursor: 'pointer' }}>
                            Edit
                          </button>
                          {anWarning && <div role="status" style={{ fontSize: 12, color: '#b26a00', marginTop: 4 }}>{anWarning}</div>}
                        </>
                      );
                    } else {
                      display = (selectedClaim as any)[f.key];
                    }
                    return (
                      <InfoRow
                        key={key}
                        label={f.label}
                        value={caEditMode ? (
                          f.type === 'commissionType' ? (
                            (() => {
                              const currentValue = caEdits[key] ?? '';
                              const options = (currentValue && !COMMISSION_TYPE_OPTIONS.includes(currentValue))
                                ? [currentValue, ...COMMISSION_TYPE_OPTIONS]
                                : COMMISSION_TYPE_OPTIONS;
                              return (
                                <select
                                  value={currentValue}
                                  onChange={(e) => setCaEdits(prev => ({ ...prev, [key]: e.target.value }))}
                                  className="info-inline-input"
                                >
                                  {options.map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                  ))}
                                </select>
                              );
                            })()
                          ) : f.type === 'craClassification' ? (
                            (() => {
                              const currentValue = caEdits[key] ?? '';
                              const options = (currentValue && !CRA_CLASSIFICATION_OPTIONS.includes(currentValue))
                                ? [currentValue, ...CRA_CLASSIFICATION_OPTIONS]
                                : CRA_CLASSIFICATION_OPTIONS;
                              return (
                                <select
                                  value={currentValue}
                                  onChange={(e) => setCaEdits(prev => ({ ...prev, [key]: e.target.value }))}
                                  className="info-inline-input"
                                >
                                  <option value="">N/A</option>
                                  {options.map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                  ))}
                                </select>
                              );
                            })()
                          ) : (
                            <input
                              type={f.type === 'number' ? 'number'
                                : f.type === 'date' ? 'date'
                                : f.type === 'datetime' ? 'datetime-local'
                                : 'text'}
                              step={f.type === 'number' ? '0.01' : undefined}
                              value={caEdits[key] ?? ''}
                              onChange={(e) => setCaEdits(prev => ({ ...prev, [key]: e.target.value }))}
                              className="info-inline-input"
                            />
                          )
                        ) : display}
                      />
                    );
                  })}
                  {/* Vehicle Registration is per-claim (#405) but NOT a
                      credit-agreement fact, so it stays editable by any
                      authenticated staff member via isEditMode/the general
                      edit flow (saved through the unrestricted
                      /agreement-fields route), independent of the role-gated
                      Credit Agreement panel above. */}
                  <InfoRow
                    label="Vehicle Registration"
                    value={isEditMode ? (
                      <input
                        type="text"
                        value={editVehicleRegistration}
                        onChange={(e) => setEditVehicleRegistration(e.target.value)}
                        className="info-inline-input"
                      />
                    ) : selectedClaim.vehicleRegistration}
                  />
                </div>

                {/* Bulk Submission Details - Only show if claim was bulk submitted */}
                {/* Shown for a bulk-submitted claim OR one whose response
                    arrived through the lender-response import: an imported
                    outcome (MBFS et al.) must be visible even though the
                    claim never went through OUR bulk-send flow, so gating
                    on submissionSent alone hid real data. */}
                {(selectedClaim.submissionSent
                  || selectedClaim.submissionOutcome
                  || selectedClaim.submissionOutcomeDate) && (
                  <div style={{
                    backgroundColor: '#e8f5e9',
                    padding: '12px',
                    borderRadius: '6px',
                    marginBottom: '15px',
                    border: '1px solid #c8e6c9'
                  }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#2e7d32' }}>Bulk Submission Details</h4>
                    <InfoRow label="Submitted To" value={selectedClaim.submissionLenderName || 'N/A'} />
                    <InfoRow label="Lender Email" value={selectedClaim.submissionLenderEmail || 'N/A'} />
                    <InfoRow
                      label="Submitted On"
                      value={selectedClaim.submissionSentDate
                        ? new Date(selectedClaim.submissionSentDate).toLocaleDateString('en-GB')
                        : 'N/A'}
                    />
                    <InfoRow
                      label="Outcome"
                      value={selectedClaim.submissionOutcome || 'Awaiting response'}
                    />
                    {selectedClaim.submissionOutcomeDate && (
                      <InfoRow
                        label="Outcome Date"
                        value={new Date(selectedClaim.submissionOutcomeDate).toLocaleString()}
                      />
                    )}
                  </div>
                )}

                {selectedClaim.status === 'CANCELLED' && (
                  <div style={{ 
                    backgroundColor: '#ffebee', 
                    padding: '12px', 
                    borderRadius: '6px', 
                    marginBottom: '15px',
                    border: '1px solid #ffcdd2'
                  }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#d32f2f' }}>Cancellation Details</h4>
                    <InfoRow label="Cancelled On" value={selectedClaim.cancellationDate ? new Date(selectedClaim.cancellationDate).toLocaleString() : 'N/A'} />
                    {(() => {
                      const cancelledBy = cancelledByLabel(selectedClaim.cancellationSource, selectedClaim.cancellationUpdatedBy);
                      return <InfoRow label="Cancelled By" value={cancelledBy.label} />;
                    })()}
                    <InfoRow label="Reason" value={selectedClaim.cancellationReason || 'N/A'} />
                    {selectedClaim.finalResponseClosure && (
                      <>
                        <InfoRow
                          label="Final response status"
                          value={FINAL_RESPONSE_STATE_LABELS[selectedClaim.finalResponseClosure.state]}
                        />
                        <InfoRow
                          label="Lender rejection reasons"
                          value={selectedClaim.finalResponseClosure.reasonLabels?.join('; ') || 'N/A'}
                        />
                      </>
                    )}
                    {selectedClaim.nonCooperationReasons?.length ? (
                      <InfoRow
                        label="Outstanding Requirements"
                        value={selectedClaim.nonCooperationReasons.map((item) => item.label).join('; ')}
                      />
                    ) : null}
                    {cancellationPaymentSummary && (
                      <>
                        <InfoRow
                          label="Payment Summary"
                          value={
                            cancellationPaymentSummary.isVoided ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ textDecoration: 'line-through', color: '#9ca3af' }}>
                                  {cancellationPaymentSummary.summary}
                                </span>
                                <span style={{
                                  backgroundColor: '#fee2e2',
                                  color: '#991b1b',
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  textTransform: 'uppercase'
                                }}>
                                  VOIDED
                                </span>
                              </span>
                            ) : (
                              cancellationPaymentSummary.summary
                            )
                          }
                        />
                        {cancellationPaymentSummary.isVoided && 'voidDetail' in cancellationPaymentSummary && cancellationPaymentSummary.voidDetail && (
                          <InfoRow label="Voided" value={cancellationPaymentSummary.voidDetail} />
                        )}
                        {cancellationPaymentSummary.amount !== null && (
                          <InfoRow
                            label="Payment Amount"
                            value={
                              <span style={cancellationPaymentSummary.isVoided ? { textDecoration: 'line-through', color: '#9ca3af' } : {}}>
                                £{cancellationPaymentSummary.amount.toFixed(2)}
                              </span>
                            }
                          />
                        )}
                        {cancellationPaymentSummary.requestedAt && (
                          <InfoRow label="Payment Requested" value={new Date(cancellationPaymentSummary.requestedAt).toLocaleString()} />
                        )}
                        {cancellationPaymentSummary.paidAt && (
                          <InfoRow label="Payment Paid" value={new Date(cancellationPaymentSummary.paidAt).toLocaleString()} />
                        )}
                        {/* #621: a final (incl. agreed reduced) payment owes nothing. */}
                        {amountStillOwed(selectedClaim.cancellationManualPayment) > 0 && (
                          <InfoRow
                            label="Outstanding Balance"
                            value={
                              <span style={{ color: '#dc2626', fontWeight: 600 }}>
                                £{amountStillOwed(selectedClaim.cancellationManualPayment).toFixed(2)}
                              </span>
                            }
                          />
                        )}
                      </>
                    )}
                    {selectedClaim.reinstatementBlock && (
                      <InfoRow
                        label="Reinstatement"
                        value={
                          <span style={{ color: '#b91c1c', fontWeight: 600 }}>
                            {selectedClaim.reinstatementBlock.message}
                          </span>
                        }
                      />
                    )}
                  </div>
                )}

                {/* Enforcement Flag Section */}
                <div style={{
                  backgroundColor: selectedClaim.doNotEnforce ? '#fff3e0' : '#f5f5f5',
                  padding: '12px',
                  borderRadius: '6px',
                  marginBottom: '15px',
                  border: `1px solid ${selectedClaim.doNotEnforce ? '#ffe0b2' : '#e0e0e0'}`
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <h4 style={{ margin: 0, fontSize: '14px', color: selectedClaim.doNotEnforce ? '#e65100' : '#555' }}>
                      {selectedClaim.doNotEnforce ? '\u26A0 DO NOT ENFORCE' : 'Enforcement'}
                    </h4>
                    <button
                      onClick={() => {
                        setEnforcementForm({
                          doNotEnforce: selectedClaim.doNotEnforce ?? false,
                          doNotEnforceReason: selectedClaim.doNotEnforceReason || '',
                          doNotEnforceNotes: selectedClaim.doNotEnforceNotes || '',
                        });
                        setShowEnforcementModal(true);
                      }}
                      style={{ fontSize: '12px', padding: '4px 10px', cursor: 'pointer', borderRadius: '4px', border: '1px solid #ccc', background: '#fff' }}
                    >
                      {selectedClaim.doNotEnforce ? 'Edit / Clear Flag' : 'Set Do Not Enforce'}
                    </button>
                  </div>
                  {selectedClaim.doNotEnforce && (
                    <>
                      <InfoRow label="Reason" value={selectedClaim.doNotEnforceReason || 'N/A'} />
                      {selectedClaim.doNotEnforceNotes && <InfoRow label="Notes" value={selectedClaim.doNotEnforceNotes} />}
                      <InfoRow label="Set By" value={selectedClaim.doNotEnforceSetBy} />
                      <InfoRow label="Set At" value={selectedClaim.doNotEnforceSetAt ? new Date(selectedClaim.doNotEnforceSetAt).toLocaleString() : 'N/A'} />
                    </>
                  )}
                </div>
              </div>

              {/* Notes Section: one box, date column, per-note edits (Freshdesk #570) */}
              <ClaimNotesPanel
                claimId={selectedClaim.id}
                noteEntries={selectedClaim.noteEntries}
                legacyNotes={selectedClaim.notes}
                onNoteEntriesChanged={(noteEntries) => {
                  const id = selectedClaim.id;
                  setSelectedClaim(prev => (prev && prev.id === id ? { ...prev, noteEntries } : prev));
                  setClaims(prev => prev.map(c => (c.id === id ? { ...c, noteEntries } : c)));
                }}
                onLegacyNotesChanged={(notes) => {
                  const id = selectedClaim.id;
                  setSelectedClaim(prev => (prev && prev.id === id ? { ...prev, notes } : prev));
                  setClaims(prev => prev.map(c => (c.id === id ? { ...c, notes } : c)));
                }}
              />

              {/* Credit Check Section */}
              <div className="info-section" style={{ marginTop: '20px' }}>
                <h4>Credit Check Information</h4>
                {selectedClaim.valid8responseUrl ? (
                  <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                    <button 
                      onClick={() => handleViewCreditCheck(selectedClaim)}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#2196F3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      View Credit Check Summary
                    </button>
                    <button 
                      onClick={() => handleViewFullCreditReport(selectedClaim)}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      View Full Credit Report
                    </button>
                  </div>
                ) : (
                  <p style={{ color: '#666', fontStyle: 'italic' }}>No credit check data available</p>
                )}
              </div>

              {/* AML Section */}
              <div className="info-section" style={{ marginTop: '20px' }}>
                <h4>AML Check Information</h4>
                {/* The stored outcome and the fetched report are independent: 334,535
                    live claims hold a verdict with no report, and this section used to
                    hide the verdict behind the report's URL. */}
                {(() => {
                  const outcome = amlOutcome(selectedClaim);
                  const reportAvailable = hasFetchableAmlReport(selectedClaim);

                  if (!outcome && !reportAvailable) {
                    return (
                      <p style={{ color: '#666', fontStyle: 'italic' }}>No AML check data available</p>
                    );
                  }

                  return (
                    <>
                      {outcome && (
                        <div style={{ marginTop: '10px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontWeight: 600 }}>Result:</span>
                            <span
                              style={{
                                padding: '2px 10px',
                                borderRadius: '12px',
                                fontSize: '13px',
                                fontWeight: 600,
                                backgroundColor: AML_TONE_COLOURS[outcome.tone].bg,
                                color: AML_TONE_COLOURS[outcome.tone].fg
                              }}
                            >
                              {outcome.verdict}
                            </span>
                          </div>
                          {outcome.reason && (
                            <p style={{ margin: '6px 0 0 0', fontSize: '14px' }}>
                              <strong>Reason:</strong> {outcome.reason}
                            </p>
                          )}
                          {outcome.note && (
                            <p
                              style={{
                                margin: '6px 0 0 0',
                                fontSize: '13px',
                                color: AML_TONE_COLOURS[outcome.tone].fg
                              }}
                            >
                              {outcome.note}
                            </p>
                          )}
                        </div>
                      )}
                      {reportAvailable ? (
                        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                          <button
                            onClick={() => handleViewAmlSummary(selectedClaim)}
                            style={{
                              padding: '8px 16px',
                              backgroundColor: '#0ea5e9',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '14px'
                            }}
                          >
                            View AML Summary
                          </button>
                        </div>
                      ) : (
                        <p style={{ color: '#666', fontStyle: 'italic', marginTop: '10px', fontSize: '13px' }}>
                          No full AML report on file for this claim.
                        </p>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* Risk Assessment Section */}
              <div className="info-section" style={{ marginTop: '20px' }}>
                <h4>Risk Assessment</h4>
                <div style={{ display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
                  {(riskAssessmentVersions && riskAssessmentVersions.length > 0
                    ? riskAssessmentVersions
                    : [{ version: '', label: 'View Risk Assessment' }]
                  ).map((ra, i, all) => (
                    <button
                      key={ra.version || 'latest'}
                      onClick={() => handleViewRiskAssessment(selectedClaim, ra.version || undefined, ra.version ? ra.label : undefined)}
                      style={{
                        padding: '8px 16px',
                        // newest in green, earlier versions in grey
                        backgroundColor: i === all.length - 1 ? '#10b981' : '#6b7280',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      {ra.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Audit Section */}
              <div className="info-section" style={{ marginTop: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h4 style={{ margin: 0 }}>Compliance Audits</h4>
                  {!showAuditForm && (
                    <button 
                      onClick={handleShowAuditForm}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#9C27B0',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      New Audit
                    </button>
                  )}
                </div>
                
                {showAuditForm ? (
                  <AuditForm 
                    claimId={selectedClaim.id}
                    onSuccess={() => handleAuditComplete('')}
                    onClose={handleCloseAuditForm}
                    onOpenPdf={handleAuditDocumentView}
                    isHidden={auditFormHiddenForPdf}
                  />
                ) : (
                  <div>
                    {auditLoading ? (
                      <p>Loading audits...</p>
                    ) : selectedClaimAudits.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {selectedClaimAudits.map((audit, index) => (
                          <div key={audit.id} style={{ 
                            padding: '12px', 
                            backgroundColor: index === 0 ? '#e8f5e9' : '#f5f5f5', 
                            borderRadius: '6px',
                            border: `1px solid ${index === 0 ? '#c8e6c9' : '#e0e0e0'}`
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                              <span style={{ fontWeight: 500 }}>Audited by: {audit.auditedBy}</span>
                              <span style={{ color: '#666', fontSize: '14px' }}>
                                {new Date(audit.auditedAt).toLocaleString()}
                              </span>
                            </div>
                            {audit.notes && (
                              <p style={{ margin: '8px 0', fontStyle: 'italic', color: '#555' }}>
                                Notes: {audit.notes}
                              </p>
                            )}
                            {audit.documentUrl && (
                              <button
                                onClick={() => {
                                  const isPdf = audit.documentUrl!.toLowerCase().includes('.pdf');
                                  if (isPdf) {
                                    openPdfViewer(
                                      audit.documentUrl!,
                                      `Audit Report - ${new Date(audit.auditedAt).toLocaleDateString()}`
                                    );
                                  } else {
                                    window.open(audit.documentUrl, '_blank');
                                  }
                                }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#2196F3',
                                  textDecoration: 'underline',
                                  fontSize: '14px',
                                  display: 'inline-block',
                                  marginTop: '8px',
                                  cursor: 'pointer',
                                  padding: 0
                                }}
                              >
                                📄 View Audit Report
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p style={{ color: '#666', fontStyle: 'italic' }}>No audits performed yet</p>
                    )}
                  </div>
                )}
              </div>

              {/* ID Verification Section */}
              {clientVerification && (
                <div className="info-section" style={{ marginTop: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h4 style={{ margin: 0 }}>Photo ID Verification</h4>
                    {/* Admin undo for a staff manual upload made in error
                        (wrong file / wrongly answered "verified").
                        Manual-only: a client's own emailed submission must
                        never be deleted from here. */}
                    {isAdmin &&
                      clientVerification.verification?.status === 'approved' &&
                      clientVerification.verification?.method === 'manual' &&
                      selectedClaim.clientId && (
                        <ResetIdVerificationButton
                          clientId={selectedClaim.clientId}
                          onReset={handleManualIdUploaded}
                          wholePerson={!!clientVerification.verifiedOnAnotherRecord}
                        />
                      )}
                  </div>
                  {verificationLoading ? (
                    <p>Loading ID verification details...</p>
                  ) : (
                    <div>
                      {clientVerification.hasVerification && clientVerification.verification ? (
                        <div style={{ 
                          padding: '12px', 
                          backgroundColor: clientVerification.verification.status === 'approved' ? '#e8f5e9' : '#fff3cd',
                          borderRadius: '6px',
                          border: `1px solid ${clientVerification.verification.status === 'approved' ? '#c8e6c9' : '#ffeaa7'}`
                        }}>
                          <div style={{ marginBottom: '10px' }}>
                            <strong>Status:</strong> {' '}
                            <span style={{ 
                              fontWeight: 600,
                              color: clientVerification.verification.status === 'approved' ? '#4CAF50' : '#FF9800'
                            }}>
                              {clientVerification.verification.status === 'approved'
                                ? (clientVerification.verifiedOnAnotherRecord
                                  ? '✓ ID verified (on another record for this person)'
                                  : '✓ Verified')
                                : '⏳ Pending Review'}
                            </span>
                          </div>
                          
                          <IdRequestHistoryRow info={clientVerification} />
                          <InfoRow 
                            label="ID Received" 
                            value={clientVerification.verification.createdAt ? 
                              new Date(clientVerification.verification.createdAt).toLocaleString() : 'N/A'} 
                          />
                          
                          {clientVerification.verification.status === 'approved' && (
                            <>
                              <InfoRow 
                                label="Approved By" 
                                value={clientVerification.verification.processedBy || 'N/A'} 
                              />
                              <InfoRow 
                                label="Approved On" 
                                value={clientVerification.verification.processedAt ? 
                                  new Date(clientVerification.verification.processedAt).toLocaleString() : 'N/A'} 
                              />
                            </>
                          )}
                          
                          {clientVerification.verification.hasPhotos && (
                            <div style={{ marginTop: '10px' }}>
                              <button
                                onClick={() => setShowIdModal(true)}
                                style={{
                                  padding: '8px 16px',
                                  backgroundColor: '#9C27B0',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '14px'
                                }}
                              >
                                View ID Photos ({clientVerification.verification.attachmentCount})
                              </button>
                            </div>
                          )}

                          {(clientVerification as any).idDocumentUrl && (
                            <div style={{ marginTop: '10px' }}>
                              <a
                                href={(clientVerification as any).idDocumentUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: 'inline-block',
                                  padding: '8px 16px',
                                  backgroundColor: '#1976D2',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  textDecoration: 'none'
                                }}
                              >
                                View ID Document
                              </a>
                            </div>
                          )}

                        </div>
                      ) : (
                        <div>
                          {/* ID belongs to the person: verified on another of their
                              client records, with no ID task on this one. */}
                          {clientVerification.verifiedOnAnotherRecord ? (
                            <div style={{
                              padding: '12px',
                              backgroundColor: '#e8f5e9',
                              borderRadius: '6px',
                              border: '1px solid #c8e6c9',
                              marginBottom: '10px'
                            }}>
                              <strong>Status:</strong>{' '}
                              <span style={{ fontWeight: 600, color: '#4CAF50' }}>
                                ✓ ID verified (on another record for this person)
                              </span>
                              <IdRequestHistoryRow info={clientVerification} style={{ marginTop: '8px', marginBottom: 0 }} />
                            </div>
                          ) : (
                          <div style={{ color: '#666', fontStyle: 'italic', marginBottom: '10px' }}>
                            <IdRequestStatus
                              info={{
                                ...clientVerification,
                                clientDeceased: clientVerification.clientDeceased || claimSaysClientDeceased(selectedClaim),
                              }}
                            />
                          </div>
                          )}
                          {/* ID Document Link (legacy imports) */}
                          {(clientVerification as any).idDocumentUrl && (
                            <div style={{ marginTop: '10px' }}>
                              <a
                                href={(clientVerification as any).idDocumentUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: 'inline-block',
                                  padding: '8px 16px',
                                  backgroundColor: '#1976D2',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  textDecoration: 'none'
                                }}
                              >
                                View ID Document
                              </a>
                            </div>
                          )}

                          {/* Staff manual ID upload (#377) - replaces the old document-less
                              "Mark ID as Verified" flip; staff always attach the document
                              they received and answer whether it verifies the client. */}
                          {selectedClaim.clientId && clientVerification.verificationStatus !== 'verified' && (
                            <ManualIdUploadForm
                              clientId={selectedClaim.clientId}
                              onUploaded={handleManualIdUploaded}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Tasks & Reminders Section */}
              <div className="info-section" style={{ marginTop: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h4>Tasks & Reminders</h4>
                  <button
                    onClick={() => setShowNewTaskModal(true)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}
                  >
                    + New Task
                  </button>
                </div>
                
                {tasksLoading ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
                    Loading tasks...
                  </div>
                ) : claimTasks.length === 0 ? (
                  <div style={{ 
                    padding: '30px', 
                    textAlign: 'center', 
                    color: '#6b7280',
                    backgroundColor: '#f9fafb',
                    borderRadius: '6px',
                    border: '1px dashed #e5e7eb'
                  }}>
                    No tasks or reminders for this claim
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {claimTasks.map((task) => (
                      <div
                        key={task.id}
                        style={{
                          padding: '12px',
                          backgroundColor: task.isOverdue ? '#fef2f2' : '#f9fafb',
                          border: `1px solid ${task.isOverdue ? '#fecaca' : '#e5e7eb'}`,
                          borderRadius: '6px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                              <span style={{
                                padding: '2px 8px',
                                fontSize: '11px',
                                fontWeight: 500,
                                backgroundColor: task.type === 'id_verification' ? '#dbeafe' : 
                                              task.type.includes('email') ? '#fef3c7' : '#e5e7eb',
                                color: task.type === 'id_verification' ? '#1e40af' : 
                                       task.type.includes('email') ? '#92400e' : '#374151',
                                borderRadius: '3px'
                              }}>
                                {getTaskTypeDisplay(task)}
                              </span>
                              <span style={{
                                padding: '2px 8px',
                                fontSize: '11px',
                                fontWeight: 500,
                                backgroundColor: task.status === 'completed' || task.status === 'approved' ? '#d1fae5' :
                                              task.status === 'pending' ? '#fed7aa' :
                                              task.status === 'in_progress' ? '#dbeafe' : '#e5e7eb',
                                color: task.status === 'completed' || task.status === 'approved' ? '#065f46' :
                                       task.status === 'pending' ? '#92400e' :
                                       task.status === 'in_progress' ? '#1e40af' : '#374151',
                                borderRadius: '3px'
                              }}>
                                {getTaskStatusDisplay(task.status)}
                              </span>
                              {task.priority === 'high' && (
                                <span style={{ color: '#dc2626', fontSize: '12px', fontWeight: 600 }}>HIGH</span>
                              )}
                            </div>
                            <div style={{ fontSize: '14px', fontWeight: 500, color: '#111827', marginBottom: '2px' }}>
                              {task.type === 'manual' ? (task.subject || task.description || 'Manual Task') :
                               /* Tasks Rebuild Task 14: list payloads no longer carry emailContent
                                  (see task_lane_service / unified_tasks list responses) -- `subject`
                                  is the real column every row already has, so it's the primary
                                  read; emailContent?.subject stays as a defensive fallback only. */
                               (task.subject || task.emailContent?.subject || 'Task')}
                            </div>
                            {task.description && task.type === 'manual' && (
                              <div style={{ fontSize: '13px', color: '#6b7280' }}>
                                {task.description}
                              </div>
                            )}
                            <div style={{ 
                              fontSize: '12px', 
                              color: task.isOverdue ? '#dc2626' : '#6b7280',
                              fontWeight: task.isOverdue ? 500 : 400
                            }}>
                              {task.dueDate ? formatTaskDate(task.dueDate) : 'No due date'}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            {task.status !== 'completed' && task.status !== 'approved' && (
                              <button
                                onClick={() => handleCompleteTask(task.id)}
                                style={{
                                  padding: '4px 10px',
                                  backgroundColor: '#10b981',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                              >
                                Complete
                              </button>
                            )}
                          </div>
                        </div>
                        {taskCompleteError?.taskId === task.id && (
                          <div style={{
                            padding: '8px 10px',
                            backgroundColor: '#fee2e2',
                            border: '1px solid #fecaca',
                            borderRadius: '4px',
                            color: '#dc2626',
                            fontSize: '13px'
                          }}>
                            {taskCompleteError.message}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Files Management Section */}
              <div className="info-section" style={{ marginTop: '20px' }}>
                <h4>Files & Documents</h4>
                <ClaimFiles
                  claimId={selectedClaim.id}
                  claim={selectedClaim}
                  correspondence={selectedClaim.correspondence}
                  onAddCorrespondence={handleAddCorrespondence}
                  onDocViewerToggle={(isOpen) => setDrawerHiddenForModal(isOpen)}
                />
              </div>
            </div>
            
            {/* New Task Modal - moved inside drawer for selectedClaim access */}
            {showNewTaskModal && (
              <div className="modal-overlay" onClick={() => setShowNewTaskModal(false)}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                  <div className="modal-header">
                    <h3>Create New Task</h3>
                    <button onClick={() => setShowNewTaskModal(false)} className="close-button">&times;</button>
                  </div>
                  <div className="modal-body" style={{ padding: '20px' }}>
                    {/* Display error or success messages */}
                    {actionError && (
                      <div style={{
                        padding: '10px',
                        marginBottom: '15px',
                        backgroundColor: '#fee2e2',
                        border: '1px solid #fecaca',
                        borderRadius: '4px',
                        color: '#dc2626',
                        fontSize: '14px'
                      }}>
                        {actionError}
                      </div>
                    )}
                    {updateMessage && (
                      <div style={{
                        padding: '10px',
                        marginBottom: '15px',
                        backgroundColor: '#dcfce7',
                        border: '1px solid #86efac',
                        borderRadius: '4px',
                        color: '#16a34a',
                        fontSize: '14px'
                      }}>
                        {updateMessage}
                      </div>
                    )}
                    <div style={{ marginBottom: '15px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 500, fontSize: '14px' }}>
                        Subject/Title *
                      </label>
                      <input
                        type="text"
                        value={newTaskForm.subject}
                        onChange={(e) => setNewTaskForm(prev => ({ ...prev, subject: e.target.value }))}
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                        placeholder="Enter task subject"
                      />
                    </div>
                    
                    <div style={{ marginBottom: '15px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 500, fontSize: '14px' }}>
                        Description
                      </label>
                      <textarea
                        value={newTaskForm.description}
                        onChange={(e) => setNewTaskForm(prev => ({ ...prev, description: e.target.value }))}
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '14px',
                          minHeight: '80px',
                          resize: 'vertical'
                        }}
                        placeholder="Enter task description (optional)"
                      />
                    </div>
                    
                    <div style={{ marginBottom: '15px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 500, fontSize: '14px' }}>
                        Due Date
                      </label>
                      <input
                        type="date"
                        value={newTaskForm.dueDate}
                        onChange={(e) => setNewTaskForm(prev => ({ ...prev, dueDate: e.target.value }))}
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 500, fontSize: '14px' }}>
                        Priority
                      </label>
                      <select
                        value={newTaskForm.priority}
                        onChange={(e) => setNewTaskForm(prev => ({ ...prev, priority: e.target.value as 'high' | 'medium' | 'low' }))}
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => setShowNewTaskModal(false)}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: '#f3f4f6',
                          color: '#374151',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleCreateTask}
                        disabled={!newTaskForm.subject.trim()}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: newTaskForm.subject.trim() ? '#3b82f6' : '#9ca3af',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: newTaskForm.subject.trim() ? 'pointer' : 'not-allowed',
                          fontSize: '14px'
                        }}
                      >
                        Create Task
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Credit Check Modal */}
        {showCreditCheckModal && (
          <div className="modal-overlay" onClick={handleCloseCreditCheckModal}>
            <div className="modal-content credit-check-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Credit Check Summary</h3>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  {creditCheckClaim && (
                    <button 
                      onClick={async () => {
                        try {
                          const result = await testCreditCheckUrl(creditCheckClaim.id);
                          console.log('Credit check URL test result:', result);
                          alert(`URL Test Result:\n${JSON.stringify(result, null, 2)}`);
                        } catch (error: any) {
                          console.error('URL test error:', error);
                          alert(`URL Test Error: ${error.message}`);
                        }
                      }}
                      style={{
                        padding: '5px 10px',
                        fontSize: '12px',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      Test URL
                    </button>
                  )}
                  <button onClick={handleCloseCreditCheckModal} className="close-button">&times;</button>
                </div>
              </div>
              <div className="modal-body">
                {creditCheckLoading ? (
                  <p>Loading credit check data...</p>
                ) : creditCheckError ? (
                  <p style={{ color: 'red' }}>Error: {creditCheckError}</p>
                ) : creditCheckSummary?.reportAvailable === false ? (
                  <CreditReportStatus reportAvailable={false} message={creditCheckSummary?.message} />
                ) : creditCheckSummary && (
                  <div className="credit-check-summary">
                    <CreditReportStatus reportFromClaimId={creditCheckSummary.reportFromClaimId} notice={creditCheckSummary.reportNotice} />
                    {/* Applicant Information */}
                    {creditCheckSummary.applicant && (
                      <div className="summary-section">
                        <h4>Applicant Information</h4>
                        <p><strong>Name:</strong> {creditCheckSummary.applicant.name || 'N/A'}</p>
                        <p><strong>Date of Birth:</strong> {creditCheckSummary.applicant.date_of_birth || 'N/A'}</p>
                        <p><strong>Age:</strong> {creditCheckSummary.applicant.age || 'N/A'}</p>
                        <p><strong>Primary Address:</strong> {creditCheckSummary.applicant.address || 'N/A'}</p>
                        
                        {/* All Addresses */}
                        {creditCheckSummary.applicant.all_addresses && creditCheckSummary.applicant.all_addresses.length > 0 && (
                          <div style={{ marginTop: '15px' }}>
                            <strong>All Addresses Found:</strong>
                            <div style={{ marginTop: '10px', maxHeight: '200px', overflowY: 'auto' }}>
                              {creditCheckSummary.applicant.all_addresses.map((addr: any, index: number) => (
                                <div key={index} style={{ 
                                  padding: '10px', 
                                  marginBottom: '8px', 
                                  backgroundColor: '#f8f9fa',
                                  borderRadius: '4px',
                                  border: '1px solid #dee2e6',
                                  fontSize: '14px'
                                }}>
                                  <div style={{ marginBottom: '4px' }}>
                                    <strong>{addr.type}</strong> - {addr.source}
                                  </div>
                                  <div style={{ color: '#495057' }}>
                                    {addr.address}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Overall Summary */}
                    {creditCheckSummary.summary && (
                      <div className="summary-section">
                        <h4>Verification Summary</h4>
                        <p className={`decision ${creditCheckSummary.summary.overall_decision?.toLowerCase()}`}>
                          <strong>Overall Decision:</strong> {creditCheckSummary.summary.overall_decision || 'N/A'}
                        </p>
                        <p><strong>Identity Verified:</strong> {creditCheckSummary.summary.identity_verified ? 'Yes' : 'No'}</p>
                        <p><strong>Credit Accounts Found:</strong> {creditCheckSummary.summary.credit_accounts_found || 0}</p>
                        <p><strong>Total Balance:</strong> {creditCheckSummary.summary.total_balance || '£0'}</p>
                        <p><strong>Worst Status:</strong> {creditCheckSummary.summary.worst_status || 'N/A'}</p>
                        <p><strong>PEPs/Sanctions Clear:</strong> {creditCheckSummary.summary.pep_sanctions_clear ? 'Yes' : 'No'}</p>
                        <p><strong>Electoral Roll Confirmed:</strong> {creditCheckSummary.summary.electoral_roll_confirmed ? 'Yes' : 'No'}</p>
                      </div>
                    )}

                    {/* Electoral Roll Information - Only show for formats that have this data and actual data exists */}
                    {creditCheckSummary.electoralRoll && 
                     creditCheckSummary.formatType !== 'historic' &&
                     (creditCheckSummary.formatType === 'valifi' || creditCheckSummary.formatType === 'valid8_legacy') && 
                     (creditCheckSummary.electoralRoll.confirmed || ((creditCheckSummary.electoralRoll.household_members?.length ?? 0) > 0)) && (
                      <div className="summary-section">
                        <h4>Electoral Roll</h4>
                        <p><strong>Confirmed:</strong> {creditCheckSummary.electoralRoll.confirmed ? 'Yes' : 'No'}</p>
                        {creditCheckSummary.electoralRoll.years_at_address && (
                          <p><strong>Years at Address:</strong> {creditCheckSummary.electoralRoll.years_at_address}</p>
                        )}
                        {creditCheckSummary.electoralRoll.household_members && creditCheckSummary.electoralRoll.household_members.length > 0 && (
                          <div style={{ marginTop: '10px' }}>
                            <strong>Household Members:</strong>
                            <ul style={{ marginTop: '5px', marginLeft: '20px' }}>
                              {creditCheckSummary.electoralRoll.household_members.map((member: any, index: number) => (
                                <li key={index}>
                                  {member.name} - Registered since: {member.registered_since}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Credit Accounts */}
                    {creditCheckSummary.creditAccounts && creditCheckSummary.creditAccounts.length > 0 && (
                      <div className="summary-section">
                        <h4>Credit Accounts ({creditCheckSummary.creditAccounts.length})</h4>
                        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          {creditCheckSummary.creditAccounts.map((account: any, index: number) => (
                            <div key={index} style={{ 
                              padding: '8px', 
                              marginBottom: '8px', 
                              backgroundColor: '#f5f5f5',
                              borderRadius: '4px',
                              fontSize: '14px'
                            }}>
                              <p style={{ margin: '2px 0' }}><strong>{account.lender}</strong></p>
                              <p style={{ margin: '2px 0' }}>Account: {account.account_number}</p>
                              <p style={{ margin: '2px 0' }}>Type: {account.account_type}</p>
                              <p style={{ margin: '2px 0' }}>Start Date: {account.start_date}</p>
                              <p style={{ margin: '2px 0' }}>Status: {account.status}</p>
                              <p style={{ margin: '2px 0' }}>Balance: {account.balance}</p>
                              {account.payment && <p style={{ margin: '2px 0' }}>Payment: {account.payment}</p>}
                              {account.settlement_date && <p style={{ margin: '2px 0' }}>Settled: {account.settlement_date}</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Message if no data */}
                    {creditCheckSummary.message && (
                      <div className="summary-section">
                        <p style={{ color: '#666', fontStyle: 'italic' }}>{creditCheckSummary.message}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Documents Modal */}
        {showDocsModal && (
          <DocsModal
            claim={selectedClaimForDocs}
            onClose={handleCloseDocs}
            onViewCreditCheck={handleViewCreditCheck}
            onViewFullReport={handleViewFullReport}
            onViewAmlSummary={handleViewAmlSummary}
            onViewFiles={(claim) => {
              handleCloseDocs();
              setSelectedClaim(claim);
            }}
          onOpenPdf={(url, fileName) => {
              openPdfViewer(url, fileName);
          }}
            onOpenDoc={(url, fileName) => {
              setSelectedDoc({ url, fileName });
              setDocViewerOpen(true);
            }}
          />
        )}
        
        {/* Email Composer Modal - Now available in hierarchical view! */}
        {showEmailComposer && emailComposerClaim && (
          <EmailComposer
            claim={emailComposerClaim}
            defaultTo={emailComposerClaim.email || ''}
            onClose={() => setShowEmailComposer(false)}
            onSent={handleEmailSent}
          />
        )}

        {/* SMS Composer Modal */}
        {showSmsComposer && smsComposerClaim && (
          <SmsComposer
            claim={smsComposerClaim}
            defaultPhone={smsComposerClaim.phoneNumber || ''}
            onClose={() => setShowSmsComposer(false)}
            onSent={handleSmsSent}
          />
        )}

        {/* Cancel scope choice modal — this claim only or all claims? */}
        {showCancelScopeModal && cancelScopeData && (
          <Modal onClose={handleCancelScopeClose}>
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
              <div style={modalCardStyle}>
                <h3 style={modalHeadingStyle}>Cancel Claim</h3>
                <p style={{ color: '#475569', fontSize: '14px', marginBottom: '16px' }}>
                  This client has <strong>{cancelScopeData.associatedData.associatedClaimsCount} other active claim{cancelScopeData.associatedData.associatedClaimsCount === 1 ? '' : 's'}</strong>.
                  How would you like to proceed?
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <button
                    type="button"
                    onClick={() => handleCancelScopeChoice(false)}
                    style={{
                      ...modalButtonBaseStyle,
                      backgroundColor: '#2563eb',
                      color: '#ffffff',
                      boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)',
                      textAlign: 'left',
                      padding: '14px 18px'
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>Cancel this claim only</div>
                    <div style={{ fontSize: '12px', opacity: 0.85, marginTop: '2px' }}>Only cancel {cancelScopeData.claimId}</div>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleCancelScopeChoice(true)}
                    style={{
                      ...modalButtonBaseStyle,
                      backgroundColor: '#dc2626',
                      color: '#ffffff',
                      boxShadow: '0 4px 12px rgba(220, 38, 38, 0.2)',
                      textAlign: 'left',
                      padding: '14px 18px'
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>Cancel all {cancelScopeData.associatedData.associatedClaimsCount + 1} claims</div>
                    <div style={{ fontSize: '12px', opacity: 0.85, marginTop: '2px' }}>Cancel all claims for this client</div>
                  </button>

                  <button
                    type="button"
                    onClick={handleCancelScopeClose}
                    style={{
                      ...modalButtonBaseStyle,
                      backgroundColor: '#f8fafc',
                      color: '#475569',
                      border: '1px solid #cbd5e1',
                      textAlign: 'center'
                    }}
                  >
                    Go Back
                  </button>
                </div>
              </div>
            </div>
          </Modal>
        )}

        {showReasonModal && pendingCancellation && (
        <Modal onClose={handleReasonCancel}>
          <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <div style={modalCardStyle}>
              <h3 style={modalHeadingStyle}>Cancellation Reason</h3>
              <p style={{ ...modalDescriptionStyle, color: '#475569' }}>
                Select a reason for cancelling <strong>{pendingCancellation.claimEmail || 'this client'}</strong>.
              </p>

              {/* Cancellation Reason Dropdown */}
              <div style={{ marginBottom: '16px' }}>
                <label htmlFor="cancel-reason" style={{ display: 'block', marginBottom: '6px', fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                  Reason
                </label>
                <select
                  id="cancel-reason"
                  value={selectedCancellationReason}
                  onChange={(e) => {
                    setSelectedCancellationReason(e.target.value);
                    setClientRequestSubReason('');
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5f5',
                    fontSize: '14px',
                    backgroundColor: '#fff',
                    cursor: 'pointer',
                    boxShadow: 'inset 0 1px 2px rgba(148, 163, 184, 0.25)'
                  }}
                >
                  {CANCELLATION_REASONS.filter((reason) => {
                    // Cancelling all associated claims: only show Charge / No Charge options
                    if (pendingCancellation?.cancelAllAssociated) {
                      return reason === REASON_GENERAL_CLOSURE
                        || reason === REASON_GENERAL_CLOSURE_CHARGE
                        || reason === REASON_INVALID_LENDER
                        || reason === REASON_NON_COOPERATIVE;
                    }
                    // The approved non-cooperation notice closes every live claim.
                    // A client with siblings must therefore use the all-claims scope.
                    if (pendingCancellation?.associatedData.hasAssociatedClaims && reason === REASON_NON_COOPERATIVE) return false;
                    // Single claim within cooling period: hide Charge option
                    if (isWithinCoolingPeriod(selectedClaim) && reason === REASON_GENERAL_CLOSURE_CHARGE) return false;
                    return true;
                  }).map((reason) => (
                    <option key={reason} value={reason}>{reason}</option>
                  ))}
                </select>
              </div>

              {selectedCancellationReason === REASON_CLIENT_REQUEST && (
                <ClientRequestSubReasonSelect
                  selected={clientRequestSubReason}
                  onChange={setClientRequestSubReason}
                  borderColor="#cbd5f5"
                />
              )}

              {/* Task 6: optional request received date. Empty = judge the
                  cooling-off window at today, exactly as before. Filled =
                  judge it at the date the client actually asked (e.g. the
                  received date on a cancellation manual-review task). */}
              <div style={{ marginBottom: '16px' }}>
                <label
                  htmlFor="cancel-request-received-date"
                  style={{ display: 'block', marginBottom: '6px', fontWeight: 500, fontSize: '14px', color: '#374151' }}
                >
                  Request received date (optional)
                </label>
                <input
                  id="cancel-request-received-date"
                  type="date"
                  value={requestReceivedDateDraft}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setRequestReceivedDateDraft(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5f5',
                    fontSize: '14px',
                    backgroundColor: '#fff',
                    boxShadow: 'inset 0 1px 2px rgba(148, 163, 184, 0.25)'
                  }}
                />
                <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#64748b' }}>
                  When the client asked to cancel, if that is earlier than today.
                  The 14-day cooling-off window is judged at this date.
                </p>
              </div>

              {/* Info notice for special reasons */}
              {selectedCancellationReason === REASON_LEASE_AGREEMENT && (
                <div style={{
                  backgroundColor: '#dbeafe',
                  border: '1px solid #93c5fd',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px',
                  fontSize: '13px',
                  color: '#1e40af'
                }}>
                  <strong>Note:</strong> Lease agreement cancellations do not require payment and will not include a reinstatement link.
                </div>
              )}

              {selectedCancellationReason === REASON_NON_COOPERATIVE && (
                <>
                  <div style={{
                    backgroundColor: '#fef3c7',
                    border: '1px solid #fcd34d',
                    borderRadius: '8px',
                    padding: '12px',
                    marginBottom: '16px',
                    fontSize: '13px',
                    color: '#92400e'
                  }}>
                    <strong>Note:</strong> All live claims for this client will close. The closure email will contain only the requirements selected below and will not include a reinstatement link.
                  </div>
                  <NonCooperationReasonsFieldset
                    selected={selectedNonCooperationReasons}
                    onChange={setSelectedNonCooperationReasons}
                  />
                </>
              )}

              {selectedCancellationReason === REASON_OTHER_SOLICITOR && (
                <div style={{
                  backgroundColor: '#fef3c7',
                  border: '1px solid #fcd34d',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px',
                  fontSize: '13px',
                  color: '#92400e'
                }}>
                  <strong>Note:</strong> The client will be charged the standard termination fee. No reinstatement link will be included.
                </div>
              )}

              {selectedCancellationReason === REASON_GENERAL_CLOSURE && (
                <div style={{
                  backgroundColor: '#dbeafe',
                  border: '1px solid #93c5fd',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px',
                  fontSize: '13px',
                  color: '#1e40af'
                }}>
                  {isWithinCoolingPeriod(selectedClaim) ? (
                    <><strong>Note:</strong> This claim is within the 14-day cooling off period. No charge applies.</>
                  ) : (
                    <><strong>Note:</strong> General closure does not require payment and will not include a reinstatement link.</>
                  )}
                </div>
              )}

              {selectedCancellationReason === REASON_INVALID_LENDER && (
                <div style={{
                  backgroundColor: '#dbeafe',
                  border: '1px solid #93c5fd',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px',
                  fontSize: '13px',
                  color: '#1e40af'
                }}>
                  <strong>Note:</strong> The claim will close without charge using the invalid-lender client email. No lender withdrawal or reinstatement link will be sent.
                </div>
              )}

              {selectedCancellationReason === REASON_GENERAL_CLOSURE_CHARGE && (
                <div style={{
                  backgroundColor: '#fef3c7',
                  border: '1px solid #fcd34d',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px',
                  fontSize: '13px',
                  color: '#92400e'
                }}>
                  <strong>Note:</strong> The client will be charged the standard termination fee. A payment link will be sent separately.
                </div>
              )}

              {/* Show textarea only when "Other" is selected */}
              {selectedCancellationReason === 'Other' && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, fontSize: '14px', color: '#374151' }}>
                    Custom reason
                  </label>
                  <textarea
                    value={reasonDraft}
                    onChange={(e) => setReasonDraft(e.target.value)}
                    rows={3}
                    placeholder="Enter custom cancellation reason..."
                    style={{
                      width: '100%',
                      minHeight: '80px',
                      borderRadius: '8px',
                      border: '1px solid #cbd5f5',
                      padding: '12px',
                      fontSize: '14px',
                      lineHeight: 1.5,
                      resize: 'vertical',
                      boxShadow: 'inset 0 1px 2px rgba(148, 163, 184, 0.25)',
                      fontFamily: 'inherit'
                    }}
                  />
                </div>
              )}

              <div style={modalActionsStyle}>
                <button
                  type="button"
                  onClick={handleReasonCancel}
                  style={{
                    ...modalButtonBaseStyle,
                    backgroundColor: '#f8fafc',
                    color: '#475569',
                    border: '1px solid #cbd5f5'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleReasonSubmit}
                  disabled={(selectedCancellationReason === REASON_NON_COOPERATIVE && selectedNonCooperationReasons.length === 0) || isClientRequestSubReasonMissing(selectedCancellationReason, clientRequestSubReason)}
                  style={{
                    ...modalButtonBaseStyle,
                    backgroundColor: (selectedCancellationReason === REASON_NON_COOPERATIVE && selectedNonCooperationReasons.length === 0) || isClientRequestSubReasonMissing(selectedCancellationReason, clientRequestSubReason) ? '#94a3b8' : '#2563eb',
                    color: '#ffffff',
                    boxShadow: '0 8px 16px rgba(37, 99, 235, 0.2)',
                    cursor: (selectedCancellationReason === REASON_NON_COOPERATIVE && selectedNonCooperationReasons.length === 0) || isClientRequestSubReasonMissing(selectedCancellationReason, clientRequestSubReason) ? 'not-allowed' : 'pointer'
                  }}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {phoneReviewTask && (
        <HoldReviewModal
          taskId={phoneReviewTask.taskId}
          requestedReason={phoneReviewTask.requestedReason}
          clientEmail={phoneReviewTask.clientEmail}
          onClose={() => setPhoneReviewTask(null)}
          onCompleted={async () => {
            setPhoneReviewTask(null);
            await loadClaims({ silent: true });
          }}
        />
      )}

        {showReviewModal && reviewBreakdown && (
          <ChargeReviewModal
            breakdown={reviewBreakdown.breakdown}
            chargeableCount={reviewBreakdown.chargeableCount}
            estimatedAmount={reviewBreakdown.estimatedAmount}
            currency={reviewBreakdown.currency}
            onBack={handleReviewBack}
            onContinue={handleReviewContinue}
          />
        )}

        {showBypassModal && pendingCancellation && (
          <Modal onClose={handleBypassModalClose}>
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
              <div style={modalCardStyle}>
                <h3 style={modalHeadingStyle}>Payment Handling</h3>
                <p style={{ ...modalDescriptionStyle, color: '#475569' }}>
                  How would you like to handle the cancellation fee for <strong>{pendingCancellation.claimEmail || 'this client'}</strong>?
                </p>
                {pendingCancellation.paymentPreview && (() => {
                  const preview = pendingCancellation.paymentPreview;
                  const chargeableCount = preview.chargeableClaimCount;
                  const breakdown = preview.breakdown;
                  const totalConsidered = breakdown ? breakdown.length : undefined;
                  const remaining =
                    typeof totalConsidered === 'number' ? totalConsidered - chargeableCount : undefined;
                  // Ring-fenced claims are among the "remaining" (not chargeable
                  // right now) but must never be reported as free -- a fee may
                  // still follow the 70+ review call.
                  const ringfencedCount = breakdown
                    ? breakdown.filter((entry) => entry.reasonCode === 'FREE_AGE_RINGFENCED').length
                    : 0;
                  const trulyFreeCount =
                    typeof remaining === 'number' ? remaining - ringfencedCount : undefined;
                  const currencySymbol = preview.currency === 'GBP' ? '£' : `${preview.currency || ''} `;
                  return (
                    <div style={{
                      backgroundColor: '#f1f5f9',
                      border: '1px solid #cbd5f5',
                      borderRadius: '8px',
                      padding: '12px',
                      marginBottom: '16px',
                      fontSize: '14px',
                      color: '#334155'
                    }}>
                      {typeof totalConsidered === 'number' ? (
                        <p style={{ margin: 0 }}>
                          <strong>{chargeableCount} of {totalConsidered}</strong>{' '}
                          {totalConsidered === 1 ? 'claim' : 'claims'} will be charged
                          {typeof preview.estimatedAmount === 'number' && (
                            <> a total of <strong>{currencySymbol}{preview.estimatedAmount.toFixed(2)}</strong></>
                          )}
                          .
                          {typeof remaining === 'number' && remaining > 0 && (
                            ringfencedCount === 0 ? (
                              <> The remaining {remaining} {remaining === 1 ? 'is' : 'are'} free to cancel.</>
                            ) : (
                              <>
                                {' '}The remaining{' '}
                                {typeof trulyFreeCount === 'number' && trulyFreeCount > 0 && (
                                  <>{trulyFreeCount} {trulyFreeCount === 1 ? 'is' : 'are'} free to cancel; </>
                                )}
                                {ringfencedCount} {ringfencedCount === 1 ? 'is' : 'are'} held for 70+ review — fee decided on the call.
                              </>
                            )
                          )}
                        </p>
                      ) : (
                        // Fallback: 409 path doesn't carry the per-claim breakdown.
                        <p style={{ margin: 0 }}>
                          <strong>{chargeableCount}</strong>{' '}
                          {chargeableCount === 1 ? 'claim meets' : 'claims meet'} the fee criteria.
                          {typeof preview.estimatedAmount === 'number' && (
                            <> Estimated total: <strong>{currencySymbol}{preview.estimatedAmount.toFixed(2)}</strong>.</>
                          )}
                        </p>
                      )}
                      {!breakdown && preview.chargeableClaimIds.length > 0 && (
                        <p style={{ margin: '8px 0 0 0', color: '#475569' }}>
                          Claims: {preview.chargeableClaimIds.join(', ')}
                        </p>
                      )}
                      {breakdown && breakdown.length > 0 && (
                        <details style={{ marginTop: 12 }}>
                          <summary style={{ cursor: 'pointer', color: '#475569', fontSize: 13 }}>
                            Show per-claim breakdown ({breakdown.length}{' '}
                            {breakdown.length === 1 ? 'claim' : 'claims'})
                          </summary>
                          <div style={{ marginTop: 8 }}>
                            {breakdown.map((entry) => (
                              <div
                                key={entry.claimId}
                                style={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  justifyContent: 'space-between',
                                  padding: '8px 0',
                                  borderBottom: '1px solid #e2e8f0',
                                  gap: 12,
                                }}
                              >
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontWeight: 600, color: '#0f172a', fontSize: 13 }}>
                                    {entry.claimRef}
                                  </div>
                                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                                    {entry.reasonCode === 'FREE_AGE_RINGFENCED' &&
                                    Boolean(entry.evidence?.wouldHaveCharged) &&
                                    !/chargeable/i.test(entry.reasonText)
                                      ? `${entry.reasonText} — this claim would otherwise be chargeable.`
                                      : entry.reasonText}
                                  </div>
                                </div>
                                <span
                                  role="status"
                                  aria-label={
                                    entry.reasonCode === 'FREE_AGE_RINGFENCED'
                                      ? `${entry.claimRef}: fee held for over-70 review`
                                      : entry.willCharge
                                        ? `${entry.claimRef}: will be charged`
                                        : `${entry.claimRef}: free to cancel`
                                  }
                                  style={{
                                    background:
                                      entry.reasonCode === 'FREE_AGE_RINGFENCED'
                                        ? '#fde68a'
                                        : entry.willCharge ? '#fef3c7' : '#e2e8f0',
                                    color:
                                      entry.reasonCode === 'FREE_AGE_RINGFENCED'
                                        ? '#78350f'
                                        : entry.willCharge ? '#92400e' : '#475569',
                                    border:
                                      entry.reasonCode === 'FREE_AGE_RINGFENCED'
                                        ? '1px solid #b45309'
                                        : 'none',
                                    padding: '2px 8px',
                                    borderRadius: 999,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {entry.reasonCode === 'FREE_AGE_RINGFENCED'
                                    ? '70+ REVIEW'
                                    : entry.willCharge ? 'CHARGE' : 'FREE'}
                                </span>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  );
                })()}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <button
                    type="button"
                    onClick={() => handleBypassDecision(true)}
                    style={{
                      ...modalButtonBaseStyle,
                      backgroundColor: '#0ea5e9',
                      color: '#ffffff',
                      boxShadow: '0 8px 16px rgba(14, 165, 233, 0.2)'
                    }}
                  >
                    Bypass payment (no fee)
                  </button>
                  <button
                    type="button"
                    onClick={() => handleBypassDecision(false)}
                    style={{
                      ...modalButtonBaseStyle,
                      backgroundColor: '#2563eb',
                      color: '#ffffff',
                      boxShadow: '0 8px 16px rgba(37, 99, 235, 0.2)'
                    }}
                  >
                    Require payment (send Stripe link)
                  </button>
                  <button
                    type="button"
                    onClick={handleBypassModalClose}
                    style={{
                      ...modalButtonBaseStyle,
                      backgroundColor: '#f8fafc',
                      color: '#475569',
                      border: '1px solid #cbd5f5'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </Modal>
        )}

        {/* Resend Document Confirmation Modal */}
        {resendConfirmModal.open && resendConfirmModal.documentType && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(4px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000
            }}
            onClick={() => setResendConfirmModal({ open: false, documentType: null, originalSignedDate: null, originalUrl: null })}
          >
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }} onClick={e => e.stopPropagation()}>
              <div style={modalCardStyle}>
                <h3 style={modalHeadingStyle}>
                  Original {resendConfirmModal.documentType.toUpperCase()} Already Signed
                </h3>
                <p style={{ ...modalDescriptionStyle, color: '#475569' }}>
                  The original {resendConfirmModal.documentType === 'foa' ? 'Form of Authority (FOA)' : 'Finance Retention (DBA)'} document was signed
                  {resendConfirmModal.originalSignedDate && (
                    <> on <strong>{new Date(resendConfirmModal.originalSignedDate).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}</strong> at <strong>{new Date(resendConfirmModal.originalSignedDate).toLocaleTimeString('en-GB', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}</strong></>
                  )}.
                </p>
                {resendConfirmModal.originalUrl && (
                  <div style={{
                    backgroundColor: '#f1f5f9',
                    border: '1px solid #cbd5f5',
                    borderRadius: '8px',
                    padding: '12px',
                    marginBottom: '16px',
                    fontSize: '14px',
                    color: '#334155'
                  }}>
                    <p style={{ margin: 0 }}>
                      <a
                        href={resendConfirmModal.originalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#2563eb', textDecoration: 'underline' }}
                      >
                        View original signed document
                      </a>
                    </p>
                  </div>
                )}
                <p style={{ ...modalDescriptionStyle, color: '#475569', marginTop: '8px' }}>
                  If you still want to send a new {resendConfirmModal.documentType === 'foa' ? 'FOA' : 'DBA'} document, please click the resend button below. If not, click the red cancel button.
                </p>
                <div style={modalActionsStyle}>
                  <button
                    type="button"
                    onClick={() => setResendConfirmModal({ open: false, documentType: null, originalSignedDate: null, originalUrl: null })}
                    style={{
                      ...modalButtonBaseStyle,
                      backgroundColor: '#ef4444',
                      color: '#ffffff',
                      border: '1px solid #dc2626'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => handleResendDocument(resendConfirmModal.documentType!)}
                    disabled={resendingFoa || resendingDba}
                    style={{
                      ...modalButtonBaseStyle,
                      backgroundColor: '#22c55e',
                      color: '#ffffff',
                      boxShadow: '0 8px 16px rgba(34, 197, 94, 0.2)'
                    }}
                  >
                    {resendingFoa || resendingDba ? 'Sending…' : 'Resend'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Email Drawer */}
        <EmailDrawer
          isOpen={showEmailDrawer}
          onClose={() => setShowEmailDrawer(false)}
          onClaimUpdate={async (claimId) => {
            // Refresh the claim data if it's currently selected
            if (selectedClaim && selectedClaim.id === claimId) {
              try {
                const updatedClaim = await getClaim(claimId);
                setSelectedClaim(updatedClaim);
              } catch (error) {
                console.error('Failed to refresh claim:', error);
              }
            }
          }}
        />

      {/* Shared Modals - Available in both views */}

      {/* Documents Modal */}
      {showDocsModal && (
        <DocsModal
          claim={selectedClaimForDocs}
          onClose={handleCloseDocs}
          onViewCreditCheck={handleViewCreditCheck}
          onViewFullReport={handleViewFullReport}
          onViewAmlSummary={handleViewAmlSummary}
          onViewFiles={(claim) => {
            handleCloseDocs();
            setSelectedClaim(claim);
          }}
          onOpenPdf={(url, fileName) => {
            openPdfViewer(url, fileName);
          }}
          onOpenDoc={(url, fileName) => {
            setSelectedDoc({ url, fileName });
            setDocViewerOpen(true);
          }}
        />
      )}


      {/* Audit Form Modal */}
      {showAuditForm && selectedClaim && (
        <AuditForm
          claimId={selectedClaim.id}
          onClose={handleCloseAuditForm}
          onSuccess={handleAuditSuccess}
          onOpenPdf={handleAuditDocumentView}
          isHidden={auditFormHiddenForPdf}
        />
      )}
      
      {/* Email Composer Modal - Now available in flat view! */}
      {showEmailComposer && emailComposerClaim && (
        <EmailComposer
          claim={emailComposerClaim}
          defaultTo={emailComposerClaim.email || ''}
          onClose={() => setShowEmailComposer(false)}
          onSent={handleEmailSent}
        />
      )}

      {/* SMS Composer Modal - Flat view */}
      {showSmsComposer && smsComposerClaim && (
        <SmsComposer
          claim={smsComposerClaim}
          defaultPhone={smsComposerClaim.phoneNumber || ''}
          onClose={() => setShowSmsComposer(false)}
          onSent={handleSmsSent}
        />
      )}

      {/* Credit Check Modal */}
      {showCreditCheckModal && (
        <div className="modal-overlay" onClick={handleCloseCreditCheckModal}>
          <div className="modal-content credit-check-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Credit Check Summary</h3>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                {creditCheckClaim && (
                  <button 
                    onClick={async () => {
                      try {
                        const result = await testCreditCheckUrl(creditCheckClaim.id);
                        console.log('Credit check URL test result:', result);
                        alert(`URL Test Result:\n${JSON.stringify(result, null, 2)}`);
                      } catch (error: any) {
                        console.error('URL test error:', error);
                        alert(`URL Test Error: ${error.message}`);
                      }
                    }}
                    style={{
                      padding: '5px 10px',
                      fontSize: '12px',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Test URL
                  </button>
                )}
                <button onClick={handleCloseCreditCheckModal} className="close-button">&times;</button>
              </div>
            </div>
            <div className="modal-body">
              {creditCheckLoading ? (
                <p>Loading credit check data...</p>
              ) : creditCheckError ? (
                <p style={{ color: 'red' }}>Error: {creditCheckError}</p>
              ) : creditCheckSummary?.reportAvailable === false ? (
                <CreditReportStatus reportAvailable={false} message={creditCheckSummary?.message} />
              ) : creditCheckSummary && (
                <div className="credit-check-summary">
                  <CreditReportStatus reportFromClaimId={creditCheckSummary.reportFromClaimId} notice={creditCheckSummary.reportNotice} />
                  {/* Applicant Information */}
                  {creditCheckSummary.applicant && (
                    <div className="summary-section">
                      <h4>Applicant Information</h4>
                      <p><strong>Name:</strong> {creditCheckSummary.applicant.name || 'N/A'}</p>
                      <p><strong>Date of Birth:</strong> {creditCheckSummary.applicant.date_of_birth || 'N/A'}</p>
                      <p><strong>Age:</strong> {creditCheckSummary.applicant.age || 'N/A'}</p>
                      <p><strong>Primary Address:</strong> {creditCheckSummary.applicant.address || 'N/A'}</p>
                      
                      {/* All Addresses */}
                      {creditCheckSummary.applicant.all_addresses && creditCheckSummary.applicant.all_addresses.length > 0 && (
                        <div style={{ marginTop: '15px' }}>
                          <strong>All Addresses Found:</strong>
                          <div style={{ marginTop: '10px', maxHeight: '200px', overflowY: 'auto' }}>
                            {creditCheckSummary.applicant.all_addresses.map((addr: any, index: number) => (
                              <div key={index} style={{ 
                                padding: '10px', 
                                marginBottom: '8px', 
                                backgroundColor: '#f8f9fa',
                                borderRadius: '4px',
                                border: '1px solid #dee2e6',
                                fontSize: '14px'
                              }}>
                                <div style={{ marginBottom: '4px' }}>
                                  <strong>{addr.type}</strong> - {addr.source}
                                </div>
                                <div style={{ color: '#495057' }}>
                                  {addr.address}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Overall Summary */}
                  {creditCheckSummary.summary && (
                    <div className="summary-section">
                      <h4>Verification Summary</h4>
                      <p className={`decision ${creditCheckSummary.summary.overall_decision?.toLowerCase()}`}>
                        <strong>Overall Decision:</strong> {creditCheckSummary.summary.overall_decision || 'N/A'}
                      </p>
                      <p><strong>Identity Verified:</strong> {creditCheckSummary.summary.identity_verified ? 'Yes' : 'No'}</p>
                      <p><strong>Credit Accounts Found:</strong> {creditCheckSummary.summary.credit_accounts_found || 0}</p>
                      <p><strong>Total Balance:</strong> {creditCheckSummary.summary.total_balance || '£0'}</p>
                      <p><strong>Worst Status:</strong> {creditCheckSummary.summary.worst_status || 'N/A'}</p>
                      <p><strong>PEPs/Sanctions Clear:</strong> {creditCheckSummary.summary.pep_sanctions_clear ? 'Yes' : 'No'}</p>
                      <p><strong>Electoral Roll Confirmed:</strong> {creditCheckSummary.summary.electoral_roll_confirmed ? 'Yes' : 'No'}</p>
                    </div>
                  )}

                  {/* Electoral Roll Information - Only show for formats that have this data and actual data exists */}
                  {creditCheckSummary.electoralRoll && 
                   creditCheckSummary.formatType !== 'historic' &&
                   (creditCheckSummary.formatType === 'valifi' || creditCheckSummary.formatType === 'valid8_legacy') && 
                   (creditCheckSummary.electoralRoll.confirmed || ((creditCheckSummary.electoralRoll.household_members?.length ?? 0) > 0)) && (
                    <div className="summary-section">
                      <h4>Electoral Roll</h4>
                      <p><strong>Confirmed:</strong> {creditCheckSummary.electoralRoll.confirmed ? 'Yes' : 'No'}</p>
                      {creditCheckSummary.electoralRoll.years_at_address && (
                        <p><strong>Years at Address:</strong> {creditCheckSummary.electoralRoll.years_at_address}</p>
                      )}
                      {creditCheckSummary.electoralRoll.household_members && creditCheckSummary.electoralRoll.household_members.length > 0 && (
                        <div style={{ marginTop: '10px' }}>
                          <strong>Household Members:</strong>
                          <ul style={{ marginTop: '5px', marginLeft: '20px' }}>
                            {creditCheckSummary.electoralRoll.household_members.map((member: any, index: number) => (
                              <li key={index}>
                                {member.name} - Registered since: {member.registered_since}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Credit Accounts */}
                  {creditCheckSummary.creditAccounts && creditCheckSummary.creditAccounts.length > 0 && (
                    <div className="summary-section">
                      <h4>Credit Accounts ({creditCheckSummary.creditAccounts.length})</h4>
                      <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {creditCheckSummary.creditAccounts.map((account: any, index: number) => (
                          <div key={index} style={{ 
                            padding: '8px', 
                            marginBottom: '8px', 
                            backgroundColor: '#f5f5f5',
                            borderRadius: '4px',
                            fontSize: '14px'
                          }}>
                            <p style={{ margin: '2px 0' }}><strong>{account.lender}</strong></p>
                            <p style={{ margin: '2px 0' }}>Account: {account.account_number}</p>
                            <p style={{ margin: '2px 0' }}>Type: {account.account_type}</p>
                            <p style={{ margin: '2px 0' }}>Start Date: {account.start_date}</p>
                            <p style={{ margin: '2px 0' }}>Status: {account.status}</p>
                            <p style={{ margin: '2px 0' }}>Balance: {account.balance}</p>
                            {account.payment && <p style={{ margin: '2px 0' }}>Payment: {account.payment}</p>}
                            {account.settlement_date && <p style={{ margin: '2px 0' }}>Settled: {account.settlement_date}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Message if no data */}
                  {creditCheckSummary.message && (
                    <div className="summary-section">
                      <p style={{ color: '#666', fontStyle: 'italic' }}>{creditCheckSummary.message}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showAmlModal && (
        <div className="modal-overlay" onClick={handleCloseAmlModal}>
          <div className="modal-content credit-check-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                AML Check Summary{amlClaim ? ` – ${amlClaim.id}` : ''}
              </h3>
              <button className="close-button" onClick={handleCloseAmlModal}>&times;</button>
            </div>
            <div className="modal-body">
              {amlLoading ? (
                <p>Loading AML data...</p>
              ) : amlError ? (
                <p style={{ color: 'red' }}>{amlError}</p>
              ) : amlSummary ? (
                <div className="aml-summary">
                  <div className="summary-section">
                    <h4>Summary</h4>
                    <p><strong>Status:</strong> {amlSummary.summary.status || 'N/A'}</p>
                    <p><strong>Identity result:</strong> {amlSummary.summary.identityResult || 'N/A'}</p>
                    <p><strong>Identity score:</strong> {amlSummary.summary.identityScore || 'N/A'}</p>
                    <p><strong>Match level:</strong> {amlSummary.summary.matchLevel || 'N/A'}</p>
                    <p><strong>Primary checks:</strong> {amlSummary.summary.numPrimaryChecks || 'N/A'}</p>
                    <p><strong>Corroborative checks:</strong> {amlSummary.summary.numCorroborativeChecks || 'N/A'}</p>
                  </div>

                  {amlIndividual && (
                    <div className="summary-section">
                      <h4>Input details</h4>
                      <p><strong>Name:</strong> {formatAmlIndividualName(amlIndividual)}</p>
                      <p><strong>DOB:</strong> {amlIndividual?.Dateofbirth || 'N/A'}</p>
                      <p><strong>Phone:</strong> {amlIndividual?.Phonenumber || 'N/A'}</p>
                      <p><strong>Email:</strong> {amlIndividual?.Emailaddress || 'N/A'}</p>
                    </div>
                  )}

                  {amlAddress && (
                    <div className="summary-section">
                      <h4>Input address</h4>
                      <p><strong>Address line 1:</strong> {amlAddress?.Address1 || 'N/A'}</p>
                      <p><strong>Building number:</strong> {amlAddress?.Buildingnumber || 'N/A'}</p>
                      <p><strong>Town:</strong> {amlAddress?.Town || 'N/A'}</p>
                      <p><strong>Postcode:</strong> {amlAddress?.Postcode || 'N/A'}</p>
                    </div>
                  )}

                  {amlSummary.summary.warnings && amlSummary.summary.warnings.length > 0 && (
                    <div className="summary-section">
                      <h4>Warnings</h4>
                      <ul style={{ marginLeft: '20px' }}>
                        {amlSummary.summary.warnings.map((warning, index) => (
                          <li key={index}>
                            {warning.label}: {warning.value}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <p>No AML data available.</p>
              )}
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={handleCloseAmlModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <LenderBulkSubmissionModal
        isOpen={showLbsModal}
        onClose={() => setShowLbsModal(false)}
        details={lenderBulkSubmission}
        loading={lbsLoading && !lenderBulkSubmission}
        saving={lbsSaving}
        error={lbsError}
        onSave={handleSaveLenderBulkSubmission}
        onRefresh={handleRefreshLenderBulkSubmission}
      />

      {/* Reinstatement scope picker */}
      {reinstatementScopeClaim && (
        <ReinstatementScopeModal
          claim={reinstatementScopeClaim}
          isOpen={true}
          onClose={() => {
            setReinstatementScopeClaim(null);
            setDrawerHiddenForModal(false);
          }}
          onSuccess={(result) => {
            applyReinstatementResult(result.updatedClaimIds, result.reinstatementDisabled);
            setUpdateMessage(result.message);
            // Hard refresh so the "No Reinstate" badges drop everywhere they
            // appear in the grouped views. Preserves the current search via
            // querystring so the user lands back on the same view.
            setTimeout(() => {
              const url = new URL(window.location.href);
              if (search) url.searchParams.set('search', search);
              else url.searchParams.delete('search');
              window.location.href = url.toString();
            }, 600);
          }}
        />
      )}

      {reinstatementPaymentPrompt && (
        <ReinstatementPaymentModal
          impact={reinstatementPaymentPrompt.impact}
          submitting={reinstatementPaymentSubmitting}
          onClose={() => setReinstatementPaymentPrompt(null)}
          onChoose={(action) => {
            void handleReinstateClaim(
              reinstatementPaymentPrompt.impact.claimId,
              reinstatementPaymentPrompt.force,
              action,
            );
          }}
        />
      )}

      {finalResponseClaim && (
        <FinalResponseModal
          claimId={finalResponseClaim.id}
          onClose={() => setFinalResponseClaim(null)}
          onChanged={async () => {
            const fresh = await getClaim(finalResponseClaim.id);
            setSelectedClaim(current => current?.id === fresh.id ? fresh : current);
            setClaims(current => current.map(claim => claim.id === fresh.id ? fresh : claim));
            await loadClaims({ silent: true });
          }}
        />
      )}

      {/* Split Claims Modal */}
      {selectedClaim && (
        <SplitClaimsModal
          isOpen={showSplitClaimsModal}
          onClose={() => {
            setShowSplitClaimsModal(false);
            setDrawerHiddenForModal(false);
          }}
          claim={selectedClaim}
          onSuccess={async (newEmail: string) => {
            // Close drawer and modal
            setSelectedClaim(null);
            setShowSplitClaimsModal(false);
            setDrawerHiddenForModal(false);
            // Set search to new email so user sees the split claims
            setSearchInput(newEmail);
            setSearch(newEmail);
            // Force page reload to ensure fresh data
            window.location.href = `${window.location.pathname}?search=${encodeURIComponent(newEmail)}`;
          }}
        />
      )}

      {/* Duplication Audit Modal */}
      <DuplicationAuditModal
        isOpen={showDupAuditModal}
        onClose={() => setShowDupAuditModal(false)}
        existing={dupAudit}
        loading={dupAuditLoading}
        saving={dupAuditSaving}
        error={dupAuditError}
        claimFactsAutoSummary={dupAuditAutoSummary}
        onSave={handleSaveDupAudit}
      />

      {/* Litigation Friend Modal */}
      {selectedClaim && (
        <AuditTrailModal
          isOpen={showAuditTrailModal}
          claimId={selectedClaim.id}
          onClose={() => { setShowAuditTrailModal(false); setDrawerHiddenForModal(false); }}
        />
      )}
      <LitigationFriendModal
        isOpen={showLitigationFriendModal}
        onClose={() => { setShowLitigationFriendModal(false); setDrawerHiddenForModal(false); }}
        claim={selectedClaim}
        onSaveSuccess={(updatedClaim) => {
          setSelectedClaim(updatedClaim);
          loadClaims({ silent: true });
        }}
        onMessage={(msg) => {
          setUpdateMessage(msg);
          setTimeout(() => setUpdateMessage(''), 8000);
        }}
      />

      {/* Send Letter Modal */}
      {selectedClaimForLetter && (
        <SendLetterModal
          isOpen={showLetterModal}
          onClose={() => {
            setShowLetterModal(false);
            setSelectedClaimForLetter(null);
            setDrawerHiddenForModal(false);
          }}
          claim={selectedClaimForLetter}
          onSend={handleSendLetter}
        />
      )}

      {/* Multiple Rep Email Confirmation Modal */}
      {showLenderWithdrawalConfirm && selectedClaim && (
        <Modal onClose={() => setShowLenderWithdrawalConfirm(false)}>
          <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <div style={modalCardStyle}>
              <h3 style={modalHeadingStyle}>Send Withdrawal of Responsibility</h3>
              <p style={{ ...modalDescriptionStyle, color: '#475569' }}>
                This tells <strong>{selectedClaim.craFinanceLender || 'the lender'}</strong> that Complex Law no
                longer acts for <strong>{`${selectedClaim.firstName || ''} ${selectedClaim.lastName || ''}`.trim()}</strong> on
                claim <strong>{selectedClaim.proclaimReference || selectedClaim.id}</strong>.
              </p>
              <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
                It goes to the lender's mapped address and is logged in correspondence.
              </p>
              {(lenderWithdrawalsSent.has(selectedClaim.id) || selectedClaim.lenderWithdrawalSentAt || selectedClaim.locWithdrawalSentToLender) && (
                <p style={{ fontSize: '13px', color: '#b45309', marginBottom: '16px' }}>
                  {selectedClaim.lenderWithdrawalSentAt
                    ? <>Already sent on {new Date(selectedClaim.lenderWithdrawalSentAt).toLocaleDateString('en-GB')}
                        {selectedClaim.lenderWithdrawalSentBy ? ` by ${selectedClaim.lenderWithdrawalSentBy}` : ''}.</>
                    : lenderWithdrawalsSent.has(selectedClaim.id)
                      ? <>Already sent during this session.</>
                      : <>A withdrawal was scheduled when this claim was cancelled; delivery is not confirmed by this marker.</>}
                  {' '}Sending again may send a duplicate withdrawal. Check correspondence before proceeding.
                </p>
              )}
              <div style={modalActionsStyle}>
                <button
                  type="button"
                  onClick={() => setShowLenderWithdrawalConfirm(false)}
                  style={{
                    ...modalButtonBaseStyle,
                    backgroundColor: '#f8fafc',
                    color: '#475569',
                    border: '1px solid #cbd5e1'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmLenderWithdrawal}
                  disabled={lenderWithdrawalSending}
                  style={{
                    ...modalButtonBaseStyle,
                    backgroundColor: '#dc2626',
                    color: '#ffffff',
                    boxShadow: '0 8px 16px rgba(220, 38, 38, 0.2)'
                  }}
                >
                  Send Withdrawal
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {lenderWithdrawalResult && (
        <Modal onClose={() => setLenderWithdrawalResult(null)}>
          <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <div style={modalCardStyle}>
              <h3 style={{
                ...modalHeadingStyle,
                color: lenderWithdrawalResult.type === 'success' ? '#16a34a' : '#dc2626'
              }}>
                {lenderWithdrawalResult.type === 'success' ? 'Withdrawal sent' : 'Withdrawal failed'}
              </h3>
              <p style={{ ...modalDescriptionStyle, color: '#475569' }}>{lenderWithdrawalResult.message}</p>
              <div style={modalActionsStyle}>
                <button
                  type="button"
                  onClick={() => setLenderWithdrawalResult(null)}
                  style={{
                    ...modalButtonBaseStyle,
                    backgroundColor: '#2563eb',
                    color: '#ffffff'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {showMultipleRepConfirm && selectedClaim && (
        <Modal onClose={() => setShowMultipleRepConfirm(false)}>
          <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <div style={modalCardStyle}>
              <h3 style={modalHeadingStyle}>Send Multiple Representation Email</h3>
              <p style={{ ...modalDescriptionStyle, color: '#475569' }}>
                This will send a clarification email to <strong>{`${selectedClaim.firstName || ''} ${selectedClaim.lastName || ''}`.trim()}</strong> ({selectedClaim.email}) regarding <strong>{selectedClaim.craFinanceLender || 'their lender'}</strong>.
              </p>
              <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
                A 7-day follow-up task will be created automatically.
              </p>
              <div style={modalActionsStyle}>
                <button
                  type="button"
                  onClick={() => setShowMultipleRepConfirm(false)}
                  style={{
                    ...modalButtonBaseStyle,
                    backgroundColor: '#f8fafc',
                    color: '#475569',
                    border: '1px solid #cbd5e1'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmMultipleRepEmail}
                  style={{
                    ...modalButtonBaseStyle,
                    backgroundColor: '#2563eb',
                    color: '#ffffff',
                    boxShadow: '0 8px 16px rgba(37, 99, 235, 0.2)'
                  }}
                >
                  Send Email
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Non-viable (disabled) lender — admin override to reinstate */}
      {lenderBlockModal && (
        <Modal onClose={() => setLenderBlockModal(null)}>
          <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <div style={modalCardStyle}>
              <h3 style={{ ...modalHeadingStyle, color: '#dc2626' }}>Lender is non-viable</h3>
              <p style={{ ...modalDescriptionStyle, color: '#475569' }}>
                Lender <strong>{lenderBlockModal.lenderName}</strong> is non-viable (closed), so reinstating claim <strong>{lenderBlockModal.claimId}</strong> is normally blocked.
              </p>
              <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
                {isAdmin
                  ? 'As an admin you can override this and reinstate anyway.'
                  : 'You do not have permission to override this. Please escalate to an admin to reinstate.'}
              </p>
              {/* Normal users get no action buttons (they can dismiss via the modal close).
                  Only admins see Cancel + Reinstate anyway. */}
              {isAdmin && (
                <div style={modalActionsStyle}>
                  <button
                    type="button"
                    onClick={() => setLenderBlockModal(null)}
                    style={{
                      ...modalButtonBaseStyle,
                      backgroundColor: '#f8fafc',
                      color: '#475569',
                      border: '1px solid #cbd5e1'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const { claimId } = lenderBlockModal;
                      setLenderBlockModal(null);
                      handleReinstateClaim(claimId, true);
                    }}
                    style={{
                      ...modalButtonBaseStyle,
                      backgroundColor: '#dc2626',
                      color: '#ffffff',
                      boxShadow: '0 8px 16px rgba(220, 38, 38, 0.2)'
                    }}
                  >
                    Reinstate anyway
                  </button>
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* Multiple Rep Email Result Modal */}
      {multipleRepResult && (
        <Modal onClose={() => setMultipleRepResult(null)}>
          <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <div style={modalCardStyle}>
              <h3 style={{
                ...modalHeadingStyle,
                color: multipleRepResult.type === 'success' ? '#16a34a' : '#dc2626'
              }}>
                {multipleRepResult.type === 'success' ? 'Email Sent' : 'Failed to Send'}
              </h3>
              <p style={{ ...modalDescriptionStyle, color: '#475569' }}>
                {multipleRepResult.message}
              </p>
              <div style={{ ...modalActionsStyle, justifyContent: 'center' }}>
                <button
                  type="button"
                  onClick={() => setMultipleRepResult(null)}
                  style={{
                    ...modalButtonBaseStyle,
                    backgroundColor: multipleRepResult.type === 'success' ? '#16a34a' : '#2563eb',
                    color: '#ffffff'
                  }}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Documents Modal */}
      {showDocsModal && (
        <DocsModal
          claim={selectedClaimForDocs}
          onClose={handleCloseDocs}
          onViewCreditCheck={handleViewCreditCheck}
          onViewFullReport={handleViewFullReport}
          onViewAmlSummary={handleViewAmlSummary}
          onViewFiles={(claim) => {
            handleCloseDocs();
            setSelectedClaim(claim);
          }}
          onOpenPdf={(url, fileName) => {
            openPdfViewer(url, fileName);
          }}
          onOpenDoc={(url, fileName) => {
            setSelectedDoc({ url, fileName });
            setDocViewerOpen(true);
          }}
        />
      )}

      {/* Audit Form Modal */}
      {showAuditForm && selectedClaim && (
        <AuditForm
          claimId={selectedClaim.id}
          onClose={handleCloseAuditForm}
          onSuccess={handleAuditSuccess}
          onOpenPdf={handleAuditDocumentView}
          isHidden={auditFormHiddenForPdf}
        />
      )}
      
      {/* Email Drawer */}
      <EmailDrawer
        isOpen={showEmailDrawer}
        onClose={() => setShowEmailDrawer(false)}
        onClaimUpdate={async (claimId) => {
          // Refresh the claim data if it's currently selected
          if (selectedClaim && selectedClaim.id === claimId) {
            try {
              const updatedClaim = await getClaim(claimId);
              setSelectedClaim(updatedClaim);
            } catch (error) {
              console.error('Failed to refresh claim:', error);
            }
          }
        }}
      />
      
      {/* ID Verification Modal */}
      {showIdModal && clientVerification?.verification && (
        <div className="modal-overlay" style={{ zIndex: 9999 }} onClick={() => setShowIdModal(false)}>
          <div className="modal-content" style={{ maxWidth: '800px', zIndex: 10000 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Photo ID Verification</h3>
              <button onClick={() => setShowIdModal(false)} className="close-button">&times;</button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column' }}>
              {/* Status Card */}
              <div style={{
                padding: '16px',
                backgroundColor: clientVerification.verification.status === 'approved' ? '#e8f5e9' : '#fff8e1',
                borderRadius: '8px',
                marginBottom: '24px',
                border: `1px solid ${clientVerification.verification.status === 'approved' ? '#c8e6c9' : '#ffe082'}`
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <span style={{ fontSize: '24px' }}>
                    {clientVerification.verification.status === 'approved' ? '✅' : '⏳'}
                  </span>
                  <span style={{
                    fontSize: '18px',
                    fontWeight: 600,
                    color: clientVerification.verification.status === 'approved' ? '#2e7d32' : '#f57c00'
                  }}>
                    {clientVerification.verification.status === 'approved' ? 'Verified' : 'Pending Review'}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '14px' }}>
                  <div><strong>Received:</strong> {new Date(clientVerification.verification.createdAt).toLocaleString()}</div>
                  {clientVerification.verification.status === 'approved' && (
                    <>
                      <div><strong>Approved By:</strong> {clientVerification.verification.processedBy || 'N/A'}</div>
                      <div><strong>Approved On:</strong> {clientVerification.verification.processedAt
                        ? new Date(clientVerification.verification.processedAt).toLocaleString()
                        : 'N/A'}</div>
                    </>
                  )}
                </div>
              </div>

              {/* ID Photos Section */}
              {clientVerification.verification.attachments && clientVerification.verification.attachments.length > 0 ? (
                <div>
                  <h4 style={{ marginBottom: '16px', color: '#333' }}>
                    ID Photos ({clientVerification.verification.attachmentCount})
                  </h4>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: '20px'
                  }}>
                    {clientVerification.verification.attachments.map((attachment: any, index: number) => (
                      <div key={index} style={{
                        border: '1px solid #e0e0e0',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        backgroundColor: '#fff',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                      }}>
                        {attachment.secureUrl ? (
                          <a
                            href={attachment.secureUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'none', display: 'block' }}
                          >
                            {attachment.type?.startsWith('image/') ? (
                              <div style={{ backgroundColor: '#f5f5f5', padding: '12px' }}>
                                <img
                                  src={attachment.secureUrl}
                                  alt={attachment.filename}
                                  style={{
                                    width: '100%',
                                    height: '220px',
                                    objectFit: 'contain',
                                    borderRadius: '8px'
                                  }}
                                />
                              </div>
                            ) : (
                              <div style={{
                                width: '100%',
                                height: '220px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#f5f5f5'
                              }}>
                                <span style={{ fontSize: '56px' }}>📄</span>
                              </div>
                            )}
                            <div style={{ padding: '12px' }}>
                              <div style={{ fontSize: '14px', color: '#333', fontWeight: 500, marginBottom: '4px' }}>
                                {attachment.filename || 'ID Document'}
                              </div>
                              <div style={{ fontSize: '12px', color: '#888' }}>
                                {attachment.type || 'Unknown type'} • Click to view full size
                              </div>
                            </div>
                          </a>
                        ) : (
                          <div style={{ padding: '40px', textAlign: 'center', color: '#999', fontStyle: 'italic' }}>
                            No preview available
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p style={{ color: '#666', fontStyle: 'italic' }}>No photos attached</p>
              )}
            </div>
          </div>
        </div>
      )}

      <LocResendDialog
        open={Boolean(locResendClaim)}
        claim={locResendClaim}
        onClose={() => setLocResendClaim(null)}
        onSent={handleLocResent}
      />

      {/* PDF Viewer Drawer */}
      {selectedPdf && (
        <PDFViewerDrawer
          open={pdfViewerOpen}
          onClose={handlePdfViewerClose}
          pdfUrl={selectedPdf.url}
          fileName={selectedPdf.fileName}
        />
      )}

      {/* Doc Viewer Drawer (for Word docs and other non-PDF formats) */}
      {selectedDoc && (
        <DocViewerDrawer
          open={docViewerOpen}
          onClose={() => {
            setDocViewerOpen(false);
            setSelectedDoc(null);
          }}
          docUrl={selectedDoc.url}
          fileName={selectedDoc.fileName}
        />
      )}

      {/* Enforcement Flag Modal */}
      {showEnforcementModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '24px', maxWidth: '450px', width: '90%', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px' }}>Enforcement Flag</h3>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={enforcementForm.doNotEnforce}
                onChange={e => setEnforcementForm(f => ({ ...f, doNotEnforce: e.target.checked }))}
              />
              <span style={{ fontWeight: 500 }}>Do Not Enforce</span>
            </label>
            {enforcementForm.doNotEnforce && (
              <>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontWeight: 500, marginBottom: '4px', fontSize: '14px' }}>Reason:</label>
                  <select
                    value={enforcementForm.doNotEnforceReason}
                    onChange={e => setEnforcementForm(f => ({ ...f, doNotEnforceReason: e.target.value }))}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '14px' }}
                  >
                    <option value="">Select a reason...</option>
                    <option value="DRO">DRO (Debt Relief Order)</option>
                    <option value="IVA">IVA (Individual Voluntary Arrangement)</option>
                    <option value="Bankruptcy">Bankruptcy</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontWeight: 500, marginBottom: '4px', fontSize: '14px' }}>Notes:</label>
                  <textarea
                    value={enforcementForm.doNotEnforceNotes}
                    onChange={e => setEnforcementForm(f => ({ ...f, doNotEnforceNotes: e.target.value }))}
                    style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '14px', resize: 'vertical' }}
                    rows={3}
                    placeholder="Additional details..."
                  />
                </div>
              </>
            )}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowEnforcementModal(false)}
                style={{ padding: '8px 16px', borderRadius: '4px', border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontSize: '14px' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEnforcementFlag}
                disabled={enforcementSaving}
                style={{ padding: '8px 16px', borderRadius: '4px', border: 'none', background: '#1976d2', color: '#fff', cursor: enforcementSaving ? 'not-allowed' : 'pointer', fontSize: '14px' }}
              >
                {enforcementSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRedressModal && selectedClaim && (
        <Modal variant="fullscreen" onClose={() => setShowRedressModal(false)}>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 10,
              marginBottom: 16,
              paddingRight: 48, // keep clear of the modal close (×) button
              flexWrap: 'wrap',
            }}
          >
            <h2
              style={{
                margin: 0,
                fontSize: 22,
                fontWeight: 700,
                color: '#111827',
                letterSpacing: '-0.01em',
              }}
            >
              Redress
            </h2>
            <span style={{ color: '#6b7280', fontSize: 15 }}>—</span>
            <span
              style={{
                fontFamily:
                  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                fontSize: 14,
                background: '#f3f4f6',
                color: '#1f2937',
                padding: '3px 10px',
                borderRadius: 4,
                border: '1px solid #e5e7eb',
              }}
            >
              {selectedClaim.proclaimReference || selectedClaim.id}
            </span>
            {(selectedClaim.firstName || selectedClaim.lastName) && (
              <span style={{ color: '#4b5563', fontSize: 14 }}>
                {[selectedClaim.firstName, selectedClaim.lastName]
                  .filter(Boolean)
                  .join(' ')}
              </span>
            )}
          </div>
          <RedressErrorBoundary>
            <RedressTab claimId={selectedClaim.id} />
          </RedressErrorBoundary>
        </Modal>
      )}

    </div>
  );
}
